import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getApplicationByNumber, updateApplicationByNumber, submitApplicationByNumber, requestModuleExemption, cancelModuleExemption, getCrmPublicSetting } from '../../../services/crmApi';
import { taxConfigApi } from '../../../services/financeApi';
import * as fileApi from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { Combobox } from '@headlessui/react';
import { COUNTRIES } from '../../../utils/countries';
import { NATIONALITIES } from '../../../utils/nationalities';
import { computeInstallmentTaxBreakdown, computePaymentBreakdownPreview, roundCurrency } from '../../../utils/crmTaxMath';
import {
  applyNicDerivedPersonalInfo,
  getPersonalInfoFromNic,
  isValidSriLankanNic,
  normalizeSriLankanNic,
} from '../../../utils/nicUtils';
import {
  getMaxApplicantDateOfBirth,
  getApplicantDateOfBirthError,
  isApplicantDateOfBirthAllowed,
  toCalendarDateString,
} from '../../../utils/applicantDateOfBirth';
import { isMembershipFeeIncluded, isInternationalPaymentEnabled, resolveRegistrationFeeContext } from '../../../utils/subProductFeeSettings';
import { resolveStudentTypeFromApplicationRefs } from '../../../utils/applicationStudentType';
import { getSlimLogoPath } from '../../../utils/slimLogo';
import { isPublicFormLocked } from '../../../utils/applicationStatus';
import ModuleFeeAdjustmentBadge from '../Common/ModuleFeeAdjustmentBadge';
import { isRepeatFeeModule, getRepeatAttemptNumber } from '../../../utils/moduleFeeAdjustment';
import MembershipFeeBandsSummary from '../Common/MembershipFeeBandsSummary';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { resolveDiscountEligibilitySource, hasAnyDiscountAllowedForPaymentContext, isDiscountRecordAllowedForPaymentContext } from '../../../utils/discountEligibility';

const DEFAULT_ENROLLMENT_TERMS = `Terms and Conditions of Enrollment

1. Eligibility
Applicants must meet all stated eligibility requirements at the time of submission. The institution reserves the right to cancel any enrollment that does not satisfy the eligibility criteria.

2. Payment Obligations
All fees must be paid in full by the due date. Failure to complete payment by the stated deadline may result in the cancellation of your enrollment.

3. Non-Refundable Payments
All payments made towards this application and enrollment are strictly non-refundable once processed, except as otherwise stated in the institution's refund policy.

4. Accuracy of Information
Applicants are responsible for ensuring all submitted information is accurate and complete. Providing false or misleading information may result in disqualification or cancellation of enrollment without refund.

5. Attendance & Participation
Enrolled students are expected to meet minimum attendance and participation requirements as communicated by the institution. Non-compliance may affect certification or completion status.

6. Certification
Certificates or credentials will only be issued upon satisfactory completion of all program requirements, including assessments and fee clearance.

7. Changes to the Program
The institution reserves the right to modify program content, schedules, fees, or delivery format. Reasonable advance notice will be provided where possible.

8. Code of Conduct
Students are expected to uphold the institution's standards of conduct throughout their enrollment. Violations may result in suspension or expulsion.`;

const PolicyModal = ({ title, content, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="overflow-y-auto px-5 py-4 flex-1">
        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{content}</pre>
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const StepButtonSpinner = ({ light = false }) => (
  <span
    className={`inline-block w-4 h-4 rounded-full animate-spin border-2 border-t-transparent ${
      light ? 'border-white' : 'border-indigo-600'
    }`}
    aria-hidden="true"
  />
);

const normalizeInstallmentWeights = (count, percentages) => {
  const safeCount = Math.max(0, Number(count) || 0);
  if (!safeCount) return [];

  const rawWeights = Array.isArray(percentages)
    ? percentages.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
    : [];

  if (rawWeights.length >= safeCount) {
    return rawWeights.slice(0, safeCount);
  }

  return Array(safeCount).fill(1);
};

const allocateByWeights = (total, weights) => {
  const safeTotal = roundCurrency(total);
  const safeWeights = Array.isArray(weights) ? weights : [];
  const weightTotal = safeWeights.reduce((sum, value) => sum + (Number(value) || 0), 0);

  if (!safeTotal || !safeWeights.length || weightTotal <= 0) {
    return safeWeights.map(() => 0);
  }

  let assignedAmount = 0;
  return safeWeights.map((weight, index) => {
    if (index === safeWeights.length - 1) {
      return roundCurrency(safeTotal - assignedAmount);
    }

    const amount = roundCurrency(safeTotal * ((Number(weight) || 0) / weightTotal));
    assignedAmount = roundCurrency(assignedAmount + amount);
    return amount;
  });
};

const rebalanceForLastInstallmentDiscount = (amounts, discountAmount) => {
  const adjustedAmounts = Array.isArray(amounts)
    ? amounts.map((amount) => roundCurrency(amount))
    : [];
  const safeDiscount = roundCurrency(discountAmount);

  if (!adjustedAmounts.length || safeDiscount <= 0) {
    return adjustedAmounts;
  }

  const lastIndex = adjustedAmounts.length - 1;
  let deficit = roundCurrency(Math.max(0, safeDiscount - adjustedAmounts[lastIndex]));

  for (let index = lastIndex - 1; index >= 0 && deficit > 0; index -= 1) {
    const shiftAmount = roundCurrency(Math.min(adjustedAmounts[index], deficit));
    adjustedAmounts[index] = roundCurrency(adjustedAmounts[index] - shiftAmount);
    adjustedAmounts[lastIndex] = roundCurrency(adjustedAmounts[lastIndex] + shiftAmount);
    deficit = roundCurrency(deficit - shiftAmount);
  }

  return adjustedAmounts;
};

const getNicDerivedDobMismatchWarning = (personalInfo) => {
  const derived = getPersonalInfoFromNic(personalInfo?.nationalId);
  if (!derived?.dateOfBirth || !personalInfo?.dateOfBirth) {
    return '';
  }

  const currentDob = String(personalInfo.dateOfBirth).slice(0, 10);
  if (currentDob !== derived.dateOfBirth) {
    return `The date of birth you entered does not match your NIC (${derived.dateOfBirth}). Please verify both are correct.`;
  }

  return '';
};

/**
 * PublicApplicationByNumber Component
 * Public form for students to complete their application using application number (no token required)
 */
const PublicApplicationByNumber = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [application, setApplication] = useState(null);
  const [baselineEmail, setBaselineEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [finalTermsAgreed, setFinalTermsAgreed] = useState(false);
  const [cardTermsAgreed, setCardTermsAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollmentTermsText, setEnrollmentTermsText] = useState('');
  const [showEnrollmentTermsModal, setShowEnrollmentTermsModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepNavigating, setStepNavigating] = useState(null);
  const [errors, setErrors] = useState({});
  const [dobNicMismatchWarning, setDobNicMismatchWarning] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileExpired, setTurnstileExpired] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [registrationClosedMessage, setRegistrationClosedMessage] = useState('');
  const [enrollmentCompletedMessage, setEnrollmentCompletedMessage] = useState('');

  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const turnstileRef = useRef(null);
  const maxApplicantDateOfBirth = useMemo(() => getMaxApplicantDateOfBirth(new Date()), []);
  const totalSteps = 5;
  const [subProduct, setSubProduct] = useState(null);
  const [prerequisiteChecks, setPrerequisiteChecks] = useState({});
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [screeningAnswers, setScreeningAnswers] = useState({});
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [paymentType, setPaymentType] = useState('full'); // 'full' or 'installment'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentSkipped, setPaymentSkipped] = useState(false);
  const [publicPaymentMethod, setPublicPaymentMethod] = useState('bank'); // 'bank' or 'card'
  const [cardProcessing, setCardProcessing] = useState(false);
  const [mainProduct, setMainProduct] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]); // Array of module codes
  const [desiredPaymentAmount, setDesiredPaymentAmount] = useState(0); // Amount student wants to pay
  const [modulePaymentMode, setModulePaymentMode] = useState('total'); // 'total' | 'installment'
  const [cardPaymentInfo, setCardPaymentInfo] = useState(null); // { status, amount, currency, date, referenceNumber }
  const [paymentLocked, setPaymentLocked] = useState(false);
  const [cardPaymentSummary, setCardPaymentSummary] = useState(null);
  const [backendPaymentSnapshot, setBackendPaymentSnapshot] = useState(null);
  // Module exemption state
  const [moduleExemptions, setModuleExemptions] = useState([]); // Saved exemption requests from DB
  const [exemptionDrafts, setExemptionDrafts] = useState({}); // { [moduleCode]: { files: [], tempFileIds: [] } } – unsaved UI drafts
  const [savingExemption, setSavingExemption] = useState({}); // { [moduleCode]: boolean }
  const [uploadingExemptionDocs, setUploadingExemptionDocs] = useState({}); // { [moduleCode]: boolean }
  const [requirementsCategoryId, setRequirementsCategoryId] = useState('');
  const [isEarlyBirdCleared, setIsEarlyBirdCleared] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState(null);
  const [discountQuote, setDiscountQuote] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');
  // Scholarship awarded by a scholarship discount code typed into this form (before it is saved on the application)
  const [codeScholarship, setCodeScholarship] = useState(null);
  // Mandatory proof / agreement document when a scholarship applies
  const [scholarshipProof, setScholarshipProof] = useState(null);
  const [uploadingScholarshipProof, setUploadingScholarshipProof] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [nationalityQuery, setNationalityQuery] = useState('');
  // Active tax configs for CRM calculations (fetched once, no auth required)
  const [activeTaxes, setActiveTaxes] = useState([]);
  const hasInitializedDesiredAmountRef = useRef(false);
  const desiredAmountTouchedRef = useRef(false);
  const lastAutoDesiredAmountRef = useRef(null);
  const lastHandledCardReturnRef = useRef('');
  // Note: Document upload removed - users will receive a separate link after submission

  // Requirement categories configured on the program
  const requirementCategoriesList = useMemo(() => {
    return Array.isArray(subProduct?.requirementCategories)
      ? subProduct.requirementCategories
      : [];
  }, [subProduct]);

  // Scholarship detection (a scholarship code typed in this form wins over the saved one)
  const activeScholarship = codeScholarship
    || (application?.scholarship?.isScholarship ? application.scholarship : null);
  const hasScholarship = Boolean(activeScholarship?.isScholarship);
  const isModuleSpecificScholarship = activeScholarship?.scope === 'MODULE_SPECIFIC';
  const hasScholarshipFeeExclusions = Boolean(
    activeScholarship?.covers?.courseFee === false ||
    activeScholarship?.covers?.registrationFee === false ||
    activeScholarship?.covers?.membershipFee === false ||
    activeScholarship?.covers?.internationalPaymentFee === false ||
    activeScholarship?.covers?.internationalFee === false
  );
  const isFullScholarship = Boolean(
    hasScholarship &&
    (
      (
        !isModuleSpecificScholarship &&
        !hasScholarshipFeeExclusions &&
        (activeScholarship?.scholarshipType === 'FULL' || activeScholarship?.percentage === 100)
      ) ||
      // The server worked out that the scholarship leaves nothing to pay
      (discountQuote?.isScholarship === true && discountQuote?.isFullScholarship === true)
    )
  );
  const scholarshipCoversReg = Boolean(
    hasScholarship &&
    (isFullScholarship || activeScholarship?.covers?.registrationFee !== false)
  );
  const scholarshipCoversMem = Boolean(
    hasScholarship &&
    (isFullScholarship || activeScholarship?.covers?.membershipFee !== false)
  );
  const scholarshipCoversExam = Boolean(
    hasScholarship &&
    (isFullScholarship || activeScholarship?.covers?.examFee === true)
  );
  const scholarshipCoversInt = Boolean(
    hasScholarship &&
    (isFullScholarship || (activeScholarship?.covers?.internationalPaymentFee !== false && activeScholarship?.covers?.internationalFee !== false))
  );

  // Fees a scholarship covers are waived in the same proportion the server applies (the quote's breakdown);
  // a full waiver leaves nothing payable, a partial one leaves the uncovered share payable.
  const scholarshipFeeAfterWaiver = (baseAmount, breakdownKey, isCovered) => {
    const base = Number(baseAmount) || 0;
    if (!hasScholarship || !isCovered || base <= 0) return base;
    if (isFullScholarship) return 0;
    const breakdown = discountQuote?.isScholarship ? discountQuote?.scholarshipWaiver?.breakdown : null;
    const waived = breakdown
      ? (Number(breakdown[breakdownKey]) || 0)
      : (base * (Number(activeScholarship?.percentage) || 0)) / 100;
    return Math.max(0, roundCurrency(base - waived));
  };

  // Check if main product is module-based
  const isModuleBased = mainProduct?.pricingType === 'Module Based';
  const allowedPaymentOption = application?.allowedPaymentOption || 'both';
  const modulesLocked = Boolean(
    application?.moduleSelectionsLocked || application?.moduleSelectionsAssignedBy
  );
  const payTotalOnly = Boolean(application?.modulePricingTerms?.payTotalOnly);
  const hasModuleInstallmentPlan = Number(subProduct?.moduleInstallments?.count) > 0;
  const showPayTotalOption = isModuleBased
    ? true
    : (allowedPaymentOption !== 'installment_only');
  const showInstallmentOption = isModuleBased
    ? (!payTotalOnly && hasModuleInstallmentPlan)
    : (allowedPaymentOption !== 'full_only');
  const assigneeModuleCodeSet = React.useMemo(() => {
    if (!modulesLocked) return null;
    return new Set(
      (application?.moduleSelections || [])
        .map((m) => String(m.code || m.moduleCode || '').trim().toUpperCase())
        .filter(Boolean)
    );
  }, [modulesLocked, application?.moduleSelections]);
  // Prefer stage-based modules (new structure); fall back to legacy flat array
  const rawModuleStages = (mainProduct?.moduleBasedPricing?.stages || [])
    .slice().sort((a, b) => (a.stageNumber || 0) - (b.stageNumber || 0));
  const rawAvailableModules = rawModuleStages.length > 0
    ? rawModuleStages.flatMap(s => s.modules || [])
    : (mainProduct?.moduleBasedPricing?.modules || []);
  const availableModules = React.useMemo(() => {
    if (!assigneeModuleCodeSet) return rawAvailableModules;
    return rawAvailableModules.filter((m) =>
      assigneeModuleCodeSet.has(String(m.code || '').trim().toUpperCase())
    );
  }, [rawAvailableModules, assigneeModuleCodeSet]);
  const moduleStages = React.useMemo(() => {
    if (!assigneeModuleCodeSet) return rawModuleStages;
    return rawModuleStages
      .map((stage) => ({
        ...stage,
        modules: (stage.modules || []).filter((m) =>
          assigneeModuleCodeSet.has(String(m.code || '').trim().toUpperCase())
        )
      }))
      .filter((stage) => (stage.modules || []).length > 0);
  }, [rawModuleStages, assigneeModuleCodeSet]);
  const assignedModuleByCode = React.useMemo(() => {
    const map = new Map();
    (application?.moduleSelections || []).forEach((module) => {
      map.set(String(module.code || module.moduleCode || '').trim().toUpperCase(), module);
    });
    return map;
  }, [application?.moduleSelections]);
  const resolveAssignedModule = (catalogModule) => {
    const assigned = assignedModuleByCode.get(String(catalogModule?.code || '').trim().toUpperCase());
    const base = assigned ? { ...catalogModule, ...assigned } : catalogModule;
    const isRepeat = isRepeatFeeModule(base, catalogModule);
    const repeatAttempt = isRepeat ? getRepeatAttemptNumber(base, catalogModule) : null;
    return {
      ...base,
      isRepeat,
      feeType: isRepeat ? 'REPEAT_FEE' : (base?.feeType || 'MODULE_FEE'),
      repeatAttempt
    };
  };
  const storedCardAgreementAudit = application?.agreementAudit?.current?.cardPayment || null;
  const hasStoredCardAgreement = storedCardAgreementAudit?.accepted === true;
  const storedCardAgreementAcceptedAt = storedCardAgreementAudit?.acceptedAt
    ? new Date(storedCardAgreementAudit.acceptedAt)
    : null;
  const shouldUseStoredCardAgreementForSubmit = !!cardPaymentInfo && hasStoredCardAgreement;
  const isSubmissionAgreementSatisfied = shouldUseStoredCardAgreementForSubmit || finalTermsAgreed;

  // Exemption helpers
  const getModuleExemption = useCallback(
    (code) => moduleExemptions.find((e) => e.moduleCode === String(code || '').toUpperCase()),
    [moduleExemptions]
  );
  const getResolvedModulePayableTotal = (catalogModule) => {
    const exemption = getModuleExemption(catalogModule?.code);
    if (exemption?.status === 'approved') {
      return Number(exemption.exemptionFee) || 0;
    }

    const displayModule = resolveAssignedModule(catalogModule);
    let total = Number(displayModule.moduleFee) || 0;
    if (displayModule.examination?.firstAttempt && !displayModule.examination.firstAttempt.included) {
      total += Number(displayModule.examination.firstAttempt.fee) || 0;
    }
    return total;
  };
  const assignedModuleCodeSetForExemptions = React.useMemo(() => {
    if (assigneeModuleCodeSet && assigneeModuleCodeSet.size > 0) {
      return assigneeModuleCodeSet;
    }
    return new Set(
      selectedModules
        .map((code) => String(code || '').trim().toUpperCase())
        .filter(Boolean)
    );
  }, [assigneeModuleCodeSet, selectedModules]);
  const hasPendingExemptions = moduleExemptions.some(
    (e) => e.status === 'pending'
      && assignedModuleCodeSetForExemptions.has(String(e.moduleCode || '').trim().toUpperCase())
  );

  const resolvedStudentType = useMemo(() => (
    resolveStudentTypeFromApplicationRefs({
      resolvedStudentType: application?.resolvedStudentType,
      leadId: application?.leadId,
      studentId: application?.studentId,
      allowStudentFallback: false
    })
  ), [
    application?.resolvedStudentType,
    application?.leadId,
    application?.studentId
  ]);

  const getExpectedModuleExemptionFee = useCallback((module) => (
    resolvedStudentType === 'internal'
      ? Number(module?.exemptionFeeInternal ?? 0)
      : Number(module?.exemptionFeeExternal ?? 0)
  ), [resolvedStudentType]);

  const paymentEligibilityContext = useMemo(() => {
    const moduleSelections = isModuleBased
      ? (Array.isArray(moduleStages) ? moduleStages : []).flatMap((stage) =>
        (stage.modules || [])
          .filter((module) => selectedModules.includes(module.code))
          .map((module) => ({
            ...module,
            stageNumber: Number(module?.stageNumber) || Number(stage?.stageNumber) || 1,
            stageName: module?.stageName || stage?.stageName || null
          }))
      )
      : [];
    return {
      isModuleBased,
      paymentType,
      modulePaymentMode,
      moduleSelections,
      mainProduct: application?.mainProductId && typeof application.mainProductId === 'object'
        ? application.mainProductId
        : (mainProduct || null)
    };
  }, [
    isModuleBased,
    paymentType,
    modulePaymentMode,
    selectedModules.join(','),
    moduleStages,
    application?.mainProductId,
    mainProduct
  ]);

  const legacyDiscountEligibility = useMemo(
    () => resolveDiscountEligibilitySource(application, subProduct),
    [application, subProduct]
  );

  const activeEarlyBirdDiscount = useMemo(() => {
    const discounts = Array.isArray(subProduct?.discounts) ? subProduct.discounts : [];
    const now = Date.now();

    return discounts.find((discount) => {
      if (!discount || discount.type !== 'early_bird' || !discount.isActive) {
        return false;
      }

      const validFrom = discount.validFrom ? new Date(discount.validFrom).getTime() : null;
      const validUntil = discount.validUntil ? new Date(discount.validUntil).getTime() : null;
      if (!validFrom || !validUntil || now < validFrom || now > validUntil) {
        return false;
      }

      if (!isDiscountRecordAllowedForPaymentContext(discount, paymentEligibilityContext, legacyDiscountEligibility)) {
        return false;
      }

      const maxUsage = Number(discount?.conditions?.maxUsage) || 0;
      const currentUsage = Number(discount?.conditions?.currentUsage) || 0;
      return !maxUsage || currentUsage < maxUsage;
    }) || null;
  }, [subProduct?.discounts, paymentEligibilityContext, legacyDiscountEligibility]);

  const isEarlyBirdApplied = discountQuote?.appliedDiscount?.kind === 'early_bird';
  const isEarlyBirdAvailable = !isEarlyBirdCleared && (isEarlyBirdApplied || !!activeEarlyBirdDiscount);
  const discountCurrency = discountQuote?.currency || (subProduct?.baseFee?.currency || 'LKR');
  const discountsAllowedForCurrentPayment = hasScholarship || hasAnyDiscountAllowedForPaymentContext(
    subProduct?.discounts,
    paymentEligibilityContext,
    legacyDiscountEligibility
  );
  const discountedMaxAmount = ((isModuleBased && modulePaymentMode === 'total') || paymentType === 'full')
    ? (discountQuote && discountQuote.finalAmount !== undefined ? Number(discountQuote.finalAmount) : 0)
    : 0;

  const membershipFeeCurrency =
    application?.membershipFeePreviewMeta?.currency
    || application?.membershipFeePreview?.currency
    || subProduct?.processingFee?.currency
    || discountCurrency
    || 'LKR';
  const membershipFeePreview = application?.membershipFeePreview || null;
  const membershipFeeBands = Array.isArray(membershipFeePreview?.bands) ? membershipFeePreview.bands : [];
  const membershipFeeIsIncluded = membershipFeePreview
    ? membershipFeePreview.included === true
    : isMembershipFeeIncluded(subProduct);
  // Catalog/display amount (sum of required bands, or legacy processingFee).
  const membershipFeeAmountBase = membershipFeePreview
    ? (Number(application?.membershipFeePreviewMeta?.configuredAmount)
      || Number(membershipFeePreview.amount)
      || 0)
    : Number(subProduct?.processingFee?.amount || 0);
  // Payable amount after stage-band coverage waivers.
  const membershipFeePayableAmount = membershipFeePreview
    ? (membershipFeeIsIncluded
      ? 0
      : (Number(application?.membershipFeePreviewMeta?.payableAmount)
        ?? Number(membershipFeePreview.amount)
        ?? 0))
    : (membershipFeeIsIncluded ? 0 : membershipFeeAmountBase);

  const getMembershipFeeForPaymentContext = () => {
    if (paymentSkipped || cardPaymentInfo) return 0;
    return scholarshipFeeAfterWaiver(membershipFeePayableAmount, 'membershipFee', scholarshipCoversMem);
  };

  const isInternalStudent = resolvedStudentType === 'internal';
  const registrationFeeContext = useMemo(
    () => resolveRegistrationFeeContext(subProduct, resolvedStudentType),
    [subProduct, resolvedStudentType]
  );
  const registrationFeeAmountBase = registrationFeeContext.configuredRegistrationFeeAmount;
  const registrationFeeCurrency = registrationFeeContext.registrationFeeCurrency;
  const registrationFeeIsIncluded = registrationFeeContext.registrationFeeIncludedInCourse;
  const registrationFeeWaivedForInternal = registrationFeeContext.waivedForInternalStudent;

  const renderRegistrationFeeSummaryLine = (payableAmount, { amountClassName = 'font-medium tabular-nums' } = {}) => {
    if (registrationFeeAmountBase <= 0) return null;

    if (scholarshipCoversReg && !(Number(payableAmount) > 0)) {
      return (
        <div className="flex justify-between text-gray-500 italic">
          <span>Registration fee <em>(waived — scholarship)</em></span>
          <span className={amountClassName}>{registrationFeeCurrency} {registrationFeeAmountBase.toLocaleString()}</span>
        </div>
      );
    }

    if (registrationFeeWaivedForInternal) {
      return (
        <div className="flex justify-between text-gray-500 italic">
          <span>Registration fee <em>(waived — internal student)</em></span>
          <span className={amountClassName}>{registrationFeeCurrency} {registrationFeeAmountBase.toLocaleString()}</span>
        </div>
      );
    }

    if (registrationFeeIsIncluded) {
      return (
        <div className="flex justify-between text-xs text-gray-400 italic">
          <span>Registration fee <em>(already included in course fee)</em></span>
          <span className={amountClassName}>{registrationFeeCurrency} {registrationFeeAmountBase.toLocaleString()}</span>
        </div>
      );
    }

    return (
      <div className="flex justify-between text-gray-700">
        <span>Registration fee</span>
        <span className={amountClassName}>+{registrationFeeCurrency} {payableAmount.toLocaleString()}</span>
      </div>
    );
  };

  const getRegistrationFeeForPaymentContext = () => {
    if (paymentSkipped || cardPaymentInfo || isInternalStudent) return 0;
    return scholarshipFeeAfterWaiver(registrationFeeContext.registrationFeeAmount, 'registrationFee', scholarshipCoversReg);
  };

  const internationalPaymentFeeAmountBase = isModuleBased && isInternationalPaymentEnabled(subProduct)
    ? Number(subProduct?.internationalPaymentFee?.amount || 0)
    : 0;
  const internationalPaymentFeeCurrency = subProduct?.internationalPaymentFee?.currency || discountCurrency || 'LKR';

  const getInternationalPaymentFeeForPaymentContext = () => {
    if (paymentSkipped || cardPaymentInfo || !isModuleBased || isFullScholarship) return 0;
    return scholarshipFeeAfterWaiver(internationalPaymentFeeAmountBase, 'internationalPaymentFee', scholarshipCoversInt);
  };

  const getMinPaymentAmount = () => {
    if (paymentSkipped || cardPaymentInfo || isFullScholarship) return 0;
    return getRegistrationFeeForPaymentContext()
      + getMembershipFeeForPaymentContext()
      + getInternationalPaymentFeeForPaymentContext();
  };

  // Sub-product specific taxes: match taxIds stored on sub-product against live Finance-service data.
  // activeTaxes is loaded asynchronously; when it is empty the memo returns [] and re-runs once it arrives.
  const subProductApplicableTaxes = useMemo(() => {
    if (!Array.isArray(subProduct?.applicableTaxes) || !subProduct.applicableTaxes.length) return [];
    return subProduct.applicableTaxes
      .map(at => {
        const storedTaxId = at?.taxId?._id ? String(at.taxId._id) : String(at?.taxId || '');
        const config = activeTaxes.find(t => String(t._id) === storedTaxId);
        if (!config) return null;
        return { ...config, included: at.included === true };
      })
      .filter(Boolean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaxes, subProduct?.applicableTaxes]);

  // Calculate total amount for selected modules
  const calculateModuleTotal = () => {
    if (!isModuleBased || selectedModules.length === 0) return 0;

    const calculated = availableModules
      .filter(module => selectedModules.includes(module.code))
      .reduce((total, module) => total + getResolvedModulePayableTotal(module), 0);

    if (payTotalOnly && application?.modulePricingTerms?.negotiatedSubtotal != null) {
      return Math.max(0, Number(application.modulePricingTerms.negotiatedSubtotal) || 0);
    }

    return calculated;
  };

  const shouldUseBackendTaxSnapshot = useMemo(() => {
    if (!backendPaymentSnapshot) return false;
    if (paymentLocked || cardPaymentInfo) return true;

    const backendInstallments = Array.isArray(backendPaymentSnapshot?.installmentPlan?.installments)
      ? backendPaymentSnapshot.installmentPlan.installments
      : [];
    const backendIsInstallment = backendInstallments.length > 0;

    if (isModuleBased) {
      const backendModuleCodes = Array.isArray(backendPaymentSnapshot?.moduleSelections)
        ? backendPaymentSnapshot.moduleSelections
            .map((module) => String(module?.code || '').trim().toUpperCase())
            .filter(Boolean)
            .sort()
        : [];
      const localModuleCodes = [...selectedModules]
        .map((code) => String(code || '').trim().toUpperCase())
        .filter(Boolean)
        .sort();
      const moduleSelectionMatches = backendModuleCodes.length > 0 &&
        JSON.stringify(localModuleCodes) === JSON.stringify(backendModuleCodes);
      return moduleSelectionMatches && ((modulePaymentMode === 'installment') === backendIsInstallment);
    }

    return (paymentType === 'installment') === backendIsInstallment;
  }, [
    backendPaymentSnapshot,
    paymentLocked,
    cardPaymentInfo,
    isModuleBased,
    modulePaymentMode,
    paymentType,
    selectedModules
  ]);

  const moduleInstallmentPreview = useMemo(() => {
    if (!isModuleBased || modulePaymentMode !== 'installment' || paymentSkipped || selectedModules.length === 0) {
      return null;
    }

    if (isFullScholarship) {
      return {
        subtotalBeforeDiscount: 0,
        subtotalAfterDiscount: 0,
        totalExclusiveTaxAmount: 0,
        totalInvoiceAmount: 0,
        discountAmount: 0,
        installments: [],
        taxTotals: []
      };
    }

    if (shouldUseBackendTaxSnapshot) {
      const backendInstallments = Array.isArray(backendPaymentSnapshot?.installmentPlan?.installments)
        ? backendPaymentSnapshot.installmentPlan.installments
        : [];
      if (backendInstallments.length > 0) {
        const invoiceSummary = backendPaymentSnapshot?.invoiceSummary || {};
        const taxes = Array.isArray(backendPaymentSnapshot?.taxes)
          ? backendPaymentSnapshot.taxes.filter((tax) => Number(tax?.taxAmount) > 0)
          : [];
        return {
          subtotalBeforeDiscount: roundCurrency(invoiceSummary.subtotalBeforeDiscount || 0),
          subtotalAfterDiscount: roundCurrency(invoiceSummary.subtotalAfterDiscount || 0),
          totalExclusiveTaxAmount: roundCurrency(invoiceSummary.exclusiveTaxAmount || 0),
          totalInvoiceAmount: roundCurrency(invoiceSummary.totalInvoiceAmount || 0),
          discountAmount: roundCurrency(invoiceSummary.discountAmount || 0),
          installments: backendInstallments.map((installment, index) => ({
            installmentNumber: Number(installment?.installmentNumber) || index + 1,
            percentage: Number(installment?.percentage) || 0,
            description: installment?.description || `Installment ${index + 1}`,
            preDiscountAmount: roundCurrency(installment?.preDiscountAmount || installment?.amount || 0),
            discountAmount: roundCurrency(installment?.discountAmount || 0),
            netAmount: roundCurrency(installment?.netAmount || installment?.amount || 0),
            taxLines: [],
            exclusiveTaxTotal: roundCurrency(installment?.exclusiveTaxTotal || installment?.exclusiveTaxAmount || 0),
            inclusiveTaxTotal: roundCurrency(installment?.inclusiveTaxTotal || installment?.inclusiveTaxAmount || 0),
            totalDue: roundCurrency(installment?.amount || 0)
          })),
          taxTotals: taxes.map((tax) => ({
            name: tax?.name || 'Tax',
            percentage: Number(tax?.percentage) || 0,
            taxAmount: roundCurrency(tax?.taxAmount || 0),
            currency: tax?.currency || invoiceSummary.currency || 'LKR',
            included: tax?.included === true
          }))
        };
      }
    }

    const installmentCount = Math.max(0, Number(subProduct?.moduleInstallments?.count) || 0);
    if (!installmentCount) return null;

    const weights = normalizeInstallmentWeights(installmentCount, subProduct?.moduleInstallments?.percentages);
    if (!weights.length) return null;

    const courseAmount = roundCurrency(calculateModuleTotal());
    const registrationFee = roundCurrency(getRegistrationFeeForPaymentContext());
    const membershipFee = roundCurrency(getMembershipFeeForPaymentContext());
    const internationalPaymentFee = roundCurrency(getInternationalPaymentFeeForPaymentContext());
    if (courseAmount <= 0) return null;

    const requestedDiscountAmount = roundCurrency(discountQuote?.discountAmount || 0);
    const totalWeight = weights.reduce((sum, value) => sum + value, 0);
    const normalizedPercentages = weights.map((weight) => roundCurrency((weight / totalWeight) * 100));
    const isScholarshipQuote = Boolean(hasScholarship && (discountQuote?.isScholarship || discountQuote?.appliedDiscount?.kind === 'scholarship'));

    let courseInstallmentAmounts;
    let appliedDiscountAmount = 0;

    if (isScholarshipQuote && requestedDiscountAmount > 0) {
      const courseWaiver = Math.min(courseAmount, Number(discountQuote?.scholarshipWaiver?.courseFeeWaiver ?? requestedDiscountAmount));
      const netCourseAmount = Math.max(0, roundCurrency(courseAmount - courseWaiver));
      courseInstallmentAmounts = allocateByWeights(netCourseAmount, weights);
      appliedDiscountAmount = courseWaiver;
    } else {
      courseInstallmentAmounts = rebalanceForLastInstallmentDiscount(
        allocateByWeights(courseAmount, weights),
        requestedDiscountAmount
      );
      const lastIndex = courseInstallmentAmounts.length - 1;
      appliedDiscountAmount = roundCurrency(Math.min(requestedDiscountAmount, courseInstallmentAmounts[lastIndex] || 0));
    }

    const lastIndex = courseInstallmentAmounts.length - 1;
    const currency = discountCurrency || membershipFeeCurrency || registrationFeeCurrency || 'LKR';

    const installments = courseInstallmentAmounts.map((courseInstallmentAmount, index) => {
      const firstInstallmentExtras = index === 0 ? roundCurrency(registrationFee + membershipFee + internationalPaymentFee) : 0;
      const preDiscountAmount = roundCurrency(courseInstallmentAmount + firstInstallmentExtras);
      const installmentDiscountAmount = isScholarshipQuote ? 0 : (index === lastIndex ? appliedDiscountAmount : 0);
      const breakdown = computeInstallmentTaxBreakdown({
        taxes: subProductApplicableTaxes,
        preDiscountAmount,
        discountAmount: installmentDiscountAmount,
        currency
      });

      return {
        installmentNumber: index + 1,
        percentage: normalizedPercentages[index],
        description: `Installment ${index + 1} (${normalizedPercentages[index].toLocaleString(undefined, { maximumFractionDigits: 2 })}%)`,
        preDiscountAmount: breakdown.preDiscountAmount,
        discountAmount: breakdown.discountAmount,
        netAmount: breakdown.subtotalAfterDiscount,
        taxLines: breakdown.taxLines,
        exclusiveTaxTotal: breakdown.exclusiveTaxTotal,
        inclusiveTaxTotal: breakdown.inclusiveTaxTotal,
        totalDue: breakdown.amount
      };
    });

    const taxTotalsMap = new Map();
    installments.forEach((installment) => {
      (Array.isArray(installment.taxLines) ? installment.taxLines : []).forEach((tax) => {
        const key = `${tax.name}::${tax.percentage}::${tax.included ? 'INCLUDED' : 'EXCLUDED'}`;
        const existing = taxTotalsMap.get(key) || {
          name: tax.name,
          percentage: tax.percentage,
          taxAmount: 0,
          currency: tax.currency || currency,
          included: tax.included === true
        };
        existing.taxAmount = roundCurrency(existing.taxAmount + (Number(tax.taxAmount) || 0));
        taxTotalsMap.set(key, existing);
      });
    });
    const taxTotals = [...taxTotalsMap.values()];

    return {
      subtotalBeforeDiscount: roundCurrency(installments.reduce((sum, installment) => sum + installment.preDiscountAmount, 0)),
      subtotalAfterDiscount: roundCurrency(installments.reduce((sum, installment) => sum + installment.netAmount, 0)),
      totalExclusiveTaxAmount: roundCurrency(installments.reduce((sum, installment) => sum + installment.exclusiveTaxTotal, 0)),
      totalInvoiceAmount: roundCurrency(installments.reduce((sum, installment) => sum + installment.totalDue, 0)),
      discountAmount: appliedDiscountAmount,
      installments,
      taxTotals
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isModuleBased,
    modulePaymentMode,
    paymentSkipped,
    selectedModules.join(','),
    subProduct?.moduleInstallments?.count,
    JSON.stringify(subProduct?.moduleInstallments?.percentages || []),
    subProductApplicableTaxes,
    discountQuote?.discountAmount,
    membershipFeeAmountBase,
    membershipFeeIsIncluded,
    registrationFeeAmountBase,
    registrationFeeIsIncluded,
    internationalPaymentFeeAmountBase,
    resolvedStudentType,
    availableModules,
    application?.moduleSelections,
    application?.modulePricingTerms,
    JSON.stringify(moduleExemptions.map(e => `${e.moduleCode}:${e.status}:${e.exemptionFee ?? ''}`).sort()),
    shouldUseBackendTaxSnapshot,
    backendPaymentSnapshot
  ]);

  const computeProductInstallmentPlanTaxInfo = (planTotal, discountAmount = 0) => {
    if (!subProductApplicableTaxes.length || paymentSkipped) return null;
    const base = Number(planTotal || 0);
    const coursePayable = base > 0 ? Math.max(0, base - discountAmount) : 0;
    if (coursePayable <= 0) return null;
    const currency = discountCurrency || membershipFeeCurrency || registrationFeeCurrency || 'LKR';
    const preview = computePaymentBreakdownPreview({
      courseAmount: coursePayable,
      discountAmount: 0,
      membershipFeeAmount: membershipFeeAmountBase,
      membershipFeeIncluded: membershipFeeIsIncluded,
      registrationFeeAmount: registrationFeeAmountBase,
      registrationFeeIncluded: registrationFeeIsIncluded || registrationFeeWaivedForInternal,
      internationalPaymentFeeAmount: getInternationalPaymentFeeForPaymentContext(),
      taxes: subProductApplicableTaxes,
      currency
    });
    if (!preview.taxes.length) return null;
    return {
      taxes: preview.taxes,
      exclusiveTaxes: preview.exclusiveTaxes,
      inclusiveTaxes: preview.inclusiveTaxes,
      totalTaxAmount: preview.totalTaxAmount,
      grossTotal: preview.grossTotal,
      currency
    };
  };

  // Derive tax amounts from sub-product applicable taxes + current payment totals
  const taxInfo = useMemo(() => {
    if (shouldUseBackendTaxSnapshot) {
      const snapshotTaxes = Array.isArray(backendPaymentSnapshot?.taxes)
        ? backendPaymentSnapshot.taxes.filter((tax) => Number(tax?.taxAmount) > 0)
        : [];
      if (snapshotTaxes.length > 0) {
        const exclusiveTaxes = snapshotTaxes.filter((tax) => tax?.included !== true);
        const inclusiveTaxes = snapshotTaxes.filter((tax) => tax?.included === true);
        const invoiceSummary = backendPaymentSnapshot?.invoiceSummary || {};
        return {
          taxes: snapshotTaxes,
          exclusiveTaxes,
          inclusiveTaxes,
          totalTaxAmount: roundCurrency(
            Number(invoiceSummary?.exclusiveTaxAmount) ||
            exclusiveTaxes.reduce((sum, tax) => sum + (Number(tax?.taxAmount) || 0), 0)
          ),
          grossTotal: roundCurrency(Number(invoiceSummary?.subtotalAfterDiscount) || 0),
          subtotalAfterDiscount: roundCurrency(Number(invoiceSummary?.subtotalAfterDiscount) || 0),
          totalInvoiceAmount: roundCurrency(Number(invoiceSummary?.totalInvoiceAmount) || 0),
          currency: invoiceSummary?.currency || snapshotTaxes?.[0]?.currency || 'LKR'
        };
      }
    }

    if (isFullScholarship) {
      return {
        taxes: [],
        exclusiveTaxes: [],
        inclusiveTaxes: [],
        totalTaxAmount: 0,
        grossTotal: 0,
        currency: discountCurrency || membershipFeeCurrency || registrationFeeCurrency || 'LKR'
      };
    }

    if (isModuleBased && modulePaymentMode === 'installment' && moduleInstallmentPreview) {
      const taxes = moduleInstallmentPreview.taxTotals;
      const exclusiveTaxes = taxes.filter(t => !t.included);
      const inclusiveTaxes = taxes.filter(t => t.included);
      return {
        taxes,
        exclusiveTaxes,
        inclusiveTaxes,
        totalTaxAmount: roundCurrency(exclusiveTaxes.reduce((sum, tax) => sum + tax.taxAmount, 0)),
        grossTotal: moduleInstallmentPreview.subtotalAfterDiscount,
        currency: discountCurrency || membershipFeeCurrency || registrationFeeCurrency || 'LKR'
      };
    }

    if (!subProductApplicableTaxes.length || paymentSkipped || isFullScholarship) return null;
    const membershipFee = getMembershipFeeForPaymentContext();
    const registrationFee = getRegistrationFeeForPaymentContext();
    const internationalPaymentFee = getInternationalPaymentFeeForPaymentContext();
    let coursePayable = 0;
    if (isModuleBased) {
      // Use the raw pre-discount module total here. The discount is applied separately
      // via discountAmount inside computePaymentBreakdownPreview. Using discountedMaxAmount
      // (which is already post-discount) would cause the discount to be subtracted twice.
      const moduleTotal = selectedModules.length === 0 ? 0 : calculateModuleTotal();
      coursePayable = moduleTotal;
    } else if (paymentType === 'full' && (subProduct?.baseFee?.amount != null || mainProduct?.fee?.amount != null)) {
      // When a promotional discount quote is present with an effective base amount (e.g. installment base fee on dual-base pricing),
      // use that pre-discount calculation base so that computePaymentBreakdownPreview deducts the promotional discount
      // from the full standard base fee rather than the discounted full payment concession fee.
      coursePayable = Number(
        (discountQuote?.appliedDiscount && discountQuote?.baseAmount)
          ? discountQuote.baseAmount
          : ((subProduct?.baseFee?.amount != null && Number(subProduct.baseFee.amount) > 0)
              ? subProduct.baseFee.amount
              : (mainProduct?.fee?.amount || 0))
      );
    } else if (paymentType === 'installment' && selectedPlan) {
      const discountAmount = Number(discountQuote?.discountAmount || 0);
      return computeProductInstallmentPlanTaxInfo(selectedPlan.totalAmount, discountAmount);
    } else if (paymentType === 'installment') {
      return null;
    } else {
      return null;
    }
    if (coursePayable <= 0) return null;
    const currency = discountCurrency || membershipFeeCurrency || registrationFeeCurrency || 'LKR';
    const discountAmount = roundCurrency(Number(discountQuote?.discountAmount) || 0);
    const preview = computePaymentBreakdownPreview({
      courseAmount: coursePayable,
      discountAmount,
      membershipFeeAmount: membershipFeeAmountBase,
      membershipFeeIncluded: membershipFeeIsIncluded,
      registrationFeeAmount: registrationFeeAmountBase,
      registrationFeeIncluded: registrationFeeIsIncluded || registrationFeeWaivedForInternal,
      internationalPaymentFeeAmount: internationalPaymentFee,
      taxes: subProductApplicableTaxes,
      currency
    });
    if (!preview.taxes.length && preview.totalTaxAmount <= 0) return null;
    return {
      taxes: preview.taxes,
      exclusiveTaxes: preview.exclusiveTaxes,
      inclusiveTaxes: preview.inclusiveTaxes,
      totalTaxAmount: preview.totalTaxAmount,
      grossTotal: preview.grossTotal,
      subtotalAfterDiscount: preview.subtotalAfterDiscount,
      totalInvoiceAmount: preview.totalInvoiceAmount,
      currency
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subProductApplicableTaxes, paymentSkipped, isModuleBased, modulePaymentMode, selectedModules.join(','),
      discountedMaxAmount, paymentType, selectedPlan?._id, discountQuote?.discountAmount, discountQuote?.baseAmount, discountQuote?.appliedDiscount,
      subProduct?.baseFee?.amount, mainProduct?.fee?.amount, membershipFeeAmountBase, registrationFeeAmountBase, internationalPaymentFeeAmountBase, resolvedStudentType, moduleInstallmentPreview,
      application?.moduleSelections, application?.modulePricingTerms,
      shouldUseBackendTaxSnapshot, backendPaymentSnapshot]);

  const getCurrentPaymentTaxAmount = () => {
    if (isModuleBased && modulePaymentMode === 'installment') {
      return Number(moduleInstallmentPreview?.installments?.[0]?.exclusiveTaxTotal || 0);
    }
    return Number(taxInfo?.totalTaxAmount || 0);
  };

  const isInstallmentDueContext = () => (
    (isModuleBased && modulePaymentMode === 'installment') ||
    (!isModuleBased && paymentType === 'installment' && !!selectedPlan)
  );

  const getCurrentInstallmentDueAmount = () => getMaxPaymentAmount() + getCurrentPaymentTaxAmount();

  const getOverallPlannedPayableAmount = () => {
    if (isModuleBased && modulePaymentMode === 'installment' && moduleInstallmentPreview) {
      return Number(moduleInstallmentPreview.totalInvoiceAmount || 0);
    }

    if (!isModuleBased && paymentType === 'installment' && selectedPlan) {
      const baseTotal = Number(selectedPlan.totalAmount || 0);
      const discountAmount =
        discountQuote?.appliedDiscount && Number(discountQuote?.discountAmount) > 0
          ? Number(discountQuote.discountAmount)
          : 0;
      const discountedBase = discountAmount > 0 ? Math.max(0, baseTotal - discountAmount) : baseTotal;
      return discountedBase + getRegistrationFeeForPaymentContext() + getMembershipFeeForPaymentContext() + (taxInfo?.totalTaxAmount || 0);
    }

    return getCurrentInstallmentDueAmount();
  };

  const getCurrentTaxSnapshots = () => {
    if (isModuleBased && modulePaymentMode === 'installment') {
      return Array.isArray(moduleInstallmentPreview?.taxTotals) ? moduleInstallmentPreview.taxTotals : [];
    }
    return Array.isArray(taxInfo?.taxes) ? taxInfo.taxes : [];
  };

  const notifyPaymentAmountAdjusted = ({ from, to, minAmount, maxAmount }) => {
    // Avoid showing payment-related toasts while the user is on other steps.
    if (currentStep !== 4) return;

    // Only show adjustment toasts when the Desired Payment Amount input is actually visible.
    // Otherwise (e.g. user just switched to installments but hasn't selected a plan yet)
    // the internal desired amount can be recalculated and would confuse users.
    const isDesiredAmountInputVisible =
      !paymentSkipped &&
      !cardPaymentInfo &&
      (
        (isModuleBased && selectedModules.length > 0) ||
        (!isModuleBased && paymentType === 'full' && !!subProduct?.baseFee) ||
        (!isModuleBased && paymentType === 'installment' && !!selectedPlan)
      );
    if (!isDesiredAmountInputVisible) return;

    if (from === to) return;
    const toastId = 'public-application-payment-amount-adjusted';
    if (to === minAmount && minAmount > 0) {
      toast(`Payment amount adjusted to minimum (${membershipFeeCurrency} ${minAmount.toLocaleString()})`, { id: toastId });
      return;
    }
    if (to === maxAmount && maxAmount > 0) {
      toast(`Payment amount adjusted to maximum (${discountCurrency} ${maxAmount.toLocaleString()})`, { id: toastId });
      return;
    }
    toast('Payment amount adjusted to valid range', { id: toastId });
  };

  const getModuleAmountByCode = (code) => {
    const module = availableModules.find(m => m.code === code);
    if (!module) return 0;
    return getResolvedModulePayableTotal(module);
  };

  // Handle module selection
  const toggleModuleSelection = (moduleCode) => {
    if (modulesLocked) return;
    // Modules with a pending or approved exemption cannot be manually toggled
    const exemption = getModuleExemption(moduleCode);
    if (exemption && (exemption.status === 'pending' || exemption.status === 'approved')) {
      return;
    }
    setSelectedModules(prev => {
      if (prev.includes(moduleCode)) {
        return prev.filter(code => code !== moduleCode);
      } else {
        return [...prev, moduleCode];
      }
    });
  };

  // Handle select all modules (skip locked-by-exemption modules)
  const toggleSelectAllModules = () => {
    if (modulesLocked) return;
    const selectableModules = availableModules.filter((m) => {
      const ex = getModuleExemption(m.code);
      return !ex || ex.status === 'rejected';
    });
    const approvedModuleCodes = moduleExemptions
      .filter((e) => e.status === 'approved')
      .map((e) => e.moduleCode);

    if (selectedModules.length >= availableModules.length) {
      // Deselect all except approved (approved are always kept)
      setSelectedModules(approvedModuleCodes);
    } else {
      setSelectedModules([...new Set([...approvedModuleCodes, ...selectableModules.map(m => m.code)])]);
    }
  };

  // ── Module Exemption Handlers ─────────────────────────────────────────────

  /** Open the exemption request draft panel for a module */
  const openExemptionDraft = (moduleCode) => {
    if (payTotalOnly) return;
    const assigned = resolveAssignedModule({ code: moduleCode });
    if (assigned?.isRepeat || assigned?.feeType === 'REPEAT_FEE') {
      toast.error('Module exemption requests are not available for repeat modules.');
      return;
    }
    setExemptionDrafts((prev) => ({
      ...prev,
      [moduleCode]: { files: [] }
    }));
  };

  /** Close/cancel the exemption request draft panel for a module */
  const closeExemptionDraft = (moduleCode) => {
    setExemptionDrafts((prev) => {
      const next = { ...prev };
      delete next[moduleCode];
      return next;
    });
  };

  /**
   * Upload supporting documents for an exemption and then save the request.
   * Called when the applicant clicks "Save" on the exemption panel.
   */
  const saveExemptionRequest = async (moduleCode) => {
    if (payTotalOnly) {
      toast.error('Module exemption requests are not available when your advisor has set a custom module total.');
      return;
    }
    const draft = exemptionDrafts[moduleCode];
    if (!draft) return;
    if (!application?._id) return;

    setSavingExemption((prev) => ({ ...prev, [moduleCode]: true }));
    try {
      // Step 1: Upload files to the application via file service
      const uploadedDocs = [];
      setUploadingExemptionDocs((prev) => ({ ...prev, [moduleCode]: true }));
      for (const file of draft.files) {
          const metadata = { category: 'Other', title: file.name, description: `[MODULE_EXEMPTION] Exemption proof for module ${moduleCode}` };
        const resp = await fileApi.uploadDocumentToApplication(application._id, file, metadata);
        const uploaded = resp.data?.data || resp.data;
        uploadedDocs.push({
          fileId: uploaded.fileId,
          fileName: uploaded.originalName || file.name || uploaded.fileName,
          fileSize: uploaded.fileSize || file.size,
          fileType: uploaded.mimeType || uploaded.fileType || file.type
        });
      }
      setUploadingExemptionDocs((prev) => ({ ...prev, [moduleCode]: false }));

      // Step 2: Submit exemption request to backend
      const result = await requestModuleExemption(application._id, moduleCode, uploadedDocs);
      const updatedExemptions = result.data?.data?.moduleExemptions || [];
      setModuleExemptions(updatedExemptions);

      // Remove the module from selectedModules (pending modules are excluded from totals)
      setSelectedModules((prev) => prev.filter((c) => c !== moduleCode));

      // Clear the draft
      closeExemptionDraft(moduleCode);
      toast.success(`Exemption review request submitted for module ${moduleCode}.`);
    } catch (err) {
      console.error('Save exemption error:', err);
      toast.error(err?.response?.data?.message || 'Failed to submit exemption request. Please try again.');
      setUploadingExemptionDocs((prev) => ({ ...prev, [moduleCode]: false }));
    } finally {
      setSavingExemption((prev) => ({ ...prev, [moduleCode]: false }));
    }
  };

  /** Cancel an already-saved pending exemption and revert module to normal */
  const revokeExemptionRequest = async (moduleCode) => {
    if (!application?._id) return;
    setSavingExemption((prev) => ({ ...prev, [moduleCode]: true }));
    try {
      const result = await cancelModuleExemption(application._id, moduleCode);
      const updatedExemptions = result.data?.data?.moduleExemptions || [];
      setModuleExemptions(updatedExemptions);
      toast.success(`Exemption request for module ${moduleCode} cancelled.`);
    } catch (err) {
      console.error('Revoke exemption error:', err);
      toast.error(err?.response?.data?.message || 'Failed to cancel exemption request.');
    } finally {
      setSavingExemption((prev) => ({ ...prev, [moduleCode]: false }));
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  // Calculate maximum payment amount based on product type
  const getMaxPaymentAmount = () => {
    if (isFullScholarship) return 0;
    const membershipFee = getMembershipFeeForPaymentContext();
    const registrationFee = getRegistrationFeeForPaymentContext();
    const internationalPaymentFee = getInternationalPaymentFeeForPaymentContext();
    const extraFees = membershipFee + registrationFee + internationalPaymentFee;
    if (isModuleBased) {
      if (modulePaymentMode === 'installment') {
        return Number(moduleInstallmentPreview?.installments?.[0]?.netAmount || 0);
      }
      // Total-mode: if a discount is active, the maximum payable is the discounted total.
      const baseMax = (discountQuote && discountQuote.finalAmount !== undefined)
        ? Number(discountQuote.finalAmount)
        : calculateModuleTotal();
      return Math.max(0, baseMax + extraFees);
    } else {
      if (paymentType === 'full' && (subProduct?.baseFee || mainProduct?.fee)) {
        const rawBase = Number(
          (discountQuote?.appliedDiscount && discountQuote?.baseAmount)
            ? discountQuote.baseAmount
            : ((subProduct?.baseFee?.amount != null && Number(subProduct.baseFee.amount) > 0)
                ? subProduct.baseFee.amount
                : (mainProduct?.fee?.amount || 0))
        );
        const baseMax = (discountQuote && discountQuote.finalAmount !== undefined)
          ? Number(discountQuote.finalAmount)
          : (discountedMaxAmount > 0 ? discountedMaxAmount : rawBase);
        return Math.max(0, baseMax + extraFees);
      } else if (paymentType === 'installment') {
        // Until a plan is chosen we don't have a valid payable range for installment payments.
        if (!selectedPlan) return 0;
        let firstInst = Number(selectedPlan.installments?.[0]?.amount || 0);
        if (hasScholarship && (discountQuote?.isScholarship || discountQuote?.appliedDiscount?.kind === 'scholarship') && Number(discountQuote?.discountAmount) > 0) {
          const totalInstSum = (selectedPlan.installments || []).reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
          if (totalInstSum > 0) {
            const share = Math.min(firstInst, roundCurrency(discountQuote.discountAmount * (firstInst / totalInstSum)));
            firstInst = Math.max(0, roundCurrency(firstInst - share));
          }
        }
        return Math.max(0, firstInst + extraFees);
      } else if (extraFees > 0) {
        return extraFees;
      }
    }
    return 0;
  };

  useEffect(() => {
    if (!isModuleBased) return;
    // Keep desired amount aligned with the current mode.
    // For installment mode, reset to 0 so the main desired-amount effect (below)
    // re-initialises to the correct tax-inclusive first-module max.
    if (modulePaymentMode === 'installment') {
      setDesiredPaymentAmount(0);
      desiredAmountTouchedRef.current = false;
    } else {
      const max = getMaxPaymentAmount();
      if (max > 0) {
        // Don't force override if user already typed a valid value
        setDesiredPaymentAmount(prev => (prev > 0 && prev <= max ? prev : max));
      }
    }
  }, [modulePaymentMode, isModuleBased]);

  useEffect(() => {
    if (!payTotalOnly) return;
    if (modulePaymentMode !== 'total') {
      setModulePaymentMode('total');
    }
  }, [payTotalOnly, modulePaymentMode]);

  const hasInitializedModuleModeRef = useRef(false);
  useEffect(() => {
    if (hasInitializedModuleModeRef.current) return;
    if (isModuleBased && !payTotalOnly && hasModuleInstallmentPlan) {
      setModulePaymentMode('installment');
      hasInitializedModuleModeRef.current = true;
    }
  }, [isModuleBased, payTotalOnly, hasModuleInstallmentPlan]);

  const fetchDiscountQuote = async ({ codeToApply, skipEarlyBirdOverride = isEarlyBirdCleared }) => {
    if (!application?._id) return { ok: false, quote: null };

    if (paymentSkipped) {
      setDiscountQuote(null);
      setDiscountError('');
      return { ok: true, quote: null };
    }

    const allowed = hasScholarship || hasAnyDiscountAllowedForPaymentContext(
      subProduct?.discounts,
      paymentEligibilityContext,
      resolveDiscountEligibilitySource(application, subProduct)
    );
    if (!allowed) {
      setDiscountQuote(null);
      setDiscountError('');
      setAppliedDiscountCode(null);
      return { ok: true, quote: null };
    }

    if (!isModuleBased && paymentType === 'installment' && !selectedPlan?._id) {
      setDiscountQuote(null);
      setDiscountError('');
      return { ok: true, quote: null };
    }

    if (isModuleBased && selectedModules.length === 0) {
      setDiscountQuote(null);
      setDiscountError('');
      return { ok: true, quote: null };
    }

    setDiscountLoading(true);
    setDiscountError('');
    try {
      const response = await api.post(
        `/crm/applications/public/${application._id}/discount-quote`,
        {
          paymentType: isModuleBased ? null : paymentType,
          installmentPlanId: !isModuleBased && paymentType === 'installment' ? selectedPlan?._id : null,
          selectedModuleCodes: isModuleBased ? selectedModules : [],
          modulePaymentMode: isModuleBased ? modulePaymentMode : null,
          discountCode: codeToApply || null,
          skipEarlyBird: skipEarlyBirdOverride
        }
      );
      const quote = response?.data?.data || null;
      setDiscountQuote(quote);
      // A scholarship code awards a scholarship instead of a price discount
      if (codeToApply && quote?.isScholarship && quote?.scholarship) {
        setCodeScholarship({ ...quote.scholarship, isScholarship: true, source: 'discount_code' });
      } else if (codeToApply || !application?.scholarship?.isScholarship) {
        setCodeScholarship(null);
      }
      return { ok: true, quote };
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || 'Failed to apply discount';
      setDiscountQuote(null);
      setCodeScholarship(null);
      setDiscountError(message);

      // If a code failed, keep early bird detection by re-quoting without code
      if (codeToApply) {
        try {
          const fallback = await api.post(
            `/crm/applications/public/${application._id}/discount-quote`,
            {
              paymentType: isModuleBased ? null : paymentType,
              installmentPlanId: !isModuleBased && paymentType === 'installment' ? selectedPlan?._id : null,
              selectedModuleCodes: isModuleBased ? selectedModules : [],
              modulePaymentMode: isModuleBased ? modulePaymentMode : null,
              discountCode: null,
              skipEarlyBird: skipEarlyBirdOverride
            }
          );
          setDiscountQuote(fallback?.data?.data || null);
        } catch (_) {
          // ignore
        }
      }
      return { ok: false, quote: null };
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleApplyDiscountCode = async () => {
    const code = (discountCodeInput || '').trim().toUpperCase();
    if (!code) {
      toast.error('Please enter a discount code');
      return;
    }

    const result = await fetchDiscountQuote({ codeToApply: code });
    if (result.ok) {
      // If early bird applies, it will take precedence and we should clear the code.
      if (result.quote?.appliedDiscount?.kind === 'early_bird') {
        setAppliedDiscountCode(null);
        setDiscountCodeInput('');
        return;
      }

      setAppliedDiscountCode(code);
    } else {
      setAppliedDiscountCode(null);
    }
  };

  const handleClearDiscountCode = async () => {
    setDiscountCodeInput('');
    setAppliedDiscountCode(null);
    setDiscountError('');
    await fetchDiscountQuote({ codeToApply: null });
  };

  useEffect(() => {
    if (isEarlyBirdAvailable && appliedDiscountCode) {
      setAppliedDiscountCode(null);
      setDiscountCodeInput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEarlyBirdAvailable]);

  // Form data
  const [formData, setFormData] = useState({
    attendanceMode: 'Physical',
    personalInfo: {
      firstName: '',
      lastName: '',
      nameOnCertificate: '',
      dateOfBirth: '',
      gender: '',
      nationality: '',
      nationalId: '',
      passportNumber: '',
      licenseNumber: ''
    },
    contactInfo: {
      email: '',
      phone: '',
      alternatePhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: 'Sri Lanka',
        postalCode: ''
      }
    },
    permanentAddress: {
      street: '',
      city: '',
      state: '',
      country: 'Sri Lanka',
      postalCode: '',
      sameAsContact: false
    },
    educationHistory: [],
    workExperience: [],
    qualifications: [],
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    additionalInfo: {
      hearAboutUs: '',
      previouslyApplied: false,
      specialRequirements: '',
      goals: ''
    },
    prerequisitesConfirmed: []
  });

  useEffect(() => {
    fetchApplication();
  }, [id]);

  useEffect(() => {
    setDobNicMismatchWarning(getNicDerivedDobMismatchWarning(formData.personalInfo));
  }, [formData.personalInfo.dateOfBirth, formData.personalInfo.nationalId]);

  // Fetch active CRM tax configs (public endpoint, no auth required)
  useEffect(() => {
    taxConfigApi.getPublicTaxConfigs()
      .then((res) => {
        const configs = res?.data?.data || [];
        if (configs.length > 0) {
          setActiveTaxes(configs);
        }
      })
      .catch(() => {
        // Tax fetch failure is non-blocking
      });
  }, []);

  // Fetch enrollment terms text (public endpoint, no auth required)
  useEffect(() => {
    getCrmPublicSetting('enrollmentTermsAndConditions')
      .then((res) => {
        const text = res?.data?.data;
        if (text) setEnrollmentTermsText(text);
      })
      .catch(() => {
        // Non-blocking — default text shown if fetch fails
      });
  }, []);

  /**
   * Helper to load latest card payment summary for this application (public endpoint).
   * Used both after Paycorp redirect and on normal reloads.
   */
  const loadCardPaymentSummary = async () => {
    try {
      const { getPublicApplicationPaymentByApplication } = await import('../../../services/crmApi');
      const applicationLookupKey = application?._id || id;
      const response = await getPublicApplicationPaymentByApplication(applicationLookupKey);
      const paymentData = response.data?.data || response.data;
      if (!paymentData) return;
      setBackendPaymentSnapshot(paymentData);

      const backendLocked = Boolean(
        paymentData.paymentLocked ||
        paymentData.hasSuccessfulCardPayment ||
        paymentData.paymentLinkClosed
      );
      if (!backendLocked) {
        setPaymentLocked(false);
        setCardPaymentSummary(null);
        return;
      }

      const successfulCardPayment = paymentData.successfulCardPayment || null;
      const currency = successfulCardPayment?.currency || paymentData.amount?.currency || 'LKR';
      const amount = Number(
        successfulCardPayment?.amount ??
        paymentData.lastPaymentAmount ??
        paymentData.amount?.approvedAmount ??
        0
      );
      const paymentDate = successfulCardPayment?.paymentDate || null;
      const referenceNumber = successfulCardPayment?.referenceNumber || null;

      setCardPaymentInfo({
        status: 'success',
        amount,
        currency,
        date: paymentDate,
        referenceNumber
      });
      setPaymentLocked(true);
      setPaymentSkipped(false);

      const invoiceSummary = paymentData.invoiceSummary || {};
      const installments = Array.isArray(paymentData.installmentPlan?.installments)
        ? paymentData.installmentPlan.installments
        : [];
      const currentInstallment = installments.find((inst) => {
        const amt = Number(inst?.amount) || 0;
        const paid = Number(inst?.paidAmount) || 0;
        return amt > 0 && paid < amt;
      }) || installments[0] || null;
      const fallbackInstallmentAmount = Number(currentInstallment?.amount) || 0;
      const fallbackInstallmentPaid = Number(currentInstallment?.paidAmount) || 0;
      const backendInstallmentNumber = Number(paymentData.currentInstallmentNumber) || null;
      const backendInstallmentEffectiveAmount = Number(paymentData.currentInstallmentEffectiveAmount) || 0;
      const backendInstallmentRemaining = Number(paymentData.currentInstallmentRemainingBalance) || 0;
      const currentInstallmentAmount = backendInstallmentEffectiveAmount > 0
        ? backendInstallmentEffectiveAmount
        : fallbackInstallmentAmount;
      const currentInstallmentDue = backendInstallmentEffectiveAmount > 0
        ? backendInstallmentRemaining
        : Math.max(0, fallbackInstallmentAmount - fallbackInstallmentPaid);

      setCardPaymentSummary({
        currency: invoiceSummary.currency || currency,
        discountAmount: Number(invoiceSummary.discountAmount || paymentData.discount?.discountAmount || 0),
        subtotalAfterDiscount: Number(invoiceSummary.subtotalAfterDiscount || 0),
        inclusiveTaxAmount: Number(invoiceSummary.inclusiveTaxAmount || 0),
        exclusiveTaxAmount: Number(invoiceSummary.exclusiveTaxAmount || 0),
        courseBaseAmount: Number(
          paymentData.financialBreakdown?.courseBaseAmount ||
          paymentData.financialBreakdown?.configuredBaseAmount ||
          0
        ),
        courseNetAmount: Number(
          paymentData.financialBreakdown?.courseNetAmount ||
          0
        ),
        membershipFeeAmount: Number(paymentData.membershipFee?.amount || 0),
        membershipFeeIncluded: paymentData.membershipFee?.included === true,
        registrationFeeAmount: Number(paymentData.registrationFee?.amount || 0),
        registrationFeeIncluded: paymentData.registrationFee?.included === true,
        registrationFeeWaivedForInternal: paymentData.registrationFee?.waivedForInternalStudent === true,
        internationalPaymentFeeAmount: Number(
          paymentData.financialBreakdown?.internationalPaymentFeeAmount ||
          paymentData.internationalPaymentFee?.amount ||
          0
        ),
        totalInvoiceAmount: Number(invoiceSummary.totalInvoiceAmount || 0),
        currentInstallmentNumber: backendInstallmentNumber || currentInstallment?.installmentNumber || null,
        currentInstallmentAmount,
        currentInstallmentDue
      });

      // If the ApplicationPayment carries module selections, sync them into selectedModules
      if (Array.isArray(paymentData.moduleSelections) && paymentData.moduleSelections.length > 0) {
        const moduleCodes = paymentData.moduleSelections
          .map((m) => m.code)
          .filter(Boolean);
        if (moduleCodes.length > 0) {
          setSelectedModules(moduleCodes);
        }
      }

      // Force user to Payment step and lock to card view
      setCurrentStep(4);
      setPublicPaymentMethod('card');
      if (amount > 0) {
        setDesiredPaymentAmount(amount);
      }
    } catch (error) {
      // For public flow we silently ignore 404/401 etc.
      console.error('Failed to load public card payment summary for application:', error);
    }
  };

  // Handle Paycorp card payment return (cardPayment, reqid, responseCode)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const cardPaymentStatus = searchParams.get('cardPayment'); // 'success' | 'failed' | 'cancelled'
    const reqid = searchParams.get('reqid');
    const responseCode = searchParams.get('responseCode');
    const returnKey = `${cardPaymentStatus || ''}:${reqid || ''}:${responseCode || ''}`;
    if (cardPaymentStatus) {
      console.info('[PublicApplicationByNumber] Paycorp return detected', {
        appPath: location.pathname,
        returnUrl: `${location.pathname}${location.search}`,
        cardPaymentStatus,
        reqid,
        responseCode
      });
    }

    if (!cardPaymentStatus) {
      return;
    }

    // Prevent duplicate toasts for the same return in a single page lifecycle.
    if (lastHandledCardReturnRef.current === returnKey) {
      return;
    }
    lastHandledCardReturnRef.current = returnKey;

    // Always bring user back to payment step
    setCurrentStep(4);

    if (cardPaymentStatus === 'success') {
      toast.success('Card payment completed successfully.');
      setPaymentLocked(true);
      setPublicPaymentMethod('card');
      setCardPaymentInfo((prev) => (
        prev || {
          status: 'success',
          amount: Number(desiredPaymentAmount) || 0,
          currency: subProduct?.baseFee?.currency || 'LKR',
          date: new Date().toISOString(),
          referenceNumber: null
        }
      ));
      loadCardPaymentSummary();
    } else if (cardPaymentStatus === 'failed') {
      setPaymentLocked(false);
      setCardPaymentInfo(null);
      setCardPaymentSummary(null);
      setPublicPaymentMethod('bank');
      toast.error(
        `Card payment failed${responseCode ? ` (code: ${responseCode})` : ''}. Please try again or choose Bank Transfer.`
      );
    } else if (cardPaymentStatus === 'cancelled') {
      setPaymentLocked(false);
      setCardPaymentInfo(null);
      setCardPaymentSummary(null);
      setPublicPaymentMethod('bank');
      toast('Card payment was cancelled. You can retry or choose Bank Transfer.', {
        icon: '⚠️'
      });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net: if we already know a card payment completed but module selections
  // haven't been restored yet for a module-based program, reload the summary once.
  useEffect(() => {
    if (cardPaymentInfo && isModuleBased && selectedModules.length === 0) {
      loadCardPaymentSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardPaymentInfo, isModuleBased, selectedModules.length]);

  // On initial load or normal reload (no cardPayment param), check if a successful
  // card payment already exists and, if so, show the card payment summary.
  useEffect(() => {
    if (!cardPaymentInfo) {
      loadCardPaymentSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A scholarship code already saved on the application (sent by the sales team or entered before a card
  // payment) is shown again, together with its proof document.
  useEffect(() => {
    const saved = application?.scholarship;
    if (!saved?.isScholarship) return;
    if (saved.source === 'discount_code' && saved.discountCode && !appliedDiscountCode) {
      setDiscountCodeInput(saved.discountCode);
      setAppliedDiscountCode(saved.discountCode);
    }
    if (saved.proofDocument?.fileId && !scholarshipProof) {
      setScholarshipProof({
        fileId: saved.proofDocument.fileId,
        fileName: saved.proofDocument.fileName,
        displayName: saved.proofDocument.fileName,
        fileSize: saved.proofDocument.fileSize,
        mimeType: saved.proofDocument.mimeType
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?._id, application?.scholarship?.discountCode]);

  // Keep a live discount quote for eligible payment contexts
  useEffect(() => {
    if (!application?._id) return;

    const codeToApply = appliedDiscountCode || null;
    fetchDiscountQuote({ codeToApply });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    application?._id,
    paymentSkipped,
    isModuleBased,
    paymentType,
    selectedPlan?._id,
    modulePaymentMode,
    selectedModules.join(','),
    appliedDiscountCode
  ]);

  // Set desired payment amount to max amount when it changes
  useEffect(() => {
    const minAmount = getMinPaymentAmount();
    const maxAmount = getMaxPaymentAmount();

    // Keep desired amount inside the valid range. Prefer preserving a user-entered amount
    // when it is already valid.
    if (paymentSkipped) {
      setDesiredPaymentAmount(prev => {
        const next = 0;
        if (hasInitializedDesiredAmountRef.current) {
          notifyPaymentAmountAdjusted({ from: Number(prev) || 0, to: next, minAmount, maxAmount });
        }
        return next;
      });
      desiredAmountTouchedRef.current = false;
      lastAutoDesiredAmountRef.current = 0;
      hasInitializedDesiredAmountRef.current = true;
      return;
    }

    setDesiredPaymentAmount(prev => {
      const prevNumber = Number(prev) || 0;
      let next = prevNumber;

      // If we haven't recorded an "auto" value yet, capture the current value as the
      // last auto-initialized amount (only if the user hasn't touched the field).
      // This covers the case where an earlier effect set an initial value before this
      // effect runs (e.g., membership fee only before modules are selected).
      if (!desiredAmountTouchedRef.current && lastAutoDesiredAmountRef.current == null) {
        lastAutoDesiredAmountRef.current = prevNumber;
      }

      if (maxAmount > 0) {
        // Module-based "Pay total" / "Installments" OR product-based "Pay full amount"
        // OR installment plan selected: always reset to the tax-inclusive payable amount
        // when the relevant deps change.
        if (
          (isModuleBased && modulePaymentMode === 'total' && selectedModules.length > 0) ||
          (isModuleBased && modulePaymentMode === 'installment' && selectedModules.length > 0) ||
          (!isModuleBased && paymentType === 'full' && ((subProduct?.baseFee?.amount || 0) > 0 || (mainProduct?.fee?.amount || 0) > 0)) ||
          (!isModuleBased && paymentType === 'installment' && selectedPlan)
        ) {
          next = maxAmount + getCurrentPaymentTaxAmount();
          lastAutoDesiredAmountRef.current = next;
        } else if (!prevNumber) {
          // Unset – initialise to the tax-inclusive max.
          next = maxAmount + getCurrentPaymentTaxAmount();
          if (!desiredAmountTouchedRef.current) {
            lastAutoDesiredAmountRef.current = next;
          }
        } else {
          // All other modes: clamp within [min, taxInclusiveMax], respecting user input.
          const taxInclusiveMax = maxAmount + getCurrentPaymentTaxAmount();
          next = Math.min(Math.max(minAmount, prevNumber), taxInclusiveMax);
        }
      } else {
        next = 0;
      }

      if (hasInitializedDesiredAmountRef.current) {
        notifyPaymentAmountAdjusted({ from: prevNumber, to: next, minAmount, maxAmount });
      }
      return next;
    });

    hasInitializedDesiredAmountRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModuleBased, modulePaymentMode, selectedModules.length, selectedModules.join(','), paymentType, selectedPlan?._id, subProduct?.baseFee?.amount, mainProduct?.fee?.amount, subProduct?.processingFee?.amount, paymentSkipped, discountQuote?.finalAmount, discountQuote?.baseAmount, discountQuote?.appliedDiscount, taxInfo?.totalTaxAmount, taxInfo?.totalInvoiceAmount]);





  // Render Turnstile widget when on final step
  useEffect(() => {
    // Disable Turnstile for localhost
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.startsWith('192.168.');
    
    if (isLocalhost && currentStep === totalSteps) {
      console.log('Localhost detected - skipping Turnstile verification');
      setTurnstileToken('localhost-bypass');
      return;
    }
    
    let retryCount = 0;
    const maxRetries = 10;
    
    const renderTurnstile = () => {
      if (currentStep === totalSteps && turnstileRef.current) {
        try {
          // Clear any existing widget
          if (turnstileWidgetId !== null && window.turnstile) {
            try {
              window.turnstile.remove(turnstileWidgetId);
            } catch (e) {
              console.warn('Error removing existing Turnstile widget:', e);
            }
            setTurnstileWidgetId(null);
          }

          // Check if Turnstile is available and properly loaded
          if (window.turnstile && typeof window.turnstile.render === 'function' && window.turnstileLoaded) {
            // Render new widget
            const widgetId = window.turnstile.render(turnstileRef.current, {
              sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACBFyD5Nvhv8gW0_',
              callback: (token) => {
                setTurnstileToken(token);
                setTurnstileExpired(false);
                console.log('Turnstile verification successful');
              },
              'expired-callback': () => {
                setTurnstileToken(null);
                setTurnstileExpired(true);
                toast.error('Security verification expired. Please complete the verification again.');
              },
              'error-callback': (error) => {
                console.error('Turnstile error:', error);
                setTurnstileToken(null);
                toast.error('Security verification failed. Please try again.');
              },
              theme: process.env.REACT_APP_TURNSTILE_THEME || 'light'
            });
            setTurnstileWidgetId(widgetId);
            console.log('Turnstile widget rendered successfully');
          } else if (retryCount < maxRetries) {
            // Retry after a short delay if Turnstile not ready
            retryCount++;
            console.log(`Turnstile not ready, retrying (${retryCount}/${maxRetries})...`);
            setTimeout(renderTurnstile, 1000);
          } else {
            console.error('Turnstile failed to load after maximum retries');
            // Allow submission without Turnstile as fallback
            setTurnstileToken('fallback');
          }
        } catch (error) {
          console.error('Turnstile render error:', error);
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(renderTurnstile, 1000);
          }
        }
      }
    };

    if (currentStep === totalSteps) {
      // Wait a bit for the script to load
      setTimeout(renderTurnstile, 100);
    }

    return () => {
      // Cleanup on unmount
      if (turnstileWidgetId !== null && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (e) {
          console.warn('Error removing Turnstile widget on cleanup:', e);
        }
      }
    };
  }, [currentStep]);

  const formatDateForInput = (dateString) => toCalendarDateString(dateString);



  const fetchApplication = async () => {
    try {
      setLoading(true);
      setPaymentLocked(false);
      setCardPaymentInfo(null);
      setCardPaymentSummary(null);
      const response = await getApplicationByNumber(id);
      const appData = response.data.data || response.data;
      
      setApplication(appData);
      if (appData?.allowedPaymentOption === 'installment_only') {
        setPaymentType('installment');
      } else if (appData?.allowedPaymentOption === 'full_only') {
        setPaymentType('full');
        setSelectedPlan(null);
      }
      setBaselineEmail(
        (appData.contactInfoBaseline?.email || appData.contactInfo?.email || '').trim().toLowerCase()
      );
      setCardTermsAgreed(Boolean(appData?.agreementAudit?.current?.cardPayment?.accepted));
      setFinalTermsAgreed(Boolean(appData?.agreementAudit?.current?.submission?.accepted));
      // Card selection is disabled on the public form; keep bank unless a completed
      // card payment is restored later via loadCardPaymentSummary / Paycorp return.
      if (appData?.paymentInfo?.publicPaymentMethod === 'bank') {
        setPublicPaymentMethod('bank');
      } else if (appData?.paymentInfo?.publicPaymentMethod === 'card') {
        setPublicPaymentMethod('bank');
      }
      
      // Use sub-product data from application response (no separate API call needed)
      if (appData.subProductId && typeof appData.subProductId === 'object') {
        const subProductData = appData.subProductId;
        setSubProduct(subProductData);

        const categories = Array.isArray(subProductData.requirementCategories)
          ? subProductData.requirementCategories
          : [];

        if (categories.length > 0) {
          const existing = appData.requirementsCategoryId;
          const existsInList =
            existing &&
            categories.some((c) => String(c?._id) === String(existing));
          // Restore saved category when valid; otherwise leave empty so user must select
          setRequirementsCategoryId(existsInList ? String(existing) : '');
        } else if (appData.requirementsCategoryId) {
          setRequirementsCategoryId(String(appData.requirementsCategoryId));
        } else {
          setRequirementsCategoryId('');
        }
        
        // Initialize prerequisite checks
        if (subProductData.requirements?.prerequisites) {
          const initialChecks = {};
          subProductData.requirements.prerequisites.forEach((prereq, index) => {
            initialChecks[index] = false;
          });
          setPrerequisiteChecks(initialChecks);
        }
        
        // Initialize screening question answers
        if (subProductData.screeningQuestions) {
          const initialAnswers = {};
          subProductData.screeningQuestions.forEach((question, index) => {
            initialAnswers[index] = '';
          });

          // Hydrate from previously saved customFields (if any)
          const existingCustomFields = Array.isArray(appData.customFields) ? appData.customFields : [];
          subProductData.screeningQuestions.forEach((question, index) => {
            const questionId = question?._id ? String(question._id) : null;
            const matchById = questionId
              ? existingCustomFields.find(
                  (f) => String(f?.fieldName || '') === `screening_question_${questionId}`
                )
              : null;
            const matchByLabel = existingCustomFields.find(
              (f) => String(f?.fieldLabel || '') === String(question?.question || '')
            );
            const match = matchById || matchByLabel;

            if (match && match.fieldValue !== undefined && match.fieldValue !== null) {
              initialAnswers[index] = match.fieldValue;
            }
          });

          setScreeningAnswers(initialAnswers);
        }
      }

      // Set main product data
      if (appData.mainProductId && typeof appData.mainProductId === 'object') {
        setMainProduct(appData.mainProductId);
        
        // Initialize selected modules from existing application data if available
        if (appData.moduleSelections && Array.isArray(appData.moduleSelections)) {
          setSelectedModules(appData.moduleSelections.map(m => m.code || m.moduleCode || m._id));
        }
      } else {
        console.warn('Main Product not found or not populated:', appData.mainProductId);
      }

      // Initialize module exemptions from saved application data
      if (Array.isArray(appData.moduleExemptions) && appData.moduleExemptions.length > 0) {
        setModuleExemptions(appData.moduleExemptions);

        // Auto-restore selectedModules for approved exemptions (they must be included in the total)
        if (appData.moduleSelections && Array.isArray(appData.moduleSelections)) {
          const savedCodes = appData.moduleSelections.map(m => m.code || m.moduleCode || m._id);
          const approvedCodes = appData.moduleExemptions
            .filter((e) => e.status === 'approved')
            .map((e) => e.moduleCode);
          const pendingCodes = appData.moduleExemptions
            .filter((e) => e.status === 'pending')
            .map((e) => e.moduleCode);
          // Ensure approved codes are in selected; ensure pending codes are NOT in selected
          const merged = [...new Set([...savedCodes.filter((c) => !pendingCodes.includes(c)), ...approvedCodes])];
          setSelectedModules(merged);
        }
      }
      
      // Check if application is already submitted / no longer editable from public form
      if (appData.status && isPublicFormLocked(appData.status)) {
        setSubmitted(true);
      }
      
      // Pre-populate form with existing data
      if (appData.personalInfo || appData.contactInfo || appData.educationHistory) {
        setFormData(prevData => {
          const mergedPersonalInfo = applyNicDerivedPersonalInfo(
            { ...prevData.personalInfo, ...appData.personalInfo },
            { overwriteFromNic: true }
          );

          // If certificate name is not explicitly set, default to "FirstName LastName"
          if (!mergedPersonalInfo.nameOnCertificate && (mergedPersonalInfo.firstName || mergedPersonalInfo.lastName)) {
            mergedPersonalInfo.nameOnCertificate = [
              mergedPersonalInfo.firstName,
              mergedPersonalInfo.lastName
            ].filter(Boolean).join(' ');
          }

          const mergedContactInfo = {
            ...prevData.contactInfo,
            ...appData.contactInfo,
            address: {
              ...prevData.contactInfo.address,
              ...(appData.contactInfo?.address || {}),
            }
          };
          if (!mergedContactInfo.address.country || !String(mergedContactInfo.address.country).trim()) {
            mergedContactInfo.address.country = 'Sri Lanka';
          }

          const mergedPermanentAddress = {
            ...prevData.permanentAddress,
            ...appData.permanentAddress,
          };
          if (!mergedPermanentAddress.country || !String(mergedPermanentAddress.country).trim()) {
            mergedPermanentAddress.country = 'Sri Lanka';
          }

          return {
            ...prevData,
            attendanceMode: appData.attendanceMode || prevData.attendanceMode || 'Physical',
            personalInfo: mergedPersonalInfo,
            contactInfo: mergedContactInfo,
            permanentAddress: mergedPermanentAddress,
            educationHistory: appData.educationHistory || [],
            workExperience: appData.workExperience || [],
            qualifications: appData.qualifications || [],
            emergencyContact: { ...prevData.emergencyContact, ...appData.emergencyContact },
            additionalInfo: { ...prevData.additionalInfo, ...appData.additionalInfo }
          };
        });
      }

      
    } catch (error) {
      console.error('Error fetching application:', error);
      if (error.response?.status === 410 && error.response?.data?.code === 'APPLICATION_CLOSED_DIRECT_ENROLLMENT') {
        setEnrollmentCompletedMessage(error.response?.data?.message || 'Your enrollment has already been completed.');
      } else if (error.response?.status === 403) {
        setRegistrationClosedMessage(error.response?.data?.message || 'Registration is closed.');
      } else if (error.response?.status === 404) {
        toast.error('Application not found. Please check the application number.');
      } else {
        toast.error('Failed to load application. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModuleBased) return;
    if (application?.allowedPaymentOption === 'installment_only') {
      setPaymentType('installment');
      if (!selectedPlan && Array.isArray(subProduct?.installmentPlans) && subProduct.installmentPlans.length === 1) {
        setSelectedPlan(subProduct.installmentPlans[0]);
      }
    } else if (application?.allowedPaymentOption === 'full_only') {
      setPaymentType('full');
      setSelectedPlan(null);
    }
  }, [application?.allowedPaymentOption, subProduct?.installmentPlans, isModuleBased]);

  /**
   * Persist a lightweight draft of the public application to the backend so that
   * key details (personal info, contact info, etc.) survive a browser reload.
   * This is only called while the application is still in Draft state.
   */
  const saveDraftToServer = async (stepForSave, options = {}) => {
    const { throwOnError = false } = options;

    try {
      // Require loaded application with an ID
      if (!application?._id) return;

      const updatePayload = {};

      // Always persist personal + contact information once user has worked on step 1 or 2
      if (stepForSave >= 1) {
        updatePayload.attendanceMode = formData.attendanceMode || 'Physical';
        updatePayload.personalInfo = applyNicDerivedPersonalInfo(formData.personalInfo);
        setFormData((prev) => ({
          ...prev,
          personalInfo: updatePayload.personalInfo
        }));
      }
      if (stepForSave >= 2) {
        updatePayload.contactInfo = formData.contactInfo;
        updatePayload.permanentAddress = formData.permanentAddress;
      }

      // Persist application screening answers so they survive page reloads/payment redirects
      if (stepForSave >= 3) {
        if (requirementsCategoryId) {
          updatePayload.requirementsCategoryId = requirementsCategoryId;
        }

        const screeningQuestions = Array.isArray(subProduct?.screeningQuestions)
          ? subProduct.screeningQuestions
          : [];

        const visibleScreening = screeningQuestions
          .map((q, originalIndex) => ({ question: q, originalIndex }))
          .filter(({ question }) => {
            if (!question?.categoryId) return true;
            return requirementsCategoryId && String(question.categoryId) === String(requirementsCategoryId);
          });

        const screeningCustomFields = visibleScreening.map(({ question, originalIndex }) => {
          const stableKey = question?._id ? String(question._id) : String(originalIndex);
          return {
            fieldName: `screening_question_${stableKey}`,
            fieldLabel: question?.question,
            fieldValue: screeningAnswers[originalIndex] ?? '',
            fieldType:
              question?.questionType === 'multiple_choice'
                ? 'multiselect'
                : question?.questionType === 'textarea'
                  ? 'textarea'
                  : question?.questionType === 'yes_no'
                    ? 'boolean'
                    : 'text'
          };
        });

        const existingCustomFields = Array.isArray(application?.customFields) ? application.customFields : [];
        const nonScreeningCustomFields = existingCustomFields.filter(
          (f) => !String(f?.fieldName || '').startsWith('screening_question_')
        );

        updatePayload.customFields = [...nonScreeningCustomFields, ...screeningCustomFields];
      }

      // Persist module selections so they survive page reloads/payment redirects.
      // This is especially important for Paycorp card payments which redirect away from the page.
      if (stepForSave >= 4 && isModuleBased && !modulesLocked) {
        updatePayload.moduleSelections = Array.isArray(availableModules)
          ? availableModules
              .filter((module) => selectedModules.includes(module.code))
              .map((module) => ({
                code: module.code,
                name: module.name,
                credits: module.credits,
                moduleFee: module.moduleFee,
                examination: module.examination
              }))
          : [];
      }

      // Only send update when we actually have something to persist
      if (Object.keys(updatePayload).length === 0) return;

      await updateApplicationByNumber(application._id, updatePayload);
    } catch (error) {
      console.error('Public application draft save failed:', error);

      if (throwOnError) {
        throw error;
      }
    }
  };

  const applyDraftValidationErrors = (error) => {
    const serverErrors = error?.response?.data?.errors || {};
    const nextErrors = {};

    if (serverErrors.nationalId || serverErrors.nic) {
      nextErrors.nationalId = serverErrors.nationalId || serverErrors.nic;
    }
    if (serverErrors.email) {
      nextErrors.email = serverErrors.email;
    }
    if (serverErrors.phone) {
      nextErrors.phone = serverErrors.phone;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
    }

    const message = error?.response?.data?.message || 'Failed to save application draft';
    toast.error(message);
  };

  // Turnstile callbacks
  const onTurnstileSuccess = (token) => {
    setTurnstileToken(token);
    setTurnstileExpired(false);
  };

  const onTurnstileExpired = () => {
    setTurnstileToken(null);
    setTurnstileExpired(true);
    toast.error('Security verification expired. Please complete the verification again.');
  };

  const onTurnstileError = () => {
    setTurnstileToken(null);
    toast.error('Security verification failed. Please try again.');
  };

  const handleInputChange = (section, field, value) => {
    const normalizedValue = section === 'contactInfo' && field === 'email'
      ? String(value || '').trim().toLowerCase()
      : value;

    setFormData(prev => {
      const next = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: normalizedValue
        }
      };

      if (section === 'personalInfo' && field === 'nationalId') {
        const normalizedNic = normalizeSriLankanNic(value);
        const limitedNic = normalizedNic.length > 12 ? normalizedNic.slice(0, 12) : normalizedNic;
        next.personalInfo = applyNicDerivedPersonalInfo(
          { ...next.personalInfo, nationalId: limitedNic },
          { overwriteFromNic: true }
        );
      }

      return next;
    });

    if (section === 'personalInfo') {
      if (field === 'nationalId' || field === 'passportNumber' || field === 'licenseNumber') {
        setErrors((prev) => {
          if (!prev.nationalId && !prev.nic && !prev.dateOfBirth && !prev.gender) {
            return prev;
          }

          const next = { ...prev };
          delete next.nationalId;
          delete next.nic;
          if (field === 'nationalId') {
            delete next.dateOfBirth;
            delete next.gender;
          }
          return next;
        });
      }
    }
  };

  const handleNestedInputChange = (section, nestedSection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nestedSection]: {
          ...prev[section][nestedSection],
          [field]: value
        }
      }
    }));
  };

  const handleArrayInputChange = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (section, newItem) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
  };

  const removeArrayItem = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  // Handle document changes from DocumentUpload component
  const handleDocumentsChange = (updatedDocuments) => {
    setFormData(prev => ({
      ...prev,
      documents: updatedDocuments
    }));
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingFiles(true);
    const uploadedDocs = [];
    
    try {
      for (const file of files) {
        const metadata = {
          category: 'Other', // Use 'Other' to match Application model enum
          title: file.name,
          description: 'Application supporting document'
        };
        
        // Use public upload endpoint (no auth required)
        const response = await fileApi.uploadDocumentToApplication(application._id, file, metadata);
        const uploadedFile = response.data?.data || response.data;
        
        console.log('Uploaded file response:', uploadedFile); // Debug log
        
        uploadedDocs.push({
          fileId: uploadedFile.fileId, // Use the UUID fileId, not MongoDB _id
          fileName: uploadedFile.fileName || file.name,
          originalName: uploadedFile.originalName || file.name,
          fileSize: uploadedFile.fileSize || file.size,
          fileType: uploadedFile.mimeType || uploadedFile.fileType || file.type,
          title: uploadedFile.fileName || file.name,
          category: 'Other', // Match Application model enum
          uploadedAt: new Date(),
          _id: uploadedFile._id || uploadedFile.id,
          id: uploadedFile._id || uploadedFile.id
        });
        
        console.log('Document to save:', uploadedDocs[uploadedDocs.length - 1]); // Debug log
      }
      
      setFormData(prev => ({
        ...prev,
        documents: [...(prev.documents || []), ...uploadedDocs]
      }));
      
      toast.success(`${uploadedDocs.length} document(s) uploaded successfully`);
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload files');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Check if localhost
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.startsWith('192.168.');

      // Backend requires turnstileToken for public submit.
      // In localhost/dev we use a bypass token recognized by the CRM service.
      const turnstileTokenToSend = isLocalhost
        ? (turnstileToken || 'localhost-bypass')
        : turnstileToken;
      
      // Verify Turnstile token before submission (skip for localhost)
      if (!isLocalhost && !turnstileTokenToSend) {
        toast.error('Please complete the security verification before submitting.');
        setSubmitting(false);
        return;
      }

      if (!isSubmissionAgreementSatisfied) {
        toast.error('Please agree to the terms and conditions before submitting.');
        setSubmitting(false);
        return;
      }

      const screeningQuestions = Array.isArray(subProduct?.screeningQuestions) ? subProduct.screeningQuestions : [];
      const visibleScreening = screeningQuestions
        .map((q, originalIndex) => ({ question: q, originalIndex }))
        .filter(({ question }) => {
          if (!question?.categoryId) return true;
          return requirementsCategoryId && String(question.categoryId) === String(requirementsCategoryId);
        });
      
      // Prepare form data
      const submissionData = {
        ...formData,
        requirementsCategoryId: requirementsCategoryId || null,
        // Add screening question answers as custom fields
        customFields: subProduct?.screeningQuestions
          ? (() => {
              return visibleScreening.map(({ question, originalIndex }, index) => ({
                fieldName: `screening_question_${String(question?._id || originalIndex)}`,
                fieldLabel: question.question,
                fieldValue: screeningAnswers[originalIndex] || '',
                fieldType: question.questionType === 'multiple_choice' ? 'multiselect' : 
                           question.questionType === 'textarea' ? 'textarea' : 
                           question.questionType === 'yes_no' ? 'boolean' : 'text'
              }));
            })()
          : [],

        screeningResponses: visibleScreening.map(({ question, originalIndex }) => ({
          questionId: question?._id,
          questionType: question?.questionType,
          categoryId: question?.categoryId,
          answer: screeningAnswers[originalIndex] ?? ''
        })),
        // Add prerequisite confirmations
        prerequisitesConfirmed: subProduct?.requirements?.prerequisites
          ? subProduct.requirements.prerequisites.map((prereq, index) => ({
              title: prereq.title,
              documentType: prereq.documentType,
              confirmed: prerequisiteChecks[index] || false,
              confirmedAt: prerequisiteChecks[index] ? new Date() : null
            }))
          : [],
        // Add payment verification status - only if not skipped
        paymentVerified: (paymentSkipped || isFullScholarship) ? false : paymentVerified,
        processingFeeAmount: (paymentSkipped || isFullScholarship) ? 0 : membershipFeeAmountBase,
        processingFeeCurrency: (paymentSkipped || isFullScholarship) ? null : (membershipFeeAmountBase > 0 ? membershipFeeCurrency : null),
        // Add payment preference - only if not skipped
        paymentType: (paymentSkipped || isFullScholarship) ? null : paymentType,
        selectedInstallmentPlanId: (paymentSkipped || isFullScholarship || paymentType !== 'installment') ? null : (selectedPlan ? selectedPlan._id : null),
        paymentSkipped: paymentSkipped,
        // Add desired payment amount
        desiredPaymentAmount: (paymentSkipped || isFullScholarship) ? 0 : desiredPaymentAmount,
        // Add module selections for module-based products
        moduleSelections: isModuleBased ? availableModules
          .filter(module => selectedModules.includes(module.code))
          .map(module => {
            const exemption = getModuleExemption(module.code);
            const isApproved = exemption?.status === 'approved';
            return {
              code: module.code,
              name: module.name,
              credits: module.credits,
              moduleFee: isApproved ? (Number(exemption.exemptionFee) || 0) : module.moduleFee,
              examination: module.examination,
              // Carry exemption info so downstream records show the correct fee origin
              exemptionApplied: isApproved || undefined,
              originalModuleFee: isApproved ? module.moduleFee : undefined
            };
          }) : null
      };
      
      // Submit application for review (with all form data and turnstileToken for verification)
      const installmentPlanIdToSend = (paymentSkipped || paymentType !== 'installment') ? null : (selectedPlan ? selectedPlan._id : null);
      
      // Submit application with all form data in one call (no draft save)
      const submitResponse = await submitApplicationByNumber(application._id, { 
        ...submissionData, // Include all form data
        turnstileToken: turnstileTokenToSend,
        installmentPlanId: installmentPlanIdToSend,
        paymentType: paymentSkipped ? null : paymentType, // Don't send payment type if skipped
        paymentSkipped: paymentSkipped,
        publicPaymentMethod: paymentSkipped ? null : publicPaymentMethod,
        submissionAgreement: shouldUseStoredCardAgreementForSubmit
          ? null
          : {
              agreed: finalTermsAgreed,
              agreedAtClient: finalTermsAgreed ? new Date().toISOString() : null,
              source: 'public_application_step_5_submission'
            },
        modulePaymentMode: isModuleBased ? modulePaymentMode : null,
        taxSnapshots: paymentSkipped ? [] : getCurrentTaxSnapshots(),
        discountCode: paymentSkipped
          ? null
          : (isEarlyBirdApplied ? null : (appliedDiscountCode || null)),
        scholarshipProof: hasScholarship && scholarshipProof?.fileId
          ? {
              fileId: scholarshipProof.fileId,
              fileName: scholarshipProof.fileName,
              fileSize: scholarshipProof.fileSize,
              mimeType: scholarshipProof.mimeType
            }
          : undefined
      });
      
      // Update application with submitted status
      if (submitResponse.data.success) {
        setApplication(submitResponse.data.data);
        setSubmitted(true);
        toast.success('Application submitted successfully! A copy of your application details will be emailed to you.');
      }
      
    } catch (error) {
      console.error('Submit application error:', error);

      const errMsg = error.response?.data?.message || '';
      // If the application was already submitted (email timeout caused a 500 on first attempt
      // but the DB record was saved), treat it as success rather than an error.
      if (errMsg.includes('can only be submitted from Draft')) {
        toast.success('Your application has already been submitted successfully!');
        window.location.reload();
        return;
      }

      toast.error(errMsg || 'Failed to submit application. Please try again.');
      
      // Reset Turnstile on submission failure
      setTurnstileToken(null);
      setTurnstileExpired(true);
      if (window.turnstile && turnstileWidgetId !== null) {
        try {
          window.turnstile.reset(turnstileWidgetId);
        } catch (e) {
          console.warn('Error resetting Turnstile widget:', e);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardPayment = async () => {
    try {
      if (paymentLocked || cardPaymentInfo) {
        toast.error('Payment is locked because a successful card payment already exists.');
        return;
      }

      // Ensure we are on the payment step
      if (currentStep !== 4) {
        setCurrentStep(4);
      }

      if (isModuleBased && hasPendingExemptions) {
        setErrors({
          modules: 'One or more modules are awaiting exemption review. Please wait for the review to complete before proceeding.'
        });
        toast.error('Please wait for pending module exemption reviews to be resolved before paying by card.');
        return;
      }

      // Validate payment step inputs (modules, desired amount, etc.)
      const isValid = validateStep({ skipPaymentProof: true });
      if (!isValid) {
        return;
      }

      if (!application?._id) {
        toast.error('Application is not loaded. Please refresh and try again.');
        return;
      }

      if (!desiredPaymentAmount || desiredPaymentAmount <= 0) {
        toast.error('Please enter a valid payment amount before paying by card.');
        return;
      }

      if (!cardTermsAgreed && !hasStoredCardAgreement) {
        toast.error('Please agree to the terms and conditions before paying by card.');
        return;
      }

      setCardProcessing(true);

      // Save draft so eligibility/screening answers survive the payment redirect
      await saveDraftToServer(4);
      // If user had previously marked payment as skipped, clear that when starting card payment
      if (paymentSkipped) {
        setPaymentSkipped(false);
      }

      const payload = {
        paymentType: isModuleBased ? null : paymentType,
        installmentPlanId:
          !isModuleBased && paymentType === 'installment' && selectedPlan?._id
            ? selectedPlan._id
            : null,
        selectedModuleCodes: isModuleBased ? selectedModules : [],
        modulePaymentMode: isModuleBased ? modulePaymentMode : null,
        desiredPaymentAmount,
        taxSnapshots: getCurrentTaxSnapshots(),
        cardAgreement: {
          agreed: true,
          agreedAtClient: new Date().toISOString(),
          source: 'public_application_step_4_card_payment'
        },
        discountCode:
          (isEarlyBirdApplied ? null : (appliedDiscountCode || null)),
        scholarshipProof: hasScholarship && scholarshipProof?.fileId
          ? {
              fileId: scholarshipProof.fileId,
              fileName: scholarshipProof.fileName,
              fileSize: scholarshipProof.fileSize,
              mimeType: scholarshipProof.mimeType
            }
          : undefined
      };

      // Step 1: Ensure ApplicationPayment + public payment link exist for this application
      const response = await api.post(
        `/crm/applications/public/${application._id}/initiate-card-payment-link`,
        payload
      );

      const data = response?.data?.data || {};
      const token = data.token;

      if (!token) {
        toast.error('Payment link token is not available right now. Please try again later.');
        return;
      }

      // Step 2: Initiate Paycorp card payment directly using the ApplicationPayment public token
      const cardInitResponse = await api.post(
        `/crm/application-payments/public/${token}/initiate-card`,
        {
          actualAmount: Number(desiredPaymentAmount),
          paymentMethod: 'Card',
          paymentDate: new Date().toISOString().split('T')[0],
          notes: 'Card payment initiated from public application form',
          paymentAgreement: {
            agreed: true,
            agreedAtClient: hasStoredCardAgreement
              ? (storedCardAgreementAudit?.acceptedAt || new Date().toISOString())
              : new Date().toISOString(),
            source: 'public_application_step_4_card_payment'
          }
        }
      );

      const cardData = cardInitResponse?.data?.data || {};
      const paymentPageUrl = cardData.paymentPageUrl || cardData.paymentUrl;

      if (!paymentPageUrl) {
        toast.error('Card payment page is not available right now. Please try again later.');
        return;
      }

      toast.success('Redirecting to secure card payment page...');
      window.location.href = paymentPageUrl;
    } catch (error) {
      console.error('Card payment init error (public application):', error);
      const message =
        error.response?.data?.message ||
        error.message ||
        'Unable to start card payment right now. Please try again later.';
      toast.error(message);
    } finally {
      setCardProcessing(false);
    }
  };

  const validateStep = (options = {}) => {
    const newErrors = {};
    const { skipPaymentProof = false } = options;

    const isEligibilityGate = (question) => {
      if (!question) return false;
      if (question?.eligibility?.isEligibilityGate === true) return true;
      // Backward compatible default (existing behavior)
      return question.questionType === 'yes_no' && question.isRequired && question?.eligibility?.isEligibilityGate !== false;
    };

    const normalizeEligibleOptions = (question) => {
      const raw = question?.eligibility?.eligibleOptions;
      const list = Array.isArray(raw) ? raw : [];
      return list.map((o) => String(o ?? '').trim()).filter(Boolean);
    };
    
    switch (currentStep) {
      case 1: { // Personal Information
        const personalInfo = applyNicDerivedPersonalInfo(formData.personalInfo);
        if (
          personalInfo.dateOfBirth !== formData.personalInfo.dateOfBirth
          || personalInfo.gender !== formData.personalInfo.gender
          || personalInfo.nationalId !== formData.personalInfo.nationalId
        ) {
          setFormData((prev) => ({
            ...prev,
            personalInfo
          }));
        }

        if (!personalInfo.firstName?.trim()) {
          newErrors.firstName = 'First name is required';
        }
        if (!personalInfo.nameOnCertificate?.trim()) {
          newErrors.nameOnCertificate = 'Name to appear on certificate is required';
        }
        if (!personalInfo.dateOfBirth) {
          newErrors.dateOfBirth = 'Date of birth is required';
        } else if (!isApplicantDateOfBirthAllowed(personalInfo.dateOfBirth, maxApplicantDateOfBirth)) {
          newErrors.dateOfBirth = getApplicantDateOfBirthError(maxApplicantDateOfBirth);
        }
        if (!personalInfo.gender) {
          newErrors.gender = 'Gender is required';
        }
        if (!personalInfo.nationality?.trim()) {
          newErrors.nationality = 'Nationality is required';
        }
        const hasPassport = Boolean(personalInfo.passportNumber?.trim());
        const hasLicense = Boolean(personalInfo.licenseNumber?.trim());
        const hasNic = Boolean(personalInfo.nationalId?.trim());

        if (!hasNic) {
          if (!hasPassport && !hasLicense) {
            newErrors.nationalId = 'NIC is required';
          }
        } else if (!isValidSriLankanNic(personalInfo.nationalId)) {
          newErrors.nationalId = 'Invalid NIC format (e.g., 123456789V or 200012345678)';
        }
        break;
      }
      case 2: // Contact Information
        if (!formData.contactInfo.email?.trim()) {
          newErrors.email = 'Email is required';
        } else {
          // Basic email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.contactInfo.email)) {
            newErrors.email = 'Please enter a valid email address';
          }
        }
        if (!formData.contactInfo.phone?.trim()) {
          newErrors.phone = 'Phone number is required';
        }
        if (!formData.contactInfo.address.street?.trim()) {
          newErrors.street = 'Street address is required';
        }
        if (!formData.contactInfo.address.city?.trim()) {
          newErrors.city = 'City is required';
        }
        if (!formData.contactInfo.address.country?.trim()) {
          newErrors.country = 'Country is required';
        }
        break;
      case 3: // Application Questions
        if (requirementCategoriesList.length > 0) {
          if (!requirementsCategoryId) {
            newErrors.requirementsCategoryId = 'Please select a Requirement Category';
          }
        }

        // Validate required screening questions
        if (subProduct?.screeningQuestions && subProduct.screeningQuestions.length > 0) {
          subProduct.screeningQuestions.forEach((question, index) => {
            if (question?.categoryId) {
              if (!requirementsCategoryId || String(question.categoryId) !== String(requirementsCategoryId)) {
                return;
              }
            }
            if (question.isRequired) {
              const answer = screeningAnswers[index];
              if (!answer || (Array.isArray(answer) && answer.length === 0) || answer.toString().trim() === '') {
                newErrors[`screening_${index}`] = 'This question is required';
              } else if (isEligibilityGate(question)) {
                if (question.questionType === 'yes_no') {
                  const eligibleYesNo = question?.eligibility?.eligibleYesNo === 'No' ? 'No' : 'Yes';
                  if (answer !== eligibleYesNo) {
                    newErrors[`screening_${index}`] = 'You are not eligible for this program based on your answer';
                  }
                } else if (question.questionType === 'single_choice') {
                  const eligibleOptions = normalizeEligibleOptions(question);
                  if (eligibleOptions.length > 0 && !eligibleOptions.includes(String(answer))) {
                    newErrors[`screening_${index}`] = 'You are not eligible for this program based on your answer';
                  }
                } else if (question.questionType === 'multiple_choice') {
                  const eligibleOptions = normalizeEligibleOptions(question);
                  if (eligibleOptions.length > 0) {
                    const selected = Array.isArray(answer) ? answer : [answer];
                    const normalizedSelected = selected.map((a) => String(a ?? '').trim()).filter(Boolean);
                    const hasAny = normalizedSelected.some((a) => eligibleOptions.includes(a));
                    if (!hasAny) {
                      newErrors[`screening_${index}`] = 'You are not eligible for this program based on your answer';
                    }
                  }
                }
              }
            }
          });
        }
        break;
      case 4: // Payment
        // Block progression when there are unresolved (pending) module exemption requests
        if (isModuleBased && hasPendingExemptions) {
          newErrors.modules = 'One or more modules are awaiting exemption review. You cannot proceed until all exemption requests are resolved.';
        }
        // For module-based products, at least one module must be selected (only if payment is not skipped
        // and no card payment has already been completed)
        if (isModuleBased && !paymentSkipped && !cardPaymentInfo) {
          if (selectedModules.length === 0 && !hasPendingExemptions) {
            newErrors.modules = 'Please select at least one module to continue';
          }
        }
        // Installment plan must be selected before proceeding (product-based only)
        if (!isFullScholarship && !isModuleBased && !paymentSkipped && !cardPaymentInfo && paymentType === 'installment' && !selectedPlan) {
          newErrors.selectedPlan = 'Please select an installment plan to continue';
        }

        // Validate desired payment amount only when the input is actually shown
        const shouldValidateDesiredAmount =
          !isFullScholarship &&
          !paymentSkipped &&
          !cardPaymentInfo &&
          (
            (isModuleBased && selectedModules.length > 0) ||
            (!isModuleBased && paymentType === 'full' && !!subProduct?.baseFee) ||
            (!isModuleBased && paymentType === 'installment' && !!selectedPlan)
          );

        if (shouldValidateDesiredAmount) {
          const maxAmount = getMaxPaymentAmount() + getCurrentPaymentTaxAmount();
          const minAmount = getMinPaymentAmount();
          if (!desiredPaymentAmount || desiredPaymentAmount <= 0) {
            newErrors.desiredPaymentAmount = 'Please enter a payment amount';
          } else if (minAmount > 0 && desiredPaymentAmount < minAmount) {
            newErrors.desiredPaymentAmount = `Payment amount must be at least ${membershipFeeCurrency} ${minAmount.toLocaleString()} (required fees)`;
          } else if (maxAmount > 0 && desiredPaymentAmount > maxAmount) {
            newErrors.desiredPaymentAmount = `Payment amount cannot exceed ${maxAmount.toLocaleString()}`;
          }
        }
        // A scholarship needs its proof / agreement document before the application can go on
        if (hasScholarship && !cardPaymentInfo && !scholarshipProof?.fileId) {
          newErrors.scholarshipProof = 'Please upload the scholarship proof / agreement document to continue';
        }
        // Require a bank receipt upload or a completed card payment when payment is required
        {
          const isPaymentRequired = !isFullScholarship && !(!isModuleBased && (Number(subProduct?.baseFee?.amount || 0) <= 0) && (membershipFeeAmountBase <= 0));

          if (!skipPaymentProof && isPaymentRequired && !paymentSkipped) {
            if (publicPaymentMethod === 'bank') {
              if (!paymentReceipt) {
                newErrors.paymentReceipt = 'Please upload a bank receipt to continue';
              }
            } else if (publicPaymentMethod === 'card') {
              if (!cardPaymentInfo) {
                newErrors.paymentReceipt = 'Please complete the card payment to continue';
              }
            }
          }
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    if (!isValid) {
      const firstErrorMessage = Object.values(newErrors)[0];
      if (firstErrorMessage && typeof firstErrorMessage === 'string') {
        toast.error(firstErrorMessage);
      }
    }
    return isValid;
  };

  const nextStep = async () => {
    // Block moving to Review step if payment is required but not provided
    if (currentStep === 4) {
      // A scholarship always needs its proof / agreement document first.
      if (hasScholarship && !cardPaymentInfo && !scholarshipProof?.fileId) {
        const message = 'Please upload the scholarship proof / agreement document to continue';
        setErrors({ scholarshipProof: message });
        toast.error(message);
        document.querySelector('[data-testid="scholarship-proof-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      // Require selecting an installment plan before anything else.
      if (!isFullScholarship && !isModuleBased && !paymentSkipped && !cardPaymentInfo && paymentType === 'installment' && !selectedPlan) {
        setErrors({ selectedPlan: 'Please select an installment plan to continue' });
        toast.error('Please select an installment plan to continue');
        return;
      }

      const isPaymentRequired = !isFullScholarship && !(!isModuleBased && (Number(subProduct?.baseFee?.amount || 0) <= 0) && (membershipFeeAmountBase <= 0));
      if (isPaymentRequired && !paymentSkipped) {
        if (publicPaymentMethod === 'bank' && !paymentReceipt) {
          setErrors({ paymentReceipt: 'Please upload a bank receipt to continue' });
          toast.error('Please upload a bank receipt to continue');
          return;
        }
        if (publicPaymentMethod === 'card' && !cardPaymentInfo) {
          setErrors({ paymentReceipt: 'Please complete the card payment to continue' });
          toast.error('Please complete the card payment to continue');
          return;
        }
      }
    }

    // Additional validation for step 4 (Payment) - module-based products (only if payment is not skipped)
    if (currentStep === 4 && isModuleBased && !paymentSkipped && !cardPaymentInfo) {
      if (hasPendingExemptions) {
        setErrors({ modules: 'One or more modules are awaiting exemption review. Please wait for the review to complete before proceeding.' });
        toast.error('Please wait for pending module exemption reviews to be resolved before continuing.');
        return;
      }
      if (selectedModules.length === 0) {
        setErrors({ modules: 'Please select at least one module to continue' });
        toast.error('Please select at least one module to continue');
        return;
      }
    }
    
    if (!validateStep()) {
      return;
    }

    setStepNavigating('next');
    try {
      // Persist key details to backend so they survive a browser reload.
      // Save past steps that contain personal/contact info and screening answers.
      if (currentStep === 1 || currentStep === 2 || currentStep === 3) {
        await saveDraftToServer(currentStep, { throwOnError: true });
      }

      if (currentStep === 3 && application?._id && requirementsCategoryId) {
        try {
          await updateApplicationByNumber(application._id, { requirementsCategoryId });
        } catch (error) {
          console.error('Public application category save failed:', error);
        }
      }

      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        setErrors({});
      }
    } catch (error) {
      applyDraftValidationErrors(error);
    } finally {
      setStepNavigating(null);
    }
  };

  const prevStep = () => {
    if (currentStep <= 1 || stepNavigating) {
      return;
    }

    setStepNavigating('prev');
    try {
      if (currentStep === 5) {
        setPaymentSkipped(false);
      }
      setCurrentStep(currentStep - 1);
    } finally {
      setStepNavigating(null);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading application..." />;
  }

  if (enrollmentCompletedMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Enrollment Completed</h2>
            <p className="text-gray-600 text-sm">{enrollmentCompletedMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (registrationClosedMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Registration Closed</h2>
            <p className="text-gray-600 text-sm">{registrationClosedMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted Successfully!</h2>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for submitting your application. We have received your application and will review it shortly.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <svg className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-left">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h3>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li>• You will receive a confirmation email at <strong>{application?.contactInfo?.email}</strong></li>
                    <li>• Our admissions team will review your application</li>
                    <li>• We will contact you within 3-5 business days with the next steps</li>
                    <li>• Please check your email regularly for updates</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6">
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Reference Number:</strong> <span className="font-mono text-gray-900">{application?.applicationNumber}</span></p>
                <p><strong>Program:</strong> {application?.mainProductId?.name} - {application?.subProductId?.name}</p>
                <p><strong>Submitted:</strong> {new Date(application?.submittedAt || application?.updatedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Application Not Found</h2>
          <p className="text-gray-600 mb-4">The application number you provided could not be found.</p>
          <p className="text-sm text-gray-500">Please check the application number and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img src={getSlimLogoPath()} alt="Logo" className="h-12 w-auto mr-4" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Application Form</h1>
                  <p className="text-sm text-gray-600">Reference: {application.applicationNumber}</p>
                  <p className="text-sm text-gray-600">
                    Program: {application.mainProductId?.name} - {application.subProductId?.name}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</div>
                <div className="w-32 bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-2 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
                </div>

                {/* Mode of Attendance Selection Box */}
                <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-4 sm:p-5">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    Mode of Attendance <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Please select your preferred mode of attending lectures and course activities.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className={`relative flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.attendanceMode === 'Physical'
                        ? 'border-indigo-600 bg-white shadow-sm ring-1 ring-indigo-500'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="attendanceMode"
                        value="Physical"
                        checked={formData.attendanceMode === 'Physical'}
                        onChange={(e) => setFormData(prev => ({ ...prev, attendanceMode: e.target.value }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">Physical (On-Campus)</span>
                        <span className="block text-xs text-gray-500">Attend classes in person at the campus center</span>
                      </div>
                    </label>
                    <label className={`relative flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.attendanceMode === 'Online'
                        ? 'border-indigo-600 bg-white shadow-sm ring-1 ring-indigo-500'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="attendanceMode"
                        value="Online"
                        checked={formData.attendanceMode === 'Online'}
                        onChange={(e) => setFormData(prev => ({ ...prev, attendanceMode: e.target.value }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">Online (Virtual)</span>
                        <span className="block text-xs text-gray-500">Participate live online via digital classrooms</span>
                      </div>
                    </label>
                    <label className={`relative flex items-center p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.attendanceMode === 'Blended'
                        ? 'border-indigo-600 bg-white shadow-sm ring-1 ring-indigo-500'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="attendanceMode"
                        value="Blended"
                        checked={formData.attendanceMode === 'Blended'}
                        onChange={(e) => setFormData(prev => ({ ...prev, attendanceMode: e.target.value }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">Blended Learning (Online + Physical)</span>
                        <span className="block text-xs text-gray-500">Combine online sessions with in-person classes at the campus center</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      value={formData.personalInfo.firstName}
                      onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                      className={`w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      required
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.personalInfo.lastName}
                      onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                      className={`w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name to appear on certificate *{' '}
                      <span className="font-normal text-gray-500">(Name should be same as in the NIC)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.personalInfo.nameOnCertificate}
                      onChange={(e) => handleInputChange('personalInfo', 'nameOnCertificate', e.target.value)}
                      className={`w-full px-3 py-2 border ${errors.nameOnCertificate ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      required
                    />
                    {errors.nameOnCertificate && (
                      <p className="mt-1 text-sm text-red-600">{errors.nameOnCertificate}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Enter your name exactly as it appears on your NIC / National ID, so it matches your certificate.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nationality *</label>
                    {(() => {
                      const currentNationality = formData.personalInfo.nationality || '';
                      const filteredNationalities = nationalityQuery.trim()
                        ? NATIONALITIES.filter((n) =>
                            n.toLowerCase().includes(nationalityQuery.trim().toLowerCase())
                          )
                        : NATIONALITIES;

                      return (
                        <Combobox
                          immediate
                          value={currentNationality}
                          onChange={(value) => {
                            handleInputChange('personalInfo', 'nationality', value || '');
                            setNationalityQuery('');
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-2 pr-10 border ${errors.nationality ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                              displayValue={(value) => value}
                              onChange={(e) => {
                                const next = e.target.value;
                                setNationalityQuery(next);
                                handleInputChange('personalInfo', 'nationality', next);
                              }}
                              placeholder="Search nationality"
                              required
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700">
                              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                              </svg>
                            </Combobox.Button>
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                              {filteredNationalities.length === 0 ? (
                                <div className="px-3 py-2 text-gray-500">No nationalities found</div>
                              ) : (
                                filteredNationalities.map((nationality) => (
                                  <Combobox.Option
                                    key={nationality}
                                    value={nationality}
                                    className={({ active }) =>
                                      `cursor-pointer select-none px-3 py-2 ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'}`
                                    }
                                  >
                                    {nationality}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </div>
                        </Combobox>
                      );
                    })()}
                    {errors.nationality && (
                      <p className="mt-1 text-sm text-red-600">{errors.nationality}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      NIC (National Identity Card) {!(formData.personalInfo.passportNumber?.trim() || formData.personalInfo.licenseNumber?.trim()) && '*'}
                    </label>
                    <input
                      type="text"
                      value={formData.personalInfo.nationalId || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'nationalId', e.target.value)}
                      className={`w-full px-3 py-2 border ${errors.nationalId || errors.nic ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder={
                        formData.personalInfo.passportNumber?.trim() || formData.personalInfo.licenseNumber?.trim()
                          ? 'Enter NIC (optional)'
                          : 'Enter NIC'
                      }
                      required={!(formData.personalInfo.passportNumber?.trim() || formData.personalInfo.licenseNumber?.trim())}
                    />
                    {(errors.nationalId || errors.nic) && (
                      <p className="mt-1 text-sm text-red-600">{errors.nationalId || errors.nic}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Date of birth and gender are auto-filled from your NIC. You can adjust date of birth if needed.
                      {formData.personalInfo.nationalId ? ' This field may be pre-populated from your lead information.' : ''}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Passport No</label>
                    <input
                      type="text"
                      value={formData.personalInfo.passportNumber || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'passportNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter passport number (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">License No</label>
                    <input
                      type="text"
                      value={formData.personalInfo.licenseNumber || ''}
                      onChange={(e) => handleInputChange('personalInfo', 'licenseNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter license number (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      value={formatDateForInput(formData.personalInfo.dateOfBirth)}
                      onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
                      max={maxApplicantDateOfBirth}
                      className={`w-full px-3 py-2 border ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      required
                    />
                    {errors.dateOfBirth && (
                      <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
                    )}
                    {dobNicMismatchWarning && !errors.dateOfBirth && (
                      <p className="mt-1 text-sm text-amber-600">{dobNicMismatchWarning}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Applicants must be at least 15 years old. The latest selectable date is {maxApplicantDateOfBirth}.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                    <select
                      value={formData.personalInfo.gender}
                      onChange={(e) => handleInputChange('personalInfo', 'gender', e.target.value)}
                      className={`w-full px-3 py-2 border ${errors.gender ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.gender && (
                      <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Contact Information */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.contactInfo.email}
                      onChange={(e) => handleInputChange('contactInfo', 'email', e.target.value)}
                      className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      required
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {baselineEmail
                        ? 'You can update your email if needed. Your phone number stays linked to your lead record.'
                        : 'Enter the email address you would like us to use for this application.'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={formData.contactInfo.phone}
                      readOnly
                      className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      required
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">This phone number is pre-populated from your existing records and cannot be changed here.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
                      <input
                        type="text"
                        value={formData.contactInfo.address.street}
                        onChange={(e) => handleNestedInputChange('contactInfo', 'address', 'street', e.target.value)}
                        className={`w-full px-3 py-2 border ${errors.street ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        required
                      />
                      {errors.street && (
                        <p className="mt-1 text-sm text-red-600">{errors.street}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                      <input
                        type="text"
                        value={formData.contactInfo.address.city}
                        onChange={(e) => handleNestedInputChange('contactInfo', 'address', 'city', e.target.value)}
                        className={`w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        required
                      />
                      {errors.city && (
                        <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                      {(() => {
                        const currentCountry = formData.contactInfo.address.country || '';
                        const filteredCountries = countryQuery.trim()
                          ? COUNTRIES.filter((c) => c.toLowerCase().includes(countryQuery.trim().toLowerCase()))
                          : COUNTRIES;

                        return (
                          <Combobox
                            immediate
                            value={currentCountry}
                            onChange={(value) => {
                              handleNestedInputChange('contactInfo', 'address', 'country', value || '');
                              setCountryQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 py-2 pr-10 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                displayValue={(value) => value}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setCountryQuery(next);
                                  handleNestedInputChange('contactInfo', 'address', 'country', next);
                                }}
                                required
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700">
                                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                                </svg>
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                                {filteredCountries.length === 0 ? (
                                  <div className="px-3 py-2 text-gray-500">No countries found</div>
                                ) : (
                                  filteredCountries.map((country) => (
                                    <Combobox.Option
                                      key={country}
                                      value={country}
                                      className={({ active }) =>
                                        `cursor-pointer select-none px-3 py-2 ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'}`
                                      }
                                    >
                                      {country}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                        );
                      })()}
                      {errors.country && (
                        <p className="mt-1 text-sm text-red-600">{errors.country}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Application Questions */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {requirementCategoriesList.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Requirement Category *</label>
                    <select
                      value={requirementsCategoryId}
                      onChange={(e) => {
                        setRequirementsCategoryId(e.target.value);
                        setErrors((prev) => ({ ...prev, requirementsCategoryId: undefined }));
                      }}
                      className={`w-full px-3 py-2 border ${errors.requirementsCategoryId ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    >
                      <option value="">Select category</option>
                      {requirementCategoriesList.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {errors.requirementsCategoryId && (
                      <p className="mt-1 text-sm text-red-600">{errors.requirementsCategoryId}</p>
                    )}
                  </div>
                )}

                {/* Application Questions Section */}
                {subProduct?.screeningQuestions && subProduct.screeningQuestions.length > 0 ? (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Questions</h2>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-yellow-800">
                          Please answer all questions below. Your responses will help us better understand your application.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {(() => {
                        const questions = Array.isArray(subProduct.screeningQuestions)
                          ? subProduct.screeningQuestions
                          : [];
                        const sorted = questions
                          .map((q, originalIndex) => ({ question: q, originalIndex }))
                          .sort((a, b) => (a.question.order || 0) - (b.question.order || 0));

                        const visible = sorted.filter(({ question }) => {
                          if (!question?.categoryId) return true;
                          return requirementsCategoryId && String(question.categoryId) === String(requirementsCategoryId);
                        });

                        if (visible.length === 0) {
                          if (requirementCategoriesList.length > 0 && !requirementsCategoryId) {
                            return (
                              <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
                                <p className="text-sm text-gray-600">Please select a Requirement Category above to view applicable application questions.</p>
                              </div>
                            );
                          }
                          return (
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
                              <p className="text-sm text-gray-600">No additional application questions for the selected requirement category.</p>
                            </div>
                          );
                        }

                        return visible.map(({ question, originalIndex }, visibleIndex) => (
                          <div key={`${originalIndex}_${String(question?._id || '')}`} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex items-start space-x-2 mb-3">
                              <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-medium">
                                {visibleIndex + 1}
                              </span>
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-900">
                                  {question.question}
                                  {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                                </label>
                              </div>
                            </div>

                            {question.instructions && (
                              <p className="text-xs text-gray-500 mb-3 ml-8">{question.instructions}</p>
                            )}

                            {/* Question input based on type */}
                            {question.questionType === 'text' && (
                              <input
                                type="text"
                                value={screeningAnswers[originalIndex] || ''}
                                onChange={(e) => setScreeningAnswers(prev => ({ ...prev, [originalIndex]: e.target.value }))}
                                placeholder="Enter your answer"
                                className={`w-full px-3 py-2 border ${errors[`screening_${originalIndex}`] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                              />
                            )}

                            {question.questionType === 'textarea' && (
                              <textarea
                                value={screeningAnswers[originalIndex] || ''}
                                onChange={(e) => setScreeningAnswers(prev => ({ ...prev, [originalIndex]: e.target.value }))}
                                placeholder="Enter your answer"
                                rows={4}
                                className={`w-full px-3 py-2 border ${errors[`screening_${originalIndex}`] ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                              />
                            )}

                            {question.questionType === 'single_choice' && (
                              <div className="space-y-2">
                                {question.options?.map((option, optIdx) => (
                                  <label key={optIdx} className="flex items-center">
                                    <input
                                      type="radio"
                                      name={`question_${originalIndex}`}
                                      value={option}
                                      checked={screeningAnswers[originalIndex] === option}
                                      onChange={(e) => setScreeningAnswers(prev => ({ ...prev, [originalIndex]: e.target.value }))}
                                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{option}</span>
                                  </label>
                                ))}
                              </div>
                            )}

                            {question.questionType === 'multiple_choice' && (
                              <div className="space-y-2">
                                {question.options?.map((option, optIdx) => (
                                  <label key={optIdx} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      value={option}
                                      checked={Array.isArray(screeningAnswers[originalIndex]) && screeningAnswers[originalIndex].includes(option)}
                                      onChange={(e) => {
                                        const current = Array.isArray(screeningAnswers[originalIndex]) ? screeningAnswers[originalIndex] : [];
                                        const updated = e.target.checked
                                          ? [...current, option]
                                          : current.filter(item => item !== option);
                                        setScreeningAnswers(prev => ({ ...prev, [originalIndex]: updated }));
                                      }}
                                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{option}</span>
                                  </label>
                                ))}
                              </div>
                            )}

                            {question.questionType === 'yes_no' && (
                              <div className="flex space-x-4">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`question_${originalIndex}`}
                                    value="Yes"
                                    checked={screeningAnswers[originalIndex] === 'Yes'}
                                    onChange={(e) => setScreeningAnswers(prev => ({ ...prev, [originalIndex]: e.target.value }))}
                                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                  />
                                  <span className="ml-2 text-sm font-medium text-gray-700">Yes</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`question_${originalIndex}`}
                                    value="No"
                                    checked={screeningAnswers[originalIndex] === 'No'}
                                    onChange={(e) => setScreeningAnswers(prev => ({ ...prev, [originalIndex]: e.target.value }))}
                                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                  />
                                  <span className="ml-2 text-sm font-medium text-gray-700">No</span>
                                </label>
                              </div>
                            )}

                            {errors[`screening_${originalIndex}`] && (
                              <p className="mt-2 text-sm text-red-600">{errors[`screening_${originalIndex}`]}</p>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm text-gray-600">No application questions for this program.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Payment */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment</h2>

                {/* Scholarship Banner */}
                {hasScholarship && (
                  <div className="bg-gradient-to-r from-indigo-50 via-purple-50/50 to-blue-50/40 border border-indigo-200 rounded-xl p-5 shadow-sm mb-4">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 shadow-sm shadow-indigo-200">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 uppercase tracking-wider border border-indigo-200">
                            Scholarship Awarded
                          </span>
                          <span className="text-xs text-indigo-700 font-semibold">
                            {activeScholarship?.schemeName || 'Institutional Scholarship'}
                          </span>
                        </div>
                        <h3 className="text-base font-bold text-gray-900 mt-1">
                          {isFullScholarship
                            ? '100% Full Scholarship Awarded — Zero Tuition / Fees Due'
                            : (activeScholarship?.scope === 'MODULE_SPECIFIC'
                              ? `Scholarship on selected modules (${(activeScholarship?.targetModuleCodes || []).join(', ')})`
                              : activeScholarship?.scholarshipType === 'FIXED_AMOUNT'
                              ? `${Number(activeScholarship?.fixedAmount || 0).toLocaleString()} Scholarship Granted`
                              : `${activeScholarship?.percentage || ''}% Partial Scholarship Granted`)}
                        </h3>
                        <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                          {isFullScholarship
                            ? 'You have been awarded a full scholarship for this program. No payment is required. Upload your scholarship proof / agreement document, then click "Next" to review and submit your application.'
                            : 'An institutional scholarship waiver has been applied to your invoice. Installments are proportionally reduced and you only need to pay the net balance remaining.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* When a successful card payment exists, show a simple summary instead of the full payment UI */}
                {cardPaymentInfo ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg
                          className="w-6 h-6 text-green-600 mr-3 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div>
                          <h3 className="text-base font-semibold text-green-900">
                            Card payment completed
                          </h3>
                          <p className="mt-1 text-sm text-green-800">
                            Your payment has been received successfully via card. The institution
                            will verify and update your enrollment accordingly. This payment section
                            is now locked, and no additional payments can be made from this application.
                          </p>
                          <p className="mt-3 text-2xl font-bold text-green-900">
                            {(cardPaymentInfo.currency || 'LKR')}{' '}
                            {Number(cardPaymentInfo.amount || 0).toLocaleString()}
                          </p>
                          {cardPaymentSummary && (
                            <div className="mt-2 text-xs text-green-800 space-y-0.5">
                              {cardPaymentSummary.discountAmount > 0 && (
                                <p>
                                  Discount applied: -{cardPaymentSummary.currency} {cardPaymentSummary.discountAmount.toLocaleString()}
                                </p>
                              )}
                              {cardPaymentSummary.membershipFeeAmount > 0 && (
                                <p>
                                  Membership fee: {cardPaymentSummary.membershipFeeIncluded ? '' : '+'}
                                  {cardPaymentSummary.currency} {cardPaymentSummary.membershipFeeAmount.toLocaleString()}
                                  {cardPaymentSummary.membershipFeeIncluded ? ' (included)' : ''}
                                </p>
                              )}
                              {cardPaymentSummary.registrationFeeAmount > 0 && (
                                <p>
                                  Registration fee: {cardPaymentSummary.registrationFeeWaivedForInternal || cardPaymentSummary.registrationFeeIncluded ? '' : '+'}
                                  {cardPaymentSummary.currency} {cardPaymentSummary.registrationFeeAmount.toLocaleString()}
                                  {cardPaymentSummary.registrationFeeWaivedForInternal
                                    ? ' (waived — internal student)'
                                    : cardPaymentSummary.registrationFeeIncluded ? ' (included)' : ''}
                                </p>
                              )}
                              {cardPaymentSummary.internationalPaymentFeeAmount > 0 && (
                                <p>
                                  International payment fee: +{cardPaymentSummary.currency} {cardPaymentSummary.internationalPaymentFeeAmount.toLocaleString()}
                                </p>
                              )}
                              {cardPaymentSummary.inclusiveTaxAmount > 0 && (
                                <p>
                                  Inclusive taxes: {cardPaymentSummary.currency} {cardPaymentSummary.inclusiveTaxAmount.toLocaleString()} (already included)
                                </p>
                              )}
                              {cardPaymentSummary.exclusiveTaxAmount > 0 && (
                                <p>
                                  Taxes added: +{cardPaymentSummary.currency} {cardPaymentSummary.exclusiveTaxAmount.toLocaleString()}
                                </p>
                              )}
                              {cardPaymentSummary.totalInvoiceAmount > 0 && (
                                <p>
                                  Total payable: {cardPaymentSummary.currency} {cardPaymentSummary.totalInvoiceAmount.toLocaleString()}
                                </p>
                              )}
                              {cardPaymentSummary.currentInstallmentAmount > 0 && (
                                <p>
                                  {cardPaymentSummary.currentInstallmentNumber
                                    ? `Installment ${cardPaymentSummary.currentInstallmentNumber} amount: `
                                    : 'Current installment amount: '}
                                  {cardPaymentSummary.currency} {cardPaymentSummary.currentInstallmentAmount.toLocaleString()}
                                  {cardPaymentSummary.currentInstallmentDue > 0
                                    ? ` (remaining: ${cardPaymentSummary.currency} ${cardPaymentSummary.currentInstallmentDue.toLocaleString()})`
                                    : ''}
                                </p>
                              )}
                            </div>
                          )}
                          <p className="mt-1 text-xs text-green-800">
                            Reference:{' '}
                            <span className="font-mono">
                              {cardPaymentInfo.referenceNumber || 'N/A'}
                            </span>
                            {cardPaymentInfo.date && (
                              <>
                                {' '}
                                | Date:{' '}
                                {new Date(cardPaymentInfo.date).toLocaleString()}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Selected modules summary for module-based programs */}
                    {isModuleBased && selectedModules.length > 0 && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-indigo-900 mb-2">
                          Selected Modules ({selectedModules.length})
                        </h4>
                        <ul className="text-xs text-indigo-900 space-y-1">
                          {availableModules
                            .filter((m) => selectedModules.includes(m.code))
                            .map((module, index) => {
                              const displayModule = resolveAssignedModule(module);
                              const isRepeat = Boolean(displayModule?.isRepeat || displayModule?.feeType === 'REPEAT_FEE');
                              const exemption = getModuleExemption(module.code);
                              const isApproved = exemption?.status === 'approved';
                              const moduleTotal = getResolvedModulePayableTotal(module);
                              return (
                                <li key={module.code || index}>
                                  <span className="font-mono">{module.code}</span> - {module.name}{' '}
                                  {module.credits ? (
                                    <span className="text-gray-600">
                                      ({module.credits} credits)
                                    </span>
                                  ) : null}
                                  <span className={`font-semibold ${isApproved ? 'text-green-700' : isRepeat ? 'text-purple-700' : ''}`}>
                                    {' '}
                                    – LKR {moduleTotal.toLocaleString()}
                                    {isApproved && ' (Exemption)'}
                                    {isRepeat && ` (Repeat Fee${displayModule.repeatAttempt ? ` - Attempt ${displayModule.repeatAttempt}` : ''})`}
                                  </span>
                                </li>
                              );
                            })}
                        </ul>
                        <p className="mt-2 text-xs font-semibold text-indigo-900">
                          Total for selected modules:{' '}
                          <span className="text-indigo-700">
                            {(cardPaymentSummary?.currency || 'LKR')}{' '}
                            {Number(
                              cardPaymentSummary?.courseBaseAmount > 0
                                ? cardPaymentSummary.courseBaseAmount
                                : calculateModuleTotal()
                            ).toLocaleString()}
                          </span>
                        </p>

                        {Number(cardPaymentSummary?.membershipFeeAmount || 0) > 0 && (
                          <p className="mt-0.5 text-xs font-semibold text-indigo-900">
                            Membership fee:{' '}
                            <span className="text-indigo-700">
                              {cardPaymentSummary?.membershipFeeIncluded ? '' : '+'}
                              {cardPaymentSummary?.currency || 'LKR'} {Number(cardPaymentSummary?.membershipFeeAmount || 0).toLocaleString()}
                              {cardPaymentSummary?.membershipFeeIncluded ? ' (included)' : ''}
                            </span>
                          </p>
                        )}

                        {Number(cardPaymentSummary?.registrationFeeAmount || 0) > 0 && (
                          <p className="mt-0.5 text-xs font-semibold text-indigo-900">
                            Registration fee:{' '}
                            <span className="text-indigo-700">
                              {cardPaymentSummary?.registrationFeeWaivedForInternal || cardPaymentSummary?.registrationFeeIncluded ? '' : '+'}
                              {cardPaymentSummary?.currency || 'LKR'} {Number(cardPaymentSummary?.registrationFeeAmount || 0).toLocaleString()}
                              {cardPaymentSummary?.registrationFeeWaivedForInternal
                                ? ' (waived — internal student)'
                                : cardPaymentSummary?.registrationFeeIncluded ? ' (included)' : ''}
                            </span>
                          </p>
                        )}

                        {Number(cardPaymentSummary?.internationalPaymentFeeAmount || 0) > 0 && (
                          <p className="mt-0.5 text-xs font-semibold text-indigo-900">
                            International payment fee:{' '}
                            <span className="text-indigo-700">
                              +{cardPaymentSummary?.currency || 'LKR'} {Number(cardPaymentSummary?.internationalPaymentFeeAmount || 0).toLocaleString()}
                            </span>
                          </p>
                        )}

                        {Number(cardPaymentSummary?.discountAmount || 0) > 0 && (
                          <div className="mt-2 text-xs text-indigo-900">
                            <p>
                              Discount applied:{' '}
                              <span className="font-semibold text-indigo-700">
                                -{cardPaymentSummary?.currency || 'LKR'}{' '}
                                {Number(cardPaymentSummary?.discountAmount || 0).toLocaleString()}
                              </span>
                            </p>
                            {Number(cardPaymentSummary?.courseNetAmount || cardPaymentSummary?.subtotalAfterDiscount || 0) > 0 && (
                              <p className="mt-0.5">
                                Total after discount:{' '}
                                <span className="font-semibold text-indigo-700">
                                  {cardPaymentSummary?.currency || 'LKR'}{' '}
                                  {Number(cardPaymentSummary?.courseNetAmount || cardPaymentSummary?.subtotalAfterDiscount || 0).toLocaleString()}
                                </span>
                              </p>
                            )}
                          </div>
                        )}

                        {Number(cardPaymentSummary?.inclusiveTaxAmount || 0) > 0 && (
                          <p className="mt-0.5 text-xs font-semibold text-indigo-900">
                            Inclusive taxes:{' '}
                            <span className="text-indigo-700">
                              {cardPaymentSummary?.currency || 'LKR'} {Number(cardPaymentSummary?.inclusiveTaxAmount || 0).toLocaleString()} (already included)
                            </span>
                          </p>
                        )}

                        {Number(cardPaymentSummary?.exclusiveTaxAmount || 0) > 0 && (
                          <p className="mt-0.5 text-xs font-semibold text-indigo-900">
                            Taxes added:{' '}
                            <span className="text-indigo-700">
                              +{cardPaymentSummary?.currency || 'LKR'} {Number(cardPaymentSummary?.exclusiveTaxAmount || 0).toLocaleString()}
                            </span>
                          </p>
                        )}

                        {Number(cardPaymentSummary?.totalInvoiceAmount || 0) > 0 && (
                          <p className="mt-2 text-xs font-semibold text-indigo-900">
                            Total payable:{' '}
                            <span className="text-indigo-700">
                              {cardPaymentSummary?.currency || 'LKR'} {Number(cardPaymentSummary?.totalInvoiceAmount || 0).toLocaleString()}
                            </span>
                          </p>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-gray-600">
                      If you need to make any changes, please contact the institution instead of
                      attempting another payment from this form.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Module / Product based payment UI when there is no completed card payment */}
                    {isModuleBased && availableModules.length > 0 ? (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-blue-800">Module-Based Program</p>
                          <p className="text-xs text-blue-700 mt-1">
                            {modulesLocked
                              ? 'Your enrollment modules have been assigned by your advisor. Review the modules below, request exemptions if eligible, then proceed to payment.'
                              : 'Please select the modules you wish to enroll in. You will pay for only the selected modules.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Module-based payment mode */}
                    {!isFullScholarship && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-900 mb-2">Payment Mode</p>
                      {payTotalOnly && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2">
                          Your advisor set a pay-total amount. Only Pay total is available.
                        </p>
                      )}
                      {showInstallmentOption && !showPayTotalOption && (
                        <p className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-md px-2 py-1.5 mb-2">
                          Installment payment is required for this program ({subProduct.moduleInstallments.count} installments).
                        </p>
                      )}
                      <div className="space-y-2">
                        {showPayTotalOption && (
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="modulePaymentMode"
                            value="total"
                            checked={modulePaymentMode === 'total'}
                            onChange={() => {
                              setModulePaymentMode('total');
                            }}
                            className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Pay total (you can pay full or a partial amount)</span>
                        </label>
                        )}
                        {showInstallmentOption && (
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="modulePaymentMode"
                              value="installment"
                              checked={modulePaymentMode === 'installment'}
                              onChange={() => {
                                setModulePaymentMode('installment');
                              }}
                              className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Pay in installments ({subProduct.moduleInstallments.count} installments)
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Select All Option */}
                    {!modulesLocked && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedModules.length === availableModules.length && availableModules.length > 0}
                          onChange={toggleSelectAllModules}
                          className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="ml-3 text-sm font-semibold text-gray-900">Select All Modules</span>
                      </label>
                      <span className="text-sm text-gray-600">
                        {selectedModules.length} of {availableModules.length} selected
                      </span>
                    </div>
                    )}
                    {modulesLocked && (
                      <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <span className="text-sm font-semibold text-indigo-900">Assigned modules</span>
                        <span className="text-sm text-indigo-700">
                          {selectedModules.length} module{selectedModules.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    {/* Pending exemptions info banner */}
                    {payTotalOnly && (
                      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div>
                          <p className="text-sm font-semibold text-blue-900">Custom module total set</p>
                          <p className="text-xs text-blue-800 mt-0.5">
                            Your advisor has set a module total for this application. Module exemption requests are not available in this case.
                          </p>
                        </div>
                      </div>
                    )}
                    {hasPendingExemptions && (
                      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Exemption Review Pending</p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            One or more modules have pending exemption fee review requests. Those modules are excluded from your total until reviewed. You cannot proceed to the next step until all reviews are resolved.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Module List - grouped by stage when stages are defined */}
                    {moduleStages.length > 0 ? (
                      <div className="space-y-6">
                        {moduleStages.map((stage, stageIdx) => (
                          <div key={stage._id || stageIdx}>
                            <div className="flex items-center gap-2 mb-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
                                Stage {stage.stageNumber}
                              </span>
                              {stage.stageName && stage.stageName !== `Stage ${stage.stageNumber}` && (
                                <h3 className="text-sm font-semibold text-gray-700">{stage.stageName}</h3>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {(stage.modules || []).map((module, index) => {
                                const resolved = resolveAssignedModule(module);
                                const isRepeatModule = isRepeatFeeModule(resolved, module);
                                const repeatAttempt = isRepeatModule ? getRepeatAttemptNumber(resolved, module) : null;
                                const displayModule = {
                                  ...resolved,
                                  isRepeat: isRepeatModule,
                                  repeatAttempt,
                                  feeType: isRepeatModule ? 'REPEAT_FEE' : (resolved?.feeType || 'MODULE_FEE'),
                                };
                                const exemption = getModuleExemption(module.code);
                                const isDraft = !!exemptionDrafts[module.code];
                                const isPending = exemption?.status === 'pending';
                                const isApproved = exemption?.status === 'approved';
                                const isRejected = exemption?.status === 'rejected';
                                const isLocked = isPending || isApproved;
                                const isSelected = isApproved
                                  ? true
                                  : modulesLocked
                                    ? !isPending
                                    : selectedModules.includes(module.code);
                                const examFee = module.examination?.firstAttempt && !module.examination.firstAttempt.included
                                  ? (module.examination.firstAttempt.fee || 0) : 0;
                                const moduleTotal = isApproved
                                  ? (Number(exemption.exemptionFee) || 0)
                                  : (displayModule.moduleFee || 0) + examFee;
                                return (
                                  <div
                                    key={module.code || index}
                                    className={`border-2 rounded-lg p-4 transition-all ${
                                      isApproved ? 'border-green-500 bg-green-50'
                                        : isPending ? 'border-amber-400 bg-amber-50'
                                        : isSelected ? isRepeatModule ? 'border-purple-600 bg-purple-50/60' : 'border-indigo-600 bg-indigo-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleModuleSelection(module.code)}
                                        disabled={isLocked || modulesLocked}
                                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-1 flex-shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                          <span className="text-sm font-mono font-semibold text-indigo-600">{module.code}</span>
                                          <span className="text-base font-semibold text-gray-900">{module.name}</span>
                                          {isRepeatModule && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                                              Repeat Fee{displayModule.repeatAttempt ? ` (Attempt ${displayModule.repeatAttempt})` : ''}
                                            </span>
                                          )}
                                          {isPending && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Awaiting Review</span>}
                                          {isApproved && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Exemption Approved</span>}
                                          {isRejected && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">Exemption Rejected</span>}
                                        </div>
                                        {module.credits && (
                                          <p className="text-xs text-gray-600 mb-2">
                                            <span className="font-medium">Credits:</span> {module.credits}
                                          </p>
                                        )}
                                        <div className="mb-2">
                                          {isRepeatModule ? (
                                            <p className="text-sm text-purple-900">
                                              <span className="font-semibold">Repeat Fee{displayModule.repeatAttempt ? ` (Attempt ${displayModule.repeatAttempt})` : ''}:</span>{' '}
                                              <span className="font-bold text-purple-950">LKR {(displayModule.moduleFee || 0).toLocaleString()}</span>
                                            </p>
                                          ) : isApproved ? (
                                            <div>
                                              <p className="text-xs text-gray-500 line-through">Original: LKR {(module.moduleFee || 0).toLocaleString()}</p>
                                              <p className="text-sm text-green-700 font-semibold">Exemption Fee: LKR {(Number(exemption.exemptionFee) || 0).toLocaleString()}</p>
                                            </div>
                                          ) : (
                                            <p className="text-sm text-gray-700">
                                              <span className="font-medium">Module Fee:</span>{' '}
                                              <span className="font-semibold text-gray-900">LKR {(displayModule.moduleFee || 0).toLocaleString()}</span>
                                            </p>
                                          )}
                                          <ModuleFeeAdjustmentBadge module={displayModule} className="mt-2" />
                                        </div>
                                        {module.examination && (
                                          <div className="bg-white/60 rounded p-2 mb-2">
                                            <p className="text-xs font-medium text-gray-700 mb-1">Examination:</p>
                                            {module.examination.firstAttempt && (
                                              <div className="text-xs text-gray-600">
                                                {module.examination.firstAttempt.included ? (
                                                  <span className="text-green-600">Included</span>
                                                ) : (
                                                  <span>LKR {(module.examination.firstAttempt.fee || 0).toLocaleString()}</span>
                                                )}
                                              </div>
                                            )}
                                            {module.examination.repeatAttempts?.length > 0 && (
                                              <div className="text-xs text-gray-600 mt-1">
                                                <span className="font-medium">Repeat Attempts:</span>
                                                <ul className="ml-4 mt-0.5">
                                                  {module.examination.repeatAttempts.map((attempt, idx) => (
                                                    <li key={idx}>Attempt {attempt.attemptNumber}: LKR {(attempt.fee || 0).toLocaleString()}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        <div className="pt-2 border-t border-gray-200 mb-2">
                                          <p className="text-sm font-semibold text-gray-900">
                                            Total for this module:{' '}
                                            <span className={isApproved ? 'text-green-600' : isRepeatModule ? 'text-purple-700' : 'text-indigo-600'}>
                                              LKR {moduleTotal.toLocaleString()}
                                            </span>
                                          </p>
                                        </div>
                                        {isRejected && exemption.reviewNotes && (
                                          <p className="text-xs text-red-600 mb-2"><span className="font-medium">Review note:</span> {exemption.reviewNotes}</p>
                                        )}
                                        {isPending && (
                                          <p className="text-sm text-amber-800 font-semibold mb-2">
                                            Requested Exemption Fee: LKR {(Number(exemption.exemptionFee) || 0).toLocaleString()}
                                            <span className="block text-xs font-normal text-amber-700 mt-0.5">
                                              ({resolvedStudentType === 'internal' ? 'Internal' : 'External'} student rate)
                                            </span>
                                          </p>
                                        )}
                                        {isDraft && (
                                          <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg">
                                            <p className="text-xs text-gray-600 mb-2">
                                              Expected exemption fee ({resolvedStudentType === 'internal' ? 'internal' : 'external'} student):{' '}
                                              <span className="font-semibold text-green-700">
                                                LKR {getExpectedModuleExemptionFee(module).toLocaleString()}
                                              </span>
                                            </p>
                                            <p className="text-xs font-medium text-gray-700 mb-2">Upload supporting documents (optional):</p>
                                            <input
                                              type="file"
                                              multiple
                                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                              onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                setExemptionDrafts(prev => ({ ...prev, [module.code]: { ...prev[module.code], files } }));
                                              }}
                                              className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 mb-3"
                                            />
                                            {uploadingExemptionDocs[module.code] && <p className="text-xs text-gray-500 mb-2">Uploading documents...</p>}
                                            <div className="flex gap-2">
                                              <button type="button" disabled={savingExemption[module.code]} onClick={() => saveExemptionRequest(module.code)}
                                                className="flex-1 py-1.5 px-3 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                                {savingExemption[module.code] ? 'Submitting...' : 'Submit Request'}
                                              </button>
                                              <button type="button" onClick={() => closeExemptionDraft(module.code)}
                                                className="py-1.5 px-3 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200">
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                        {!isDraft && (
                                          <div className="mt-1">
                                            {isPending && (
                                              <button type="button" onClick={() => revokeExemptionRequest(module.code)}
                                                className="text-xs text-red-600 hover:text-red-800 font-medium underline">
                                                Cancel Exemption Request
                                              </button>
                                            )}
                                            {!isPending && !isApproved && !payTotalOnly && (
                                              isRepeatModule ? (
                                                <span className="inline-flex items-center text-xs text-gray-500 italic bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                                  Repeat module — exemption not applicable
                                                </span>
                                              ) : (
                                                <button type="button" onClick={() => openExemptionDraft(module.code)}
                                                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline">
                                                  {isRejected ? 'Re-request Exemption Fee Review' : 'Request Exemption Fee Review'}
                                                </button>
                                              )
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {availableModules.map((module, index) => {
                        const resolved = resolveAssignedModule(module);
                        const isRepeatModule = isRepeatFeeModule(resolved, module);
                        const repeatAttempt = isRepeatModule ? getRepeatAttemptNumber(resolved, module) : null;
                        const displayModule = {
                          ...resolved,
                          isRepeat: isRepeatModule,
                          repeatAttempt,
                          feeType: isRepeatModule ? 'REPEAT_FEE' : (resolved?.feeType || 'MODULE_FEE'),
                        };
                        const exemption = getModuleExemption(module.code);
                        const isDraft = !!exemptionDrafts[module.code];
                        const isPending = exemption?.status === 'pending';
                        const isApproved = exemption?.status === 'approved';
                        const isRejected = exemption?.status === 'rejected';
                        const isLocked = isPending || isApproved;
                        const isSelected = isApproved
                          ? true
                          : modulesLocked
                            ? !isPending
                            : selectedModules.includes(module.code);
                        const examFee = module.examination?.firstAttempt && !module.examination.firstAttempt.included
                          ? (module.examination.firstAttempt.fee || 0) : 0;
                        const moduleTotal = isApproved
                          ? (Number(exemption.exemptionFee) || 0)
                          : (displayModule.moduleFee || 0) + examFee;

                        return (
                          <div
                            key={module.code || index}
                            className={`border-2 rounded-lg p-4 transition-all ${
                              isApproved ? 'border-green-500 bg-green-50'
                                : isPending ? 'border-amber-400 bg-amber-50'
                                : isSelected ? isRepeatModule ? 'border-purple-600 bg-purple-50/60' : 'border-indigo-600 bg-indigo-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleModuleSelection(module.code)}
                                disabled={isLocked || modulesLocked}
                                className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-1 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className="text-sm font-mono font-semibold text-indigo-600">{module.code}</span>
                                  <span className="text-base font-semibold text-gray-900">{module.name}</span>
                                  {isRepeatModule && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                                      Repeat Fee{displayModule.repeatAttempt ? ` (Attempt ${displayModule.repeatAttempt})` : ''}
                                    </span>
                                  )}
                                  {isPending && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Awaiting Review</span>}
                                  {isApproved && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Exemption Approved</span>}
                                  {isRejected && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">Exemption Rejected</span>}
                                </div>
                                {module.credits && (
                                  <p className="text-xs text-gray-600 mb-2">
                                    <span className="font-medium">Credits:</span> {module.credits}
                                  </p>
                                )}
                                <div className="mb-2">
                                  {isRepeatModule ? (
                                    <p className="text-sm text-purple-900">
                                      <span className="font-semibold">Repeat Fee{displayModule.repeatAttempt ? ` (Attempt ${displayModule.repeatAttempt})` : ''}:</span>{' '}
                                      <span className="font-bold text-purple-950">LKR {(displayModule.moduleFee || 0).toLocaleString()}</span>
                                    </p>
                                  ) : isApproved ? (
                                    <div>
                                      <p className="text-xs text-gray-500 line-through">Original: LKR {(module.moduleFee || 0).toLocaleString()}</p>
                                      <p className="text-sm text-green-700 font-semibold">Exemption Fee: LKR {(Number(exemption.exemptionFee) || 0).toLocaleString()}</p>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-700">
                                      <span className="font-medium">Module Fee:</span>{' '}
                                      <span className="font-semibold text-gray-900">LKR {(displayModule.moduleFee || 0).toLocaleString()}</span>
                                    </p>
                                  )}
                                  <ModuleFeeAdjustmentBadge module={displayModule} className="mt-2" />
                                </div>
                                {module.examination && (
                                  <div className="bg-white/60 rounded p-2 mb-2">
                                    <p className="text-xs font-medium text-gray-700 mb-1">Examination:</p>
                                    {module.examination.firstAttempt && (
                                      <div className="text-xs text-gray-600">
                                        <span className="font-medium">First Attempt:</span>{' '}
                                        {module.examination.firstAttempt.included ? (
                                          <span className="text-green-600">Included</span>
                                        ) : (
                                          <span>LKR {module.examination.firstAttempt.fee?.toLocaleString() || '0'}</span>
                                        )}
                                      </div>
                                    )}
                                    {module.examination.repeatAttempts && module.examination.repeatAttempts.length > 0 && (
                                      <div className="text-xs text-gray-600 mt-1">
                                        <span className="font-medium">Repeat Attempts:</span>
                                        <ul className="ml-4 mt-0.5">
                                          {module.examination.repeatAttempts.map((attempt, idx) => (
                                            <li key={idx}>Attempt {attempt.attemptNumber}: LKR {attempt.fee?.toLocaleString() || '0'}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="pt-2 border-t border-gray-200 mb-2">
                                  <p className="text-sm font-semibold text-gray-900">
                                    Total for this module:{' '}
                                    <span className={isApproved ? 'text-green-600' : isRepeatModule ? 'text-purple-700' : 'text-indigo-600'}>
                                      LKR {moduleTotal.toLocaleString()}
                                    </span>
                                  </p>
                                </div>
                                {isRejected && exemption.reviewNotes && (
                                  <p className="text-xs text-red-600 mb-2"><span className="font-medium">Review note:</span> {exemption.reviewNotes}</p>
                                )}
                                {isPending && (
                                  <p className="text-sm text-amber-800 font-semibold mb-2">
                                    Requested Exemption Fee: LKR {(Number(exemption.exemptionFee) || 0).toLocaleString()}
                                    <span className="block text-xs font-normal text-amber-700 mt-0.5">
                                      ({resolvedStudentType === 'internal' ? 'Internal' : 'External'} student rate)
                                    </span>
                                  </p>
                                )}
                                {isDraft && (
                                  <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg">
                                    <p className="text-xs text-gray-600 mb-2">
                                      Expected exemption fee ({resolvedStudentType === 'internal' ? 'internal' : 'external'} student):{' '}
                                      <span className="font-semibold text-green-700">
                                        LKR {getExpectedModuleExemptionFee(module).toLocaleString()}
                                      </span>
                                    </p>
                                    <p className="text-xs font-medium text-gray-700 mb-2">Upload supporting documents (optional):</p>
                                    <input
                                      type="file"
                                      multiple
                                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        setExemptionDrafts(prev => ({ ...prev, [module.code]: { ...prev[module.code], files } }));
                                      }}
                                      className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 mb-3"
                                    />
                                    {uploadingExemptionDocs[module.code] && <p className="text-xs text-gray-500 mb-2">Uploading documents...</p>}
                                    <div className="flex gap-2">
                                      <button type="button" disabled={savingExemption[module.code]} onClick={() => saveExemptionRequest(module.code)}
                                        className="flex-1 py-1.5 px-3 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                        {savingExemption[module.code] ? 'Submitting...' : 'Submit Request'}
                                      </button>
                                      <button type="button" onClick={() => closeExemptionDraft(module.code)}
                                        className="py-1.5 px-3 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200">
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {!isDraft && (
                                  <div className="mt-1">
                                    {isPending && (
                                      <button type="button" onClick={() => revokeExemptionRequest(module.code)}
                                        className="text-xs text-red-600 hover:text-red-800 font-medium underline">
                                        Cancel Exemption Request
                                      </button>
                                    )}
                                    {!isPending && !isApproved && !payTotalOnly && (
                                      isRepeatModule ? (
                                        <span className="inline-flex items-center text-xs text-gray-500 italic bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                          Repeat module — exemption not applicable
                                        </span>
                                      ) : (
                                        <button type="button" onClick={() => openExemptionDraft(module.code)}
                                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline">
                                          {isRejected ? 'Re-request Exemption Fee Review' : 'Request Exemption Fee Review'}
                                        </button>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    )}


                  </div>
                ) : !isModuleBased ? (
                  /* Product-Based Pricing Section */
                  <div className="space-y-3">
                    {!isFullScholarship && (
                      <>
                        {/* Payment Type Selection */}
                        <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Payment Option <span className="text-red-500">*</span>
                      </label>
                      {allowedPaymentOption === 'full_only' ? (
                        <div className="p-4 border-2 border-indigo-600 bg-indigo-50/70 rounded-lg ring-2 ring-indigo-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 rounded-full border-2 border-indigo-600 flex items-center justify-center">
                                <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                              </div>
                              <div className="text-left">
                                <p className="font-semibold text-gray-900">Pay Full Amount</p>
                                <p className="text-sm text-gray-600">Pay the complete course fee now</p>
                              </div>
                            </div>
                            <span className="text-xs font-semibold px-2.5 py-1 bg-indigo-100 text-indigo-800 rounded-full">
                              Required by admissions
                            </span>
                          </div>
                        </div>
                      ) : allowedPaymentOption === 'installment_only' ? (
                        <div className="p-4 border-2 border-indigo-600 bg-indigo-50/70 rounded-lg ring-2 ring-indigo-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 rounded-full border-2 border-indigo-600 flex items-center justify-center">
                                <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                              </div>
                              <div className="text-left">
                                <p className="font-semibold text-gray-900">Pay In Installments</p>
                                <p className="text-sm text-gray-600">Pay in multiple installments</p>
                              </div>
                            </div>
                            <span className="text-xs font-semibold px-2.5 py-1 bg-indigo-100 text-indigo-800 rounded-full">
                              Required by admissions
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button
                            type="button"
                            onClick={() => {
                              setPaymentType('full');
                              setSelectedPlan(null);
                              setDesiredPaymentAmount(0); // Reset payment amount when changing payment type
                            }}
                            className={`p-4 border-2 rounded-lg transition-all ${paymentType === 'full'
                                ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                                : 'border-gray-300 hover:border-indigo-400'
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentType === 'full' ? 'border-indigo-600' : 'border-gray-300'
                                }`}>
                                {paymentType === 'full' && (
                                  <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                                )}
                              </div>
                              <div className="text-left">
                                <p className="font-semibold text-gray-900">Pay Full Amount</p>
                                <p className="text-sm text-gray-600">Pay the complete course fee now</p>
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setPaymentType('installment');
                              // Will be set by useEffect to max amount when plan is selected
                            }}
                            className={`p-4 border-2 rounded-lg transition-all ${paymentType === 'installment'
                                ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                                : 'border-gray-300 hover:border-indigo-400'
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentType === 'installment' ? 'border-indigo-600' : 'border-gray-300'
                                }`}>
                                {paymentType === 'installment' && (
                                  <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                                )}
                              </div>
                              <div className="text-left">
                                <p className="font-semibold text-gray-900">Pay In Installments</p>
                                <p className="text-sm text-gray-600">Pay in multiple installments</p>
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>

                    {paymentType === 'installment' && (
                      <>
                        {subProduct.installmentPlans && subProduct.installmentPlans.length > 0 ? (
                          <>
                            {/* Installment Plan Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-3">
                                Select Installment Plan <span className="text-red-500">*</span>
                              </label>
                              <div className="space-y-3">
                                {subProduct.installmentPlans.map((plan, index) => {
                                  const isSelectedPlan = Boolean(
                                    selectedPlan &&
                                    (
                                      (selectedPlan._id && plan._id && String(selectedPlan._id) === String(plan._id)) ||
                                      (selectedPlan.id && plan.id && String(selectedPlan.id) === String(plan.id)) ||
                                      (selectedPlan === plan)
                                    )
                                  );
                                  const planDiscountAmount =
                                    !paymentSkipped &&
                                    !isModuleBased &&
                                    paymentType === 'installment' &&
                                    discountQuote?.appliedDiscount &&
                                    Number(discountQuote?.discountAmount) > 0
                                      ? Number(discountQuote.discountAmount)
                                      : 0;
                                  const planTaxInfo = computeProductInstallmentPlanTaxInfo(
                                    plan.totalAmount,
                                    planDiscountAmount
                                  );

                                  return (
                                  <button
                                    key={plan._id || plan.id || index}
                                    type="button"
                                    onClick={() => setSelectedPlan(plan)}
                                    className={`w-full p-4 border-2 rounded-lg transition-all text-left ${
                                      isSelectedPlan
                                        ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                                        : 'border-gray-300 hover:border-indigo-400'
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${
                                        isSelectedPlan ? 'border-indigo-600' : 'border-gray-300'
                                      }`}>
                                        {isSelectedPlan && (
                                          <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                                        )}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="font-semibold text-gray-900">{plan.planName}</p>
                                          <p className="text-sm font-medium text-indigo-600">
                                            {plan.numberOfInstallments} installments
                                          </p>
                                        </div>
                                        {plan.description && (
                                          <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                                        )}
                                        <div className="space-y-1 text-sm">
                                          {plan.installments.map((inst, idx) => {
                                            const isScholarshipQuote = Boolean(hasScholarship && (discountQuote?.isScholarship || discountQuote?.appliedDiscount?.kind === 'scholarship'));
                                            const totalDiscount = planDiscountAmount;
                                            const isLast = idx === plan.installments.length - 1;
                                            const isFirst = idx === 0;
                                            const membershipFee = getMembershipFeeForPaymentContext();
                                            const registrationFee = getRegistrationFeeForPaymentContext();
                                            const baseAmount = Number(inst.amount || 0);
                                            const membershipApplied = isFirst && membershipFee > 0 ? membershipFee : 0;
                                            const registrationApplied = isFirst && registrationFee > 0 ? registrationFee : 0;

                                            let discountApplied = 0;
                                            if (isScholarshipQuote && totalDiscount > 0) {
                                              const totalPlanAmount = Number(plan.totalAmount || 0);
                                              if (totalPlanAmount > 0) {
                                                if (isLast) {
                                                  const priorSum = plan.installments.slice(0, idx).reduce((s, it) => s + roundCurrency(totalDiscount * (Number(it.amount || 0) / totalPlanAmount)), 0);
                                                  discountApplied = Math.min(baseAmount, Math.max(0, roundCurrency(totalDiscount - priorSum)));
                                                } else {
                                                  discountApplied = Math.min(baseAmount, roundCurrency(totalDiscount * (baseAmount / totalPlanAmount)));
                                                }
                                              }
                                            } else {
                                              discountApplied = isLast && totalDiscount > 0 ? totalDiscount : 0;
                                            }

                                            const taxApplied = isFirst && (planTaxInfo?.totalTaxAmount || 0) > 0 ? planTaxInfo.totalTaxAmount : 0;
                                            const payableAmount = Math.max(0, baseAmount + membershipApplied + registrationApplied + taxApplied - discountApplied);

                                            return (
                                            <div key={idx} className="text-gray-700">
                                              <div className="flex items-center justify-between">
                                                <span>Installment {inst.installmentNumber}: {inst.description}</span>
                                                <span className="font-medium">
                                                  {subProduct.baseFee.currency} {baseAmount.toLocaleString()}
                                                </span>
                                              </div>
                                              {(registrationApplied > 0 || membershipApplied > 0 || discountApplied > 0 || taxApplied > 0) && (
                                                <div className="mt-1 flex items-center justify-between text-xs text-gray-600">
                                                  <span>
                                                    {registrationApplied > 0 ? `Registration fee: +${registrationFeeCurrency} ${registrationApplied.toLocaleString()}` : ''}
                                                    {registrationApplied > 0 && (membershipApplied > 0 || discountApplied > 0 || taxApplied > 0) ? ' • ' : ''}
                                                    {membershipApplied > 0 ? `Membership fee: +${membershipFeeCurrency} ${membershipApplied.toLocaleString()}` : ''}
                                                    {membershipApplied > 0 && (discountApplied > 0 || taxApplied > 0) ? ' • ' : ''}
                                                    {taxApplied > 0 ? planTaxInfo.taxes.map((t, i) => <span key={i} className="text-indigo-600">{t.name} ({t.percentage}%): +{t.currency} {t.taxAmount.toLocaleString()}</span>) : ''}
                                                    {taxApplied > 0 && discountApplied > 0 ? ' • ' : ''}
                                                    {discountApplied > 0 ? `Discount: -${discountCurrency} ${discountApplied.toLocaleString()}` : ''}
                                                  </span>
                                                  <span className="font-medium text-indigo-700">
                                                    Payable: {subProduct.baseFee.currency} {payableAmount.toLocaleString()}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                            );
                                          })}
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          <div className="flex items-center justify-between font-semibold">
                                            <span>Total Amount:</span>
                                            <span className="text-indigo-600">
                                              {(() => {
                                                const baseTotal = Number(plan.totalAmount || 0);
                                                const discountAmount = planDiscountAmount;
                                                const membershipFee = getMembershipFeeForPaymentContext();
                                                const registrationFee = getRegistrationFeeForPaymentContext();
                                                const discountedBase = discountAmount > 0 ? Math.max(0, baseTotal - discountAmount) : baseTotal;
                                                const taxAmt = planTaxInfo?.totalTaxAmount || 0;
                                                return `${subProduct.baseFee.currency} ${(discountedBase + (membershipFee > 0 ? membershipFee : 0) + (registrationFee > 0 ? registrationFee : 0) + taxAmt).toLocaleString()}`;
                                              })()}
                                            </span>
                                          </div>
                                          {(() => {
                                            const baseTotal = Number(plan.totalAmount || 0);
                                            const discountAmount = planDiscountAmount;
                                            const membershipFee = getMembershipFeeForPaymentContext();
                                            const registrationFee = getRegistrationFeeForPaymentContext();
                                            const discountedBase = discountAmount > 0 ? Math.max(0, baseTotal - discountAmount) : baseTotal;
                                            const taxAmt = planTaxInfo?.totalTaxAmount || 0;
                                            const totalWithMembershipAndTax = discountedBase + (membershipFee > 0 ? membershipFee : 0) + (registrationFee > 0 ? registrationFee : 0) + taxAmt;

                                            if (discountAmount <= 0 && membershipFee <= 0 && registrationFee <= 0 && taxAmt <= 0) return null;
                                            return (
                                              <div className="mt-1 text-xs text-gray-600 space-y-0.5">
                                                <div className="flex items-center justify-between">
                                                  <span>Base total</span>
                                                  <span>{subProduct.baseFee.currency} {baseTotal.toLocaleString()}</span>
                                                </div>
                                                {discountAmount > 0 && (
                                                  <div className="flex items-center justify-between">
                                                    <span>{hasScholarship && (discountQuote?.isScholarship || discountQuote?.appliedDiscount?.kind === 'scholarship') ? 'Scholarship waiver' : 'Discount (applied to last installment)'}</span>
                                                    <span>-{discountCurrency} {discountAmount.toLocaleString()}</span>
                                                  </div>
                                                )}
                                                {registrationFee > 0 && (
                                                  <div className="flex items-center justify-between">
                                                    <span>Registration fee (added to first installment)</span>
                                                    <span>+{registrationFeeCurrency} {registrationFee.toLocaleString()}</span>
                                                  </div>
                                                )}
                                                {membershipFee > 0 && (
                                                  <div className="flex items-center justify-between">
                                                    <span>Membership fee (added to first installment)</span>
                                                    <span>+{membershipFeeCurrency} {membershipFee.toLocaleString()}</span>
                                                  </div>
                                                )}
                                                {taxAmt > 0 && planTaxInfo.taxes.map((t, i) => (
                                                  <div key={i} className="flex items-center justify-between text-indigo-600">
                                                    <span>{t.name} ({t.percentage}%) (added to first installment)</span>
                                                    <span>+{t.currency} {t.taxAmount.toLocaleString()}</span>
                                                  </div>
                                                ))}
                                                <div className="flex items-center justify-between font-medium text-gray-800">
                                                  <span>Total payable</span>
                                                  <span>{subProduct.baseFee.currency} {totalWithMembershipAndTax.toLocaleString()}</span>
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                  );
                                })}
                              </div>
                              {errors.selectedPlan && (
                                <p className="mt-2 text-xs text-red-600">{errors.selectedPlan}</p>
                              )}
                            </div>

                            {/* First Installment Amount */}
                            {selectedPlan && (
                              <>
                                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                                  <p className="text-sm text-gray-700 mb-2 font-medium">Amount To Pay (First Installment)</p>
                                  <p className="text-3xl font-bold text-indigo-600">
                                    {(() => {
                                      const registrationFee = getRegistrationFeeForPaymentContext();
                                      const membershipFee = getMembershipFeeForPaymentContext();
                                      let first = Number(selectedPlan.installments?.[0]?.amount || 0);
                                      if (hasScholarship && (discountQuote?.isScholarship || discountQuote?.appliedDiscount?.kind === 'scholarship') && Number(discountQuote?.discountAmount) > 0) {
                                        const totalInstSum = (selectedPlan.installments || []).reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
                                        if (totalInstSum > 0) {
                                          const share = Math.min(first, roundCurrency(discountQuote.discountAmount * (first / totalInstSum)));
                                          first = Math.max(0, roundCurrency(first - share));
                                        }
                                      }
                                      const adjusted = first + (registrationFee > 0 ? registrationFee : 0) + (membershipFee > 0 ? membershipFee : 0) + (taxInfo?.totalTaxAmount || 0);
                                      return `${subProduct.baseFee.currency} ${adjusted.toLocaleString()}`;
                                    })()}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-2">
                                    {selectedPlan.installments[0].description}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    Base installment: {subProduct.baseFee.currency} {(() => {
                                      let first = Number(selectedPlan.installments?.[0]?.amount || 0);
                                      if (hasScholarship && (discountQuote?.isScholarship || discountQuote?.appliedDiscount?.kind === 'scholarship') && Number(discountQuote?.discountAmount) > 0) {
                                        const totalInstSum = (selectedPlan.installments || []).reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
                                        if (totalInstSum > 0) {
                                          const share = Math.min(first, roundCurrency(discountQuote.discountAmount * (first / totalInstSum)));
                                          first = Math.max(0, roundCurrency(first - share));
                                        }
                                      }
                                      return first.toLocaleString();
                                    })()}
                                  </p>
                                  {getRegistrationFeeForPaymentContext() > 0 && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Includes registration fee: +{registrationFeeCurrency} {getRegistrationFeeForPaymentContext().toLocaleString()}
                                    </p>
                                  )}
                                  {getMembershipFeeForPaymentContext() > 0 && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Includes membership fee: +{membershipFeeCurrency} {getMembershipFeeForPaymentContext().toLocaleString()}
                                    </p>
                                  )}
                                  {taxInfo && taxInfo.totalTaxAmount > 0 && taxInfo.taxes.map((t, i) => (
                                    <p key={i} className="text-xs text-indigo-700 mt-1 font-medium">
                                      {t.name} ({t.percentage}%): +{t.currency} {t.taxAmount.toLocaleString()}
                                      <span className="text-gray-500 font-normal ml-1">(applied to first installment)</span>
                                    </p>
                                  ))}
                                </div>
                                
                              </>
                            )}
                          </>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                            <p className="text-yellow-900 font-medium mb-1">Installment plans are not available right now</p>
                            <p className="text-sm text-yellow-800">Please select "Pay Full Amount" option to continue</p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-yellow-900 font-medium">Module information is not available</p>
                    <p className="text-sm text-yellow-800 mt-1">Please contact the institution for module details</p>
                  </div>
                )}
                  </>
                )}

                {/* Discounts */}
                {!paymentSkipped && !cardPaymentInfo && (!isFullScholarship || !!codeScholarship || !!appliedDiscountCode) && discountsAllowedForCurrentPayment && (
                  (isModuleBased ? selectedModules.length > 0 : !!subProduct?.baseFee?.amount)
                ) && (
                  <div className="mt-4 bg-white border border-gray-300 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900">Discounts</h4>
                      {discountLoading && (
                        <span className="text-xs text-gray-500">Applying...</span>
                      )}
                    </div>

                    {isModuleBased && modulePaymentMode === 'installment' && (
                      <p className="mt-2 text-xs text-gray-600">
                        Discount (if any) is applied only to the last module installment.
                      </p>
                    )}

                    {!isModuleBased && paymentType === 'installment' && (
                      <p className="mt-2 text-xs text-gray-600">
                        Discount (if any) is applied to the last installment.
                      </p>
                    )}

                    {(hasScholarship && !codeScholarship && !appliedDiscountCode) ? (
                      <div className="mt-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50/60 rounded-lg border border-indigo-200">
                        <p className="text-xs font-semibold text-indigo-900">
                          Scholarship Awarded ({activeScholarship?.schemeName || 'Institutional Scholarship'})
                        </p>
                        <p className="text-xs text-indigo-700 mt-0.5">
                          Institutional scholarship terms are active. Additional promotional codes cannot be combined with scholarships.
                        </p>
                      </div>
                    ) : (<>
                    {isEarlyBirdAvailable && !hasScholarship && (
                      <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-md p-3">
                        <div className="flex justify-between items-start gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Early bird discount applied automatically
                            </p>
                            {(discountQuote?.appliedDiscount?.name || activeEarlyBirdDiscount?.name) && (
                              <p className="text-xs text-gray-700 mt-1">
                                {discountQuote?.appliedDiscount?.name || activeEarlyBirdDiscount?.name}
                              </p>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                              Other discount codes are disabled while early bird is active (a scholarship code can still be entered below).
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setIsEarlyBirdCleared(true);
                              fetchDiscountQuote({ codeToApply: appliedDiscountCode, skipEarlyBirdOverride: true });
                            }}
                            className="text-xs font-medium text-indigo-700 hover:text-indigo-900 whitespace-nowrap"
                          >
                            Remove early bird
                          </button>
                        </div>
                      </div>
                    )}
                    {(
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {(isEarlyBirdAvailable || hasScholarship) ? 'Scholarship / Discount Code' : 'Discount Code'}
                        </label>
                        <div className="flex flex-col md:flex-row gap-2">
                          <input
                            type="text"
                            value={discountCodeInput}
                            onChange={(e) => {
                              setDiscountCodeInput(e.target.value);
                              setDiscountError('');
                            }}
                            disabled={discountLoading || !!cardPaymentInfo}
                            className="w-full md:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60"
                            placeholder="Enter discount code"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleApplyDiscountCode}
                              disabled={discountLoading || !!cardPaymentInfo || !discountCodeInput.trim()}
                              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Apply
                            </button>
                            {(appliedDiscountCode || discountQuote?.appliedDiscount?.code) && (
                              <button
                                type="button"
                                onClick={handleClearDiscountCode}
                                disabled={discountLoading || !!cardPaymentInfo}
                                className="px-4 py-2 bg-gray-100 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>

                        {isEarlyBirdCleared && (
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => {
                                setIsEarlyBirdCleared(false);
                                setAppliedDiscountCode(null);
                                setDiscountCodeInput('');
                                fetchDiscountQuote({ codeToApply: null, skipEarlyBirdOverride: false });
                              }}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              &larr; Revert to Early Bird discount
                            </button>
                          </div>
                        )}
                        {discountError && (
                          <p className="mt-2 text-xs text-red-600">{discountError}</p>
                        )}

                        {codeScholarship && (
                          <p className="mt-2 text-xs font-medium text-emerald-700" data-testid="scholarship-code-applied">
                            Scholarship code applied: {codeScholarship.schemeName || 'Scholarship'}
                          </p>
                        )}
                        {discountQuote?.appliedDiscount?.kind && (
                          <div className="mt-2 text-xs text-gray-700">
                            <p>
                              Applied:{' '}
                              <span className="font-medium">
                                {discountQuote.appliedDiscount.kind === 'general_discount'
                                  ? 'General discount'
                                  : discountQuote.appliedDiscount.kind === 'special_discount'
                                    ? 'Special discount'
                                    : discountQuote.appliedDiscount.kind === 'scholarship'
                                      ? 'Scholarship'
                                      : discountQuote.appliedDiscount.kind}
                              </span>
                              {discountQuote.appliedDiscount.code
                                ? ` (Code: ${discountQuote.appliedDiscount.code})`
                                : ''}
                            </p>
                            {(discountQuote.appliedDiscount.remainingUses !== undefined ||
                              discountQuote.appliedDiscount.remainingCodes !== undefined) && (
                              <p className="text-gray-600 mt-1">
                                {discountQuote.appliedDiscount.remainingUses !== undefined
                                  ? `Remaining uses: ${discountQuote.appliedDiscount.remainingUses}`
                                  : null}
                                {discountQuote.appliedDiscount.remainingUses !== undefined &&
                                discountQuote.appliedDiscount.remainingCodes !== undefined
                                  ? ' • '
                                  : null}
                                {discountQuote.appliedDiscount.remainingCodes !== undefined
                                  ? `Remaining codes: ${discountQuote.appliedDiscount.remainingCodes}`
                                  : null}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    </>)}

                    {discountQuote && discountQuote.appliedDiscount && discountQuote.appliedDiscount.kind !== 'full_payment_concession' && !(!isModuleBased && paymentType === 'installment') && (
                      <div className="mt-3 pt-3 border-t border-gray-200 text-sm">
                        <div className="flex items-center justify-between text-gray-700">
                          <span>Base amount</span>
                          <span className="font-medium">
                            {discountCurrency} {Number(discountQuote.baseAmount || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-gray-700 mt-1">
                          <span>Discount</span>
                          <span className="font-medium">
                            -{discountCurrency} {Number(discountQuote.discountAmount || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-gray-900 mt-2">
                          <span className="font-semibold">Final amount</span>
                          <span className="font-semibold text-indigo-600">
                            {discountCurrency} {Number(discountQuote.finalAmount || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Amount To Pay Summary (module-based) */}
                {!paymentSkipped && !cardPaymentInfo && isModuleBased && (
                  <div className="mt-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-200">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Invoice Summary</p>
                    {selectedModules.length === 0 ? (
                      <>
                        <p className="text-2xl font-bold text-indigo-400">{discountCurrency} 0</p>
                        <p className="text-xs text-gray-500 mt-1">Select modules to see the total amount</p>
                        <p className="text-xs text-red-600 mt-1 font-medium">* Please select at least one module to continue</p>
                      </>
                    ) : (() => {
                      const A = calculateModuleTotal();
                      const B = isFullScholarship ? 0 : getRegistrationFeeForPaymentContext();
                      const C = isFullScholarship ? 0 : getMembershipFeeForPaymentContext();
                      const D = isFullScholarship ? 0 : getInternationalPaymentFeeForPaymentContext();
                      const E = isFullScholarship
                        ? roundCurrency(A + (registrationFeeAmountBase || 0) + (membershipFeeAmountBase || 0) + (internationalPaymentFeeAmountBase || 0))
                        : Number(discountQuote?.discountAmount || 0);
                      const F = isFullScholarship
                        ? 0
                        : (modulePaymentMode === 'installment' && moduleInstallmentPreview
                            ? moduleInstallmentPreview.subtotalAfterDiscount
                            : (taxInfo?.subtotalAfterDiscount || getMaxPaymentAmount()));
                      const G = isFullScholarship
                        ? 0
                        : (modulePaymentMode === 'installment' && moduleInstallmentPreview
                            ? moduleInstallmentPreview.totalExclusiveTaxAmount
                            : (taxInfo?.totalTaxAmount || 0));
                      const total = isFullScholarship ? 0 : (taxInfo?.totalInvoiceAmount || (F + G));
                      const hasExtras = isFullScholarship || E > 0 || B > 0 || C > 0 || D > 0 || internationalPaymentFeeAmountBase > 0 || registrationFeeAmountBase > 0;
                      return (
                        <>
                          <div className="space-y-1.5 text-sm">
                            {/* A: Module fees */}
                            <div className="flex justify-between text-gray-700">
                              {(() => {
                                const resolvedSelections = selectedModules.map((code) => resolveAssignedModule({ code }));
                                const allRepeat = resolvedSelections.length > 0 && resolvedSelections.every((m) => isRepeatFeeModule(m));
                                const anyRepeat = resolvedSelections.some((m) => isRepeatFeeModule(m));
                                const label = allRepeat
                                  ? `Repeat fees (${selectedModules.length} module${selectedModules.length !== 1 ? 's' : ''})`
                                  : anyRepeat
                                    ? `Module & Repeat fees (${selectedModules.length} module${selectedModules.length !== 1 ? 's' : ''})`
                                    : `Module fees (${selectedModules.length} module${selectedModules.length !== 1 ? 's' : ''})`;
                                return <span>{label}</span>;
                              })()}
                              <span className="font-medium tabular-nums">{discountCurrency} {A.toLocaleString()}</span>
                            </div>
                            {/* -E: Discount or Full Scholarship */}
                            {isFullScholarship ? (
                              <>
                                {registrationFeeAmountBase > 0 && (
                                  <div className="flex justify-between text-gray-700">
                                    <span>Registration fee</span>
                                    <span className="tabular-nums">+{registrationFeeCurrency} {registrationFeeAmountBase.toLocaleString()}</span>
                                  </div>
                                )}
                                {membershipFeeAmountBase > 0 && (
                                  <div className="flex justify-between text-gray-700">
                                    <span>Membership fee</span>
                                    <span className="tabular-nums">+{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                  </div>
                                )}
                                {internationalPaymentFeeAmountBase > 0 && (
                                  <div className="flex justify-between text-gray-700">
                                    <span>International payment fee</span>
                                    <span className="tabular-nums">+{internationalPaymentFeeCurrency} {internationalPaymentFeeAmountBase.toLocaleString()}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-green-700 font-medium">
                                  <span>Scholarship ({activeScholarship?.schemeName || 'Institutional Scholarship'} — 100% Full Waiver)</span>
                                  <span className="tabular-nums">-{discountCurrency} {E.toLocaleString()}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                {E > 0 && (
                                  <div className="flex justify-between text-green-700">
                                    <span>
                                      {discountQuote?.appliedDiscount?.kind === 'scholarship' ?
                                        `Scholarship (${discountQuote.appliedDiscount.name || 'Institutional Grant'})` :
                                        discountQuote?.appliedDiscount?.kind === 'early_bird' ? 'Early bird discount' :
                                        (discountQuote?.appliedDiscount?.name || 'Discount')}
                                      {discountQuote?.appliedDiscount?.discountType === 'percentage' ?
                                        ` (${discountQuote.appliedDiscount.value}%)` : ''}
                                    </span>
                                    <span className="font-medium tabular-nums">-{discountCurrency} {E.toLocaleString()}</span>
                                  </div>
                                )}
                                {renderRegistrationFeeSummaryLine(B)}
                              </>
                            )}
                            {/* +C: Membership fee (stage-band aware when preview available) */}
                            {!isFullScholarship && (
                              membershipFeeBands.length > 0 ? (
                                <div className="rounded-md border border-indigo-100 bg-white/70 px-2.5 py-2">
                                  <MembershipFeeBandsSummary
                                    bands={membershipFeeBands}
                                    currency={membershipFeeCurrency}
                                    compact
                                  />
                                </div>
                              ) : membershipFeeAmountBase > 0 && (
                                membershipFeeIsIncluded ? (
                                  <div className="flex justify-between text-xs text-gray-400 italic">
                                    <span>Membership fee <em>(already included in course fee)</em></span>
                                    <span className="tabular-nums">{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                  </div>
                                ) : (
                                  <div className="flex justify-between text-gray-700">
                                    <span>Membership fee</span>
                                    <span className="font-medium tabular-nums">+{membershipFeeCurrency} {C.toLocaleString()}</span>
                                  </div>
                                )
                              )
                            )}
                            {/* +D: International payment fee */}
                            {!isFullScholarship && internationalPaymentFeeAmountBase > 0 && (
                              (scholarshipCoversInt && !(D > 0)) ? (
                                <div className="flex justify-between text-gray-500 italic">
                                  <span>International payment fee <em>(waived — scholarship)</em></span>
                                  <span className="font-medium tabular-nums">{internationalPaymentFeeCurrency} {internationalPaymentFeeAmountBase.toLocaleString()}</span>
                                </div>
                              ) : (
                                <div className="flex justify-between text-gray-700">
                                  <span>International payment fee</span>
                                  <span className="font-medium tabular-nums">+{internationalPaymentFeeCurrency} {D.toLocaleString()}</span>
                                </div>
                              )
                            )}
                            {/* Subtotal F */}
                            {hasExtras && (
                              <>
                                <div className="border-t border-indigo-200 pt-1 flex justify-between text-gray-800 font-semibold">
                                  <span>Subtotal</span>
                                  <span className="tabular-nums">{discountCurrency} {F.toLocaleString()}</span>
                                </div>
                              </>
                            )}
                            {/* Inclusive taxes (informational only) */}
                            {taxInfo?.inclusiveTaxes?.map((t, i) => (
                              <div key={i} className="flex justify-between text-xs text-gray-500">
                                <span>{t.name} ({t.percentage}%) <em>included in subtotal</em></span>
                                <span className="tabular-nums">{t.currency} {t.taxAmount.toLocaleString()}</span>
                              </div>
                            ))}
                            {/* +G: Exclusive taxes */}
                            {taxInfo?.exclusiveTaxes?.map((t, i) => (
                              <div key={i} className="flex justify-between text-indigo-700">
                                <span>{t.name} ({t.percentage}%)</span>
                                <span className="font-medium tabular-nums">+{t.currency} {t.taxAmount.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          {/* Total Invoice */}
                          <div className="mt-3 pt-3 border-t-2 border-indigo-300 flex justify-between items-baseline">
                            <span className="text-sm font-semibold text-gray-800">Total Invoice</span>
                            <span className="text-2xl font-bold text-indigo-600 tabular-nums">
                              {discountCurrency} {total.toLocaleString()}
                            </span>
                          </div>
                          {!isFullScholarship && modulePaymentMode === 'installment' && moduleInstallmentPreview && (
                            <div className="mt-4 pt-4 border-t border-indigo-200">
                              <p className="text-sm font-semibold text-gray-800 mb-2">Installment Schedule</p>
                              <div className="space-y-2">
                                {moduleInstallmentPreview.installments.map((installment) => (
                                  <div key={installment.installmentNumber} className="rounded-lg border border-indigo-100 bg-white/70 p-3">
                                    <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
                                      <span>{installment.description}</span>
                                      <span>{discountCurrency} {installment.totalDue.toLocaleString()}</span>
                                    </div>
                                    <div className="mt-1 space-y-0.5 text-xs text-gray-600">
                                      <div className="flex justify-between">
                                        <span>Base amount before discount</span>
                                        <span>{discountCurrency} {installment.preDiscountAmount.toLocaleString()}</span>
                                      </div>
                                      {installment.discountAmount > 0 && (
                                        <div className="flex justify-between text-green-700">
                                          <span>Discount on this installment</span>
                                          <span>-{discountCurrency} {installment.discountAmount.toLocaleString()}</span>
                                        </div>
                                      )}
                                      {installment.taxLines.map((taxLine, index) => (
                                        <div
                                          key={`${installment.installmentNumber}-${taxLine.name}-${index}`}
                                          className={`flex justify-between ${taxLine.included ? 'text-gray-500 italic' : 'text-indigo-700'}`}
                                        >
                                          <span>{taxLine.name} ({taxLine.percentage}%) {taxLine.included ? '(included)' : ''}</span>
                                          <span>{taxLine.included ? '' : '+'}{taxLine.currency} {taxLine.taxAmount.toLocaleString()}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Scholarship proof / agreement upload (mandatory in the public form) */}
                {hasScholarship && !cardPaymentInfo && (
                  <div className="mt-4 bg-emerald-50/60 border border-emerald-200 rounded-lg p-4" data-testid="scholarship-proof-section">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Scholarship proof / agreement <span className="text-red-500">*</span>
                    </h4>
                    <p className="mt-1 text-xs text-gray-600">
                      Upload the document that supports your scholarship ({activeScholarship?.schemeName || 'Scholarship'}), for example the signed scholarship agreement or award letter. Your application cannot be submitted without it.
                    </p>
                    <div className="mt-3">
                      <ProofAttachmentDropzone
                        file={scholarshipProof}
                        busy={uploadingScholarshipProof}
                        required
                        label="Scholarship document"
                        hint={null}
                        onChange={async (file) => {
                          if (!file) {
                            setScholarshipProof(null);
                            return;
                          }
                          setUploadingScholarshipProof(true);
                          try {
                            const response = await fileApi.uploadDocumentToApplication(application._id, file, {
                              category: 'Other',
                              title: 'Scholarship Proof / Agreement',
                              description: 'Scholarship proof / agreement document'
                            });
                            const uploadedFile = response.data?.data || response.data;
                            const originalName = uploadedFile.originalName || file.name;
                            setScholarshipProof({
                              fileId: uploadedFile.fileId,
                              fileName: uploadedFile.fileName || file.name,
                              originalName,
                              displayName: originalName || uploadedFile.fileName || file.name,
                              fileSize: uploadedFile.fileSize || file.size,
                              mimeType: uploadedFile.mimeType || uploadedFile.fileType || file.type,
                              _id: uploadedFile._id || uploadedFile.id
                            });
                            setErrors((prev) => ({ ...prev, scholarshipProof: undefined }));
                            toast.success('Scholarship document uploaded successfully');
                          } catch (error) {
                            console.error('Scholarship proof upload error:', error);
                            toast.error(error.response?.data?.message || 'Failed to upload scholarship document');
                          } finally {
                            setUploadingScholarshipProof(false);
                          }
                        }}
                      />
                      {errors.scholarshipProof && (
                        <p className="mt-2 text-xs text-red-600">{errors.scholarshipProof}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Amount To Pay Summary (product-based full payment or full scholarship) */}
                {!paymentSkipped && !cardPaymentInfo && !isModuleBased && (paymentType === 'full' || isFullScholarship) && (subProduct?.baseFee || mainProduct?.fee) && (
                  <div className="mt-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-200">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Invoice Summary</p>
                    {(() => {
                      const A = Number(
                        (discountQuote?.appliedDiscount && discountQuote.appliedDiscount.kind !== 'full_payment_concession' && discountQuote?.baseAmount)
                          ? discountQuote.baseAmount
                          : ((subProduct?.baseFee?.amount != null && Number(subProduct.baseFee.amount) > 0)
                              ? subProduct.baseFee.amount
                              : (mainProduct?.fee?.amount || 0))
                      );
                      const B = isFullScholarship ? 0 : getRegistrationFeeForPaymentContext();
                      const C = isFullScholarship ? 0 : getMembershipFeeForPaymentContext();
                      const E = isFullScholarship
                        ? roundCurrency(A + (registrationFeeAmountBase || 0) + (membershipFeeAmountBase || 0))
                        : Number((discountQuote?.appliedDiscount && discountQuote.appliedDiscount.kind !== 'full_payment_concession') ? (discountQuote?.discountAmount || 0) : 0);
                      const F = isFullScholarship ? 0 : (taxInfo?.subtotalAfterDiscount ?? getMaxPaymentAmount());
                      const G = isFullScholarship ? 0 : (taxInfo?.totalTaxAmount || 0);
                      const total = isFullScholarship ? 0 : (taxInfo?.totalInvoiceAmount || (F + G));
                      const hasExtras = isFullScholarship || E > 0 || B > 0 || C > 0 || registrationFeeAmountBase > 0 || membershipFeeAmountBase > 0;
                      return (
                        <>
                          <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between text-gray-700">
                              <span>Course fee</span>
                              <span className="font-medium tabular-nums">{subProduct?.baseFee?.currency || discountCurrency} {A.toLocaleString()}</span>
                            </div>
                            {isFullScholarship ? (
                              <>
                                {registrationFeeAmountBase > 0 && (
                                  <div className="flex justify-between text-gray-700">
                                    <span>Registration fee</span>
                                    <span className="tabular-nums">+{registrationFeeCurrency} {registrationFeeAmountBase.toLocaleString()}</span>
                                  </div>
                                )}
                                {membershipFeeAmountBase > 0 && (
                                  <div className="flex justify-between text-gray-700">
                                    <span>Membership fee</span>
                                    <span className="tabular-nums">+{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-green-700 font-medium">
                                  <span>Scholarship ({activeScholarship?.schemeName || 'Institutional Scholarship'} — 100% Full Waiver)</span>
                                  <span className="tabular-nums">-{discountCurrency} {E.toLocaleString()}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                {E > 0 && (
                                  <div className="flex justify-between text-green-700">
                                    <span>
                                      {discountQuote?.appliedDiscount?.kind === 'scholarship' ?
                                        `Scholarship (${discountQuote.appliedDiscount.name || 'Institutional Grant'})` :
                                        discountQuote?.appliedDiscount?.kind === 'early_bird' ? 'Early bird discount' :
                                        (discountQuote?.appliedDiscount?.name || 'Discount')}
                                      {discountQuote?.appliedDiscount?.discountType === 'percentage' ?
                                        ` (${discountQuote.appliedDiscount.value}%)` : ''}
                                    </span>
                                    <span className="font-medium tabular-nums">-{discountCurrency} {E.toLocaleString()}</span>
                                  </div>
                                )}
                                {renderRegistrationFeeSummaryLine(B)}
                                {membershipFeeAmountBase > 0 && (
                                  (scholarshipCoversMem && !(C > 0)) ? (
                                    <div className="flex justify-between text-gray-500 italic">
                                      <span>Membership fee <em>(waived — scholarship)</em></span>
                                      <span className="tabular-nums">{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                    </div>
                                  ) : membershipFeeIsIncluded ? (
                                    <div className="flex justify-between text-xs text-gray-400 italic">
                                      <span>Membership fee <em>(already included in course fee)</em></span>
                                      <span className="tabular-nums">{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                    </div>
                                  ) : (
                                    <div className="flex justify-between text-gray-700">
                                      <span>Membership fee</span>
                                      <span className="font-medium tabular-nums">+{membershipFeeCurrency} {C.toLocaleString()}</span>
                                    </div>
                                  )
                                )}
                              </>
                            )}
                            {hasExtras && (
                              <div className="border-t border-indigo-200 pt-1 flex justify-between text-gray-800 font-semibold">
                                <span>Subtotal</span>
                                <span className="tabular-nums">{discountCurrency} {F.toLocaleString()}</span>
                              </div>
                            )}
                            {!isFullScholarship && taxInfo?.inclusiveTaxes?.map((t, i) => (
                              <div key={i} className="flex justify-between text-xs text-gray-500">
                                <span>{t.name} ({t.percentage}%) <em>included in subtotal</em></span>
                                <span className="tabular-nums">{t.currency} {t.taxAmount.toLocaleString()}</span>
                              </div>
                            ))}
                            {!isFullScholarship && taxInfo?.exclusiveTaxes?.map((t, i) => (
                              <div key={i} className="flex justify-between text-indigo-700">
                                <span>{t.name} ({t.percentage}%)</span>
                                <span className="font-medium tabular-nums">+{t.currency} {t.taxAmount.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 pt-3 border-t-2 border-indigo-300 flex justify-between items-baseline">
                            <span className="text-sm font-semibold text-gray-800">Total Invoice</span>
                            <span className="text-2xl font-bold text-indigo-600 tabular-nums">
                              {discountCurrency} {total.toLocaleString()}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Desired Payment Amount (shown after discounts) */}
                {!paymentSkipped && !cardPaymentInfo && !isFullScholarship && (
                  ((isModuleBased && selectedModules.length > 0) ||
                    (!isModuleBased && paymentType === 'full' && subProduct.baseFee) ||
                    (!isModuleBased && paymentType === 'installment' && selectedPlan)) && (
                    <div className="mt-4 bg-white border border-gray-300 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Desired Payment Amount <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 font-medium z-10">
                          {isModuleBased || paymentType === 'full'
                            ? discountCurrency
                            : (subProduct.baseFee?.currency || 'LKR')}
                        </span>
                        <input
                          type="number"
                          value={desiredPaymentAmount}
                          onChange={(e) => {
                            // Don't clamp on each keystroke; it prevents typing values like 10000
                            // when a min/max (e.g. membership fee / installment) is present.
                            desiredAmountTouchedRef.current = true;
                            const parsed = parseFloat(e.target.value);
                            setDesiredPaymentAmount(Number.isFinite(parsed) ? parsed : 0);
                          }}
                          onBlur={(e) => {
                            const parsed = parseFloat(e.target.value);
                            if (!Number.isFinite(parsed)) return;
                            const maxAmount = getMaxPaymentAmount() + getCurrentPaymentTaxAmount();
                            const minAmount = getMinPaymentAmount();
                            const clamped = Math.min(Math.max(minAmount, parsed), maxAmount);
                            if (clamped !== parsed) {
                              notifyPaymentAmountAdjusted({ from: parsed, to: clamped, minAmount, maxAmount });
                            }
                            setDesiredPaymentAmount(clamped);
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          min={getMinPaymentAmount()}
                          step="0.01"
                          required
                          className="w-full pl-16 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="0.00"
                        />
                      </div>
                      {getMinPaymentAmount() > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Minimum: {membershipFeeCurrency} {getMinPaymentAmount().toLocaleString()} (required fees)
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {isInstallmentDueContext() ? 'Current installment due:' : 'Total Invoice:'}{' '}
                        <span className="font-medium">
                          {isModuleBased || paymentType === 'full'
                            ? `${discountCurrency} ${getCurrentInstallmentDueAmount().toLocaleString()}`
                            : `${subProduct.baseFee?.currency || 'LKR'} ${getCurrentInstallmentDueAmount().toLocaleString()}`}
                        </span>
                        {' '}{isInstallmentDueContext() ? '(first installment)' : '(maximum)'}
                      </p>
                      {!isModuleBased && paymentType === 'installment' && selectedPlan && (
                        <p className="text-xs text-gray-500 mt-1">
                          Overall plan total:{' '}
                          <span className="font-medium">
                            {`${subProduct.baseFee?.currency || 'LKR'} ${getOverallPlannedPayableAmount().toLocaleString()}`}
                          </span>
                        </p>
                      )}
                      {errors.desiredPaymentAmount && (
                        <p className="mt-1 text-xs text-red-600">{errors.desiredPaymentAmount}</p>
                      )}
                    </div>
                  )
                )}

                {/* Common Sections - Payment Method selection, Bank Transfer details, and optional Card payment */}
                {isFullScholarship ? (
                  <div className="bg-gradient-to-br from-indigo-50 via-purple-50/40 to-white border border-indigo-200/90 rounded-xl p-6 text-center shadow-sm">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 text-white text-lg font-bold shadow-md shadow-indigo-200">
                      ✓
                    </div>
                    <h3 className="text-base font-bold text-gray-900">
                      100% Full Scholarship Applied
                    </h3>
                    <p className="text-xs text-indigo-700 mt-1 max-w-md mx-auto">
                      All tuition and fees for this program are completely waived under the institutional scholarship grant. No payment is required: upload your scholarship document above and submit. Our team will verify the document before your application moves on.
                    </p>
                    <p className="text-xs text-gray-500 mt-3 font-medium">
                      Please click &quot;Next&quot; below to review your information and submit your application.
                    </p>
                  </div>
                ) : !cardPaymentInfo && (
                  ((isModuleBased && selectedModules.length > 0) ||
                    (!isModuleBased && !paymentSkipped && (
                      (paymentType === 'full' && Number(subProduct?.baseFee?.amount || 0) > 0) ||
                      (paymentType === 'installment' && !!selectedPlan) ||
                      getMembershipFeeForPaymentContext() > 0
                    ))) ? (
                  <>
                    {/* Payment Method Switch */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Method <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => !cardPaymentInfo && setPublicPaymentMethod('bank')}
                          className={`px-4 py-3 border-2 rounded-lg text-sm font-medium flex items-center justify-between ${
                            publicPaymentMethod === 'bank'
                              ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                              : 'border-gray-300 hover:border-indigo-400'
                          } ${cardPaymentInfo ? 'opacity-60 cursor-not-allowed' : ''}`}
                          disabled={!!cardPaymentInfo}
                        >
                          <span className="text-gray-900">Bank Transfer</span>
                          <span className="text-xs text-gray-500 ml-2">Upload bank receipt</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => !cardPaymentInfo && setPublicPaymentMethod('card')}
                          className={`px-4 py-3 border-2 rounded-lg text-sm font-medium flex items-center justify-between opacity-60 cursor-not-allowed ${
                            publicPaymentMethod === 'card'
                              ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                              : 'border-gray-300'
                          }`}
                          disabled
                          title="Card payment is currently unavailable"
                        >
                          <span className="text-gray-900">Card Payment (Credit / Debit)</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {cardPaymentInfo ? 'Payment completed via card' : 'Pay via secure gateway'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Bank Transfer Details & Receipt Upload */}
                    {publicPaymentMethod === 'bank' && !cardPaymentInfo && (
                      <>
                        <div className="bg-yellow-100 border border-yellow-300 rounded px-3 py-2 inline-block">
                          <span className="text-xs font-semibold text-yellow-900">REFERENCE NUMBER (Include in payment): </span>
                          <span className="text-sm font-mono font-bold text-yellow-900">{application.applicationNumber}</span>
                        </div>

                        <div className="border border-gray-300 rounded-lg p-3">
                          <div className="flex items-start">
                            <svg className="h-5 w-5 text-gray-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900 mb-1">Bank Transfer Details</h4>
                              {application.bankAccountInfo?.accountNumber ? (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                  <div><span className="text-gray-600">Bank:</span> <span className="font-medium">{application.bankAccountInfo.bankName}</span></div>
                                  <div><span className="text-gray-600">Branch:</span> <span className="font-medium">{application.bankAccountInfo.branchName}</span></div>
                                  <div><span className="text-gray-600">Account Name:</span> <span className="font-medium">{application.bankAccountInfo.accountHolderName || application.bankAccountInfo.accountName}</span></div>
                                  <div><span className="text-gray-600">Account No:</span> <span className="font-mono font-medium">{application.bankAccountInfo.accountNumber}</span></div>
                                </div>
                              ) : (
                                <p className="text-xs text-amber-700">Bank transfer details not yet configured. Please contact the institution for payment instructions.</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Payment Receipt Upload */}
                        <ProofAttachmentDropzone
                          file={paymentReceipt}
                          busy={uploadingReceipt}
                          required
                          hint={null}
                          onChange={async (file) => {
                            if (!file) {
                              setPaymentReceipt(null);
                              return;
                            }
                            setUploadingReceipt(true);
                            try {
                              const metadata = {
                                category: 'Payment Receipt',
                                title: isModuleBased
                                  ? `Payment Receipt - Program Payment (${selectedModules.length} modules)`
                                  : 'Payment Receipt - Program Payment',
                                description: isModuleBased
                                  ? `Program payment receipt for ${discountCurrency} ${Number(desiredPaymentAmount || 0).toLocaleString()} (${selectedModules.length} selected modules)`
                                  : `Program payment receipt for ${discountCurrency} ${Number(desiredPaymentAmount || 0).toLocaleString()}`
                              };
                              const response = await fileApi.uploadDocumentToApplication(application._id, file, metadata);
                              const uploadedFile = response.data?.data || response.data;
                              const originalName = uploadedFile.originalName || file.name;
                              const displayName = originalName || uploadedFile.fileName || file.name;

                              setPaymentReceipt({
                                fileId: uploadedFile.fileId,
                                fileName: uploadedFile.fileName || file.name,
                                originalName,
                                displayName,
                                fileSize: uploadedFile.fileSize || file.size,
                                fileType: uploadedFile.mimeType || uploadedFile.fileType || file.type,
                                _id: uploadedFile._id || uploadedFile.id
                              });

                              toast.success('Payment receipt uploaded successfully');
                            } catch (error) {
                              console.error('Receipt upload error:', error);
                              toast.error(error.response?.data?.message || 'Failed to upload receipt');
                            } finally {
                              setUploadingReceipt(false);
                            }
                          }}
                        />
                      </>
                    )}

                    {/* Card Payment Helper */}
                    {publicPaymentMethod === 'card' && (
                      <div className="mt-2 space-y-3">
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {cardPaymentInfo ? 'Card Payment Completed' : 'Card Payment Amount'}
                          </p>
                          <p className="text-2xl font-bold text-indigo-600">
                            {(cardPaymentInfo?.currency || 'LKR')}{' '}
                            {Number(cardPaymentInfo?.amount || desiredPaymentAmount || 0).toLocaleString()}
                          </p>
                          {cardPaymentInfo ? (
                            <p className="text-xs text-green-700 mt-1">
                              ✅ Paid via card
                              {cardPaymentInfo.referenceNumber
                                ? ` (Ref: ${cardPaymentInfo.referenceNumber})`
                                : ''}{' '}
                              {cardPaymentInfo.date
                                ? `on ${new Date(cardPaymentInfo.date).toLocaleDateString()}`
                                : ''}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-600 mt-1">
                              You will be redirected to a secure card payment gateway to complete this payment.
                            </p>
                          )}
                        </div>
                        {!cardPaymentInfo && (
                          <>
                            {hasStoredCardAgreement ? (
                              <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                                <p className="text-sm font-medium text-green-900">
                                  Agreement already accepted for card payment
                                </p>
                                <p className="mt-1 text-xs text-green-800">
                                  You have already agreed to the non-refundable payment terms for this application.
                                  {storedCardAgreementAcceptedAt && (
                                    <>
                                      {' '}Accepted on {storedCardAgreementAcceptedAt.toLocaleString()}.
                                    </>
                                  )}
                                </p>
                              </div>
                            ) : (
                              <div className={`border rounded-md p-4 ${cardTermsAgreed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={cardTermsAgreed}
                                    onChange={(e) => setCardTermsAgreed(e.target.checked)}
                                    className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                  />
                                  <span className="text-sm text-gray-800">
                                    <strong>I understand and agree</strong> that all payments made towards this application and enrollment are{' '}
                                    <strong>strictly non-refundable</strong>. By continuing to card payment, I confirm that I accept the{' '}
                                    <button type="button" onClick={() => setShowEnrollmentTermsModal(true)} className="text-indigo-600 underline hover:text-indigo-800 font-medium focus:outline-none">
                                      terms and conditions of enrollment
                                    </button>.
                                  </span>
                                </label>
                                {!cardTermsAgreed && (
                                  <p className="mt-2 text-xs text-red-600 font-medium">
                                    * You must agree to this before paying by card.
                                  </p>
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={handleCardPayment}
                              disabled={cardProcessing || !desiredPaymentAmount || hasPendingExemptions || (!cardTermsAgreed && !hasStoredCardAgreement)}
                              className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white text-base font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {cardProcessing ? 'Redirecting...' : 'Pay Now by Card'}
                            </button>
                            {hasPendingExemptions && (
                              <p className="text-xs text-red-600">
                                Card payment is unavailable while module exemption requests are awaiting review.
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              You can also choose Bank Transfer above and upload a receipt instead of paying by card.
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </>
                  ) : (
                    // Show "No payment required" only for non‑module programs with
                    // zero payable total. For module‑based flows (when no modules
                    // are selected yet) we don't show anything here.
                    (!isModuleBased && (Number(subProduct?.baseFee?.amount || 0) <= 0) && (membershipFeeAmountBase <= 0)) ? (
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
                        <svg className="w-12 h-12 text-green-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-medium text-green-800">
                          No payment required for this program.
                        </p>
                      </div>
                    ) : null
                  )
                )}
              </div>
            )}

            {/* Step 5: Final Review & Submit */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Review & Submit</h2>
                
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h3 className="text-lg font-medium text-green-800 mb-2">Application Summary</h3>
                  {hasScholarship && (
                    <div className="p-3 bg-white/90 rounded-md border border-indigo-200 mb-3 shadow-xs">
                      <p className="font-semibold text-indigo-950 text-xs">
                        🎓 Scholarship Awarded: {activeScholarship?.schemeName || 'Institutional Grant'}
                      </p>
                      <p className="text-xs text-indigo-700 mt-0.5">
                        {isFullScholarship
                          ? '100% Full Waiver — Tuition and fees are completely covered (Zero Due).'
                          : `${activeScholarship?.percentage || ''}% Partial Scholarship applied.`}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2 text-sm text-green-700">
                    <p><strong>Name:</strong> {[formData.personalInfo.firstName, formData.personalInfo.lastName].filter(Boolean).join(' ').trim() || 'Not provided'}</p>
                    <p><strong>Email:</strong> {formData.contactInfo.email}</p>
                    <p><strong>Phone:</strong> {formData.contactInfo.phone}</p>
                    <p><strong>Program:</strong> {application.mainProductId?.name} - {application.subProductId?.name}</p>
                    <p>
                      <strong>Mode of Attendance:</strong>{' '}
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                        {formData.attendanceMode === 'Blended' ? 'Blended Learning' : (formData.attendanceMode || 'Physical')}
                      </span>
                    </p>
                    
                    {/* Module Selections for Module-Based Products */}
                    {isModuleBased && selectedModules.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-green-300">
                        {paymentSkipped && !cardPaymentInfo ? (
                          <p><strong>Amount To Pay:</strong> Skipped</p>
                        ) : (
                          <>
                            <p className="font-semibold mb-2">Selected Modules ({selectedModules.length}):</p>
                            <ul className="list-disc list-inside space-y-1">
                              {availableModules
                                .filter(module => selectedModules.includes(module.code))
                                .map((module, index) => {
                                  const exemption = getModuleExemption(module.code);
                                  const isApproved = exemption?.status === 'approved';
                                  const moduleTotal = getResolvedModulePayableTotal(module);
                                  return (
                                    <li key={module.code || index} className="text-sm">
                                      <span className="font-mono">{module.code}</span> - {module.name}
                                      {module.credits && <span className="text-gray-600"> ({module.credits} credits)</span>}
                                      <span className={`font-semibold${isApproved ? ' text-green-700' : ''}`}> - LKR {moduleTotal.toLocaleString()}{isApproved ? ' (Exemption Fee)' : ''}</span>
                                    </li>
                                  );
                                })}
                            </ul>

                            {cardPaymentInfo ? (
                              <p className="mt-2 font-semibold">
                                <strong>Amount Paid:</strong> {(cardPaymentInfo.currency || 'LKR')}{' '}
                                {Number(cardPaymentInfo.amount || 0).toLocaleString()} - Paid via card
                              </p>
                            ) : (() => {
                              const A = calculateModuleTotal();
                              const E = Number(discountQuote?.discountAmount || 0);
                              const B = getRegistrationFeeForPaymentContext();
                              const C = getMembershipFeeForPaymentContext();
                              const D = getInternationalPaymentFeeForPaymentContext();
                              const F = modulePaymentMode === 'installment' && moduleInstallmentPreview
                                ? moduleInstallmentPreview.subtotalAfterDiscount
                                : (taxInfo?.subtotalAfterDiscount || getMaxPaymentAmount());
                              const G = modulePaymentMode === 'installment' && moduleInstallmentPreview
                                ? moduleInstallmentPreview.totalExclusiveTaxAmount
                                : (taxInfo?.totalTaxAmount || 0);
                              const total = taxInfo?.totalInvoiceAmount || (F + G);
                              const hasExtras = E > 0 || B > 0 || C > 0 || D > 0 || internationalPaymentFeeAmountBase > 0 || registrationFeeAmountBase > 0;
                              const discountLabel = discountQuote?.appliedDiscount?.kind === 'scholarship'
                                ? `Scholarship (${discountQuote.appliedDiscount.name || 'Institutional Grant'})`
                                : isEarlyBirdApplied
                                  ? 'Early bird discount'
                                  : discountQuote?.appliedDiscount?.code
                                    ? `Discount (${discountQuote.appliedDiscount.code})`
                                    : 'Discount';
                              return (
                                <div className="mt-3 bg-white rounded border border-green-300 p-3 space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    {(() => {
                                      const resolvedSelections = selectedModules.map((code) => resolveAssignedModule({ code }));
                                      const allRepeat = resolvedSelections.length > 0 && resolvedSelections.every((m) => isRepeatFeeModule(m));
                                      const anyRepeat = resolvedSelections.some((m) => isRepeatFeeModule(m));
                                      const label = allRepeat
                                        ? `Repeat fees (${selectedModules.length})`
                                        : anyRepeat
                                          ? `Module & Repeat fees (${selectedModules.length})`
                                          : `Module fees (${selectedModules.length})`;
                                      return <span>{label}</span>;
                                    })()}
                                    <span>{discountCurrency} {A.toLocaleString()}</span>
                                  </div>
                                  {E > 0 && (
                                    <div className="flex justify-between text-green-700">
                                      <span>{discountLabel}</span>
                                      <span>-{discountCurrency} {E.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {renderRegistrationFeeSummaryLine(B, { amountClassName: '' })}
                                  {membershipFeeAmountBase > 0 && (
                                    membershipFeeIsIncluded ? (
                                      <div className="flex justify-between text-gray-400 italic">
                                        <span>Membership fee <em>(already included in course fee)</em></span>
                                        <span>{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                      </div>
                                    ) : (
                                      <div className="flex justify-between">
                                        <span>Membership fee</span>
                                        <span>+{membershipFeeCurrency} {C.toLocaleString()}</span>
                                      </div>
                                    )
                                  )}
                                  {internationalPaymentFeeAmountBase > 0 && (
                                    <div className="flex justify-between">
                                      <span>International payment fee</span>
                                      <span>+{internationalPaymentFeeCurrency} {D.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {hasExtras && (
                                    <div className="border-t border-green-300 pt-1 flex justify-between font-semibold">
                                      <span>Subtotal</span>
                                      <span>{discountCurrency} {F.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {taxInfo?.taxes?.filter(t => t.included).map((t, i) => (
                                    <div key={i} className="flex justify-between text-gray-500 italic">
                                      <span>{t.name} ({t.percentage}%)</span>
                                      <span>{t.currency} {t.taxAmount.toLocaleString()} (incl.)</span>
                                    </div>
                                  ))}
                                  {taxInfo?.taxes?.filter(t => !t.included).map((t, i) => (
                                    <div key={i} className="flex justify-between">
                                      <span>{t.name} ({t.percentage}%)</span>
                                      <span>+{t.currency} {t.taxAmount.toLocaleString()}</span>
                                    </div>
                                  ))}
                                  <div className="border-t border-green-300 pt-1 flex justify-between font-bold text-green-900">
                                    <span>Total Invoice</span>
                                    <span>{discountCurrency} {isFullScholarship ? 0 : total.toLocaleString()}</span>
                                  </div>
                                  {!isFullScholarship && modulePaymentMode === 'installment' && moduleInstallmentPreview && (
                                    <div className="pt-2 border-t border-green-200 space-y-1">
                                      <p className="font-semibold text-green-900">Installment Schedule</p>
                                      {moduleInstallmentPreview.installments.map((installment) => (
                                        <div key={installment.installmentNumber} className="flex justify-between text-green-800">
                                          <span>{installment.description}</span>
                                          <span>{discountCurrency} {installment.totalDue.toLocaleString()}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {isFullScholarship ? (
                                    <div className="pt-1 text-green-800 font-medium">
                                      Payment: <strong>100% Waived (Full Scholarship)</strong>
                                    </div>
                                  ) : (
                                    <div className="pt-1 text-green-800">
                                      Paying: <strong>{discountCurrency} {desiredPaymentAmount.toLocaleString()}</strong>
                                      {' '}— {paymentReceipt ? '✓ Receipt Uploaded' : '✗ Receipt Pending'}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    )}

                    {/* Payment Information for Product-Based Products */}
                    {!isModuleBased && (
                      <div className="mt-3 pt-3 border-t border-green-300">
                        {cardPaymentInfo ? (
                          <p className="font-semibold">
                            <strong>Amount Paid:</strong>{' '}
                            {(cardPaymentInfo.currency || subProduct.baseFee?.currency || discountCurrency)}{' '}
                            {Number(cardPaymentInfo.amount || 0).toLocaleString()} (Paid via card)
                          </p>
                        ) : paymentSkipped ? (
                          <p><strong>Amount To Pay:</strong> Skipped</p>
                        ) : (() => {
                          const A = Number(
                            (discountQuote?.appliedDiscount && discountQuote.appliedDiscount.kind !== 'full_payment_concession' && discountQuote?.baseAmount)
                              ? discountQuote.baseAmount
                              : ((subProduct?.baseFee?.amount != null && Number(subProduct.baseFee.amount) > 0)
                                  ? subProduct.baseFee.amount
                                  : (mainProduct?.fee?.amount || 0))
                          );
                          const B = isFullScholarship ? 0 : getRegistrationFeeForPaymentContext();
                          const C = isFullScholarship ? 0 : getMembershipFeeForPaymentContext();
                          const E = isFullScholarship
                            ? roundCurrency(A + (registrationFeeAmountBase || 0) + (membershipFeeAmountBase || 0))
                            : Number((discountQuote?.appliedDiscount && discountQuote.appliedDiscount.kind !== 'full_payment_concession') ? (discountQuote?.discountAmount || 0) : 0);
                          const F = isFullScholarship ? 0 : (taxInfo?.subtotalAfterDiscount || getMaxPaymentAmount());
                          const G = isFullScholarship ? 0 : (taxInfo?.totalTaxAmount || 0);
                          const total = isFullScholarship ? 0 : (taxInfo?.totalInvoiceAmount || (F + G));
                          const currentInstallmentDue = isFullScholarship ? 0 : getCurrentInstallmentDueAmount();
                          const overallPlanTotal = isFullScholarship ? 0 : getOverallPlannedPayableAmount();
                          const hasExtras = isFullScholarship || E > 0 || B > 0 || C > 0 || registrationFeeAmountBase > 0 || membershipFeeAmountBase > 0;
                          const discountLabel = discountQuote?.appliedDiscount?.kind === 'scholarship'
                            ? `Scholarship (${discountQuote.appliedDiscount.name || 'Institutional Grant'})`
                            : isEarlyBirdApplied
                              ? 'Early bird discount'
                              : discountQuote?.appliedDiscount?.code
                                ? `Discount (${discountQuote.appliedDiscount.code})`
                                : 'Discount';
                          const payLabel = paymentType === 'installment'
                            ? Math.round(desiredPaymentAmount || 0) >= Math.round(currentInstallmentDue)
                              ? 'First installment'
                              : 'Partial payment toward first installment'
                            : Math.round(desiredPaymentAmount || 0) >= Math.round(total)
                              ? 'Full Payment'
                              : 'Partial Payment';
                          return (
                            <div className="bg-white rounded border border-green-300 p-3 space-y-1 text-xs">
                              {A > 0 && (
                                <div className="flex justify-between">
                                  <span>Course fee</span>
                                  <span>{discountCurrency} {A.toLocaleString()}</span>
                                </div>
                              )}
                              {isFullScholarship ? (
                                <>
                                  {registrationFeeAmountBase > 0 && (
                                    <div className="flex justify-between">
                                      <span>Registration fee</span>
                                      <span>+{registrationFeeCurrency} {registrationFeeAmountBase.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {membershipFeeAmountBase > 0 && (
                                    <div className="flex justify-between">
                                      <span>Membership fee</span>
                                      <span>+{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between text-green-700 font-medium">
                                    <span>Scholarship ({activeScholarship?.schemeName || 'Institutional Scholarship'} — 100% Full Waiver)</span>
                                    <span>-{discountCurrency} {E.toLocaleString()}</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {E > 0 && (
                                    <div className="flex justify-between text-green-700">
                                      <span>{discountLabel}</span>
                                      <span>-{discountCurrency} {E.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {renderRegistrationFeeSummaryLine(B, { amountClassName: '' })}
                                  {membershipFeeAmountBase > 0 && (
                                    (scholarshipCoversMem && !(C > 0)) ? (
                                      <div className="flex justify-between text-gray-500 italic">
                                        <span>Membership fee <em>(waived — scholarship)</em></span>
                                        <span>{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                      </div>
                                    ) : membershipFeeIsIncluded ? (
                                      <div className="flex justify-between text-gray-400 italic">
                                        <span>Membership fee <em>(already included in course fee)</em></span>
                                        <span>{membershipFeeCurrency} {membershipFeeAmountBase.toLocaleString()}</span>
                                      </div>
                                    ) : (
                                      <div className="flex justify-between">
                                        <span>Membership fee</span>
                                        <span>+{membershipFeeCurrency} {C.toLocaleString()}</span>
                                      </div>
                                    )
                                  )}
                                </>
                              )}
                              {hasExtras && (
                                <div className="border-t border-green-300 pt-1 flex justify-between font-semibold">
                                  <span>{paymentType === 'installment' ? 'Current installment subtotal' : 'Subtotal'}</span>
                                  <span>{discountCurrency} {F.toLocaleString()}</span>
                                </div>
                              )}
                              {!isFullScholarship && taxInfo?.taxes?.filter(t => t.included).map((t, i) => (
                                <div key={i} className="flex justify-between text-gray-500 italic">
                                  <span>{t.name} ({t.percentage}%)</span>
                                  <span>{t.currency} {t.taxAmount.toLocaleString()} (incl.)</span>
                                </div>
                              ))}
                              {!isFullScholarship && taxInfo?.taxes?.filter(t => !t.included).map((t, i) => (
                                <div key={i} className="flex justify-between">
                                  <span>{t.name} ({t.percentage}%)</span>
                                  <span>+{t.currency} {t.taxAmount.toLocaleString()}</span>
                                </div>
                              ))}
                              <div className="border-t border-green-300 pt-1 flex justify-between font-bold text-green-900">
                                <span>{paymentType === 'installment' ? 'Current installment due' : 'Total Invoice'}</span>
                                <span>{discountCurrency} {(paymentType === 'installment' ? currentInstallmentDue : total).toLocaleString()}</span>
                              </div>
                              {paymentType === 'installment' && selectedPlan && (
                                <div className="flex justify-between font-semibold text-green-900">
                                  <span>Overall plan total</span>
                                  <span>{discountCurrency} {overallPlanTotal.toLocaleString()}</span>
                                </div>
                              )}
                              {isFullScholarship ? (
                                <div className="pt-1 text-green-800 font-medium">
                                  Payment: <strong>100% Waived (Full Scholarship)</strong>
                                </div>
                              ) : (
                                <div className="pt-1 text-green-800">
                                  Paying: <strong>{subProduct.baseFee?.currency || discountCurrency} {desiredPaymentAmount.toLocaleString()}</strong>
                                  {' '}({payLabel}) — {paymentReceipt ? '✓ Receipt Uploaded' : '✗ Receipt Pending'}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Comments (Optional)</label>
                  <textarea
                    value={formData.additionalInfo.comments || ''}
                    onChange={(e) => handleInputChange('additionalInfo', 'comments', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Any additional information you'd like to share..."
                  />
                </div>

                {shouldUseStoredCardAgreementForSubmit ? (
                  <div className="border rounded-md p-4 bg-green-50 border-green-200">
                    <p className="text-sm font-medium text-green-900">
                      Agreement already accepted before card payment
                    </p>
                    <p className="mt-1 text-sm text-green-800">
                      The non-refundable payment terms were accepted before the card payment was initiated.
                    </p>
                    {storedCardAgreementAcceptedAt && (
                      <p className="mt-2 text-xs text-green-700">
                        Accepted on {storedCardAgreementAcceptedAt.toLocaleString()}.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className={`border rounded-md p-4 ${finalTermsAgreed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={finalTermsAgreed}
                        onChange={(e) => setFinalTermsAgreed(e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-800">
                        <strong>I understand and agree</strong> that all payments made towards this application and enrollment are{' '}
                        <strong>strictly non-refundable</strong>. By submitting this application, I confirm that all information
                        provided is accurate and complete to the best of my knowledge, and I accept the{' '}
                        <button type="button" onClick={() => setShowEnrollmentTermsModal(true)} className="text-indigo-600 underline hover:text-indigo-800 font-medium focus:outline-none">
                          terms and conditions of enrollment
                        </button>.
                      </span>
                    </label>
                    {!finalTermsAgreed && (
                      <p className="mt-2 text-xs text-red-600 font-medium">* You must agree to this before submitting your application.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
            {/* Turnstile CAPTCHA - Only show on final step (skip for localhost) */}
            {currentStep === totalSteps && !(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.')) && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Security Verification</h3>
                <div ref={turnstileRef} className="turnstile-widget min-h-[65px] flex items-center justify-center">
                  {!turnstileWidgetId && (
                    <div className="flex items-center space-x-2 text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      <span className="text-sm">Loading security verification...</span>
                    </div>
                  )}
                </div>
                {turnstileExpired && (
                  <p className="mt-2 text-sm text-red-600">
                    Security verification has expired. Please complete it again.
                  </p>
                )}
                {turnstileToken && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Security verification completed successfully.
                  </p>
                )}
              </div>
            )}
            
            {/* Localhost development notice */}
            {currentStep === totalSteps && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.')) && (
              <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
                <p className="text-sm text-blue-800">
                  <strong>Development Mode:</strong> Security verification is disabled for localhost.
                </p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <div className="m-4">
                {currentStep > 1 && (
                  <button
                    onClick={prevStep}
                    disabled={!!stepNavigating}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {stepNavigating === 'prev' ? (
                      <>
                        <StepButtonSpinner />
                        Loading...
                      </>
                    ) : (
                      'Previous'
                    )}
                  </button>
                )}
              </div>
              
              <div className="m-4 flex gap-3">
                {currentStep < totalSteps ? (
                  <button
                    onClick={() => {
                      if (currentStep === 4 && paymentReceipt) {
                        // User uploaded receipt, so clear payment skipped flag
                        setPaymentSkipped(false);
                      }
                      nextStep();
                    }}
                    disabled={!!stepNavigating}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white text-base font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {stepNavigating === 'next' ? (
                      <>
                        <StepButtonSpinner light />
                        Loading...
                      </>
                    ) : (
                      'Next'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !isSubmissionAgreementSatisfied || (!(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.')) && !turnstileToken)}
                    className="px-6 py-3 bg-green-600 text-white text-base font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment Terms & Conditions Modal */}
      {showEnrollmentTermsModal && (
        <PolicyModal
          title="Terms & Conditions of Enrollment"
          content={enrollmentTermsText || DEFAULT_ENROLLMENT_TERMS}
          onClose={() => setShowEnrollmentTermsModal(false)}
        />
      )}
    </>
  );
};

export default PublicApplicationByNumber;